import type { Locale } from "../context/LangContext";

export function t(lang: Locale, dict: Record<Locale, string>): string {
  return dict[lang] || dict.en;
}

export const toLocalDigits = (str: string, lang: string) => {
  if (lang !== "fa" && lang !== "ar") return str;
  const digits = lang === "fa" ? "۰۱۲۳۴۵۶۷۸۹" : "٠١٢٣٤٥٦٧٨٩";
  return str.replace(/[0-9]/g, (w) => digits[+w]);
};

export const langs: {
  code: Locale;
  label: string;
  dir: "ltr" | "rtl";
  fontClass: string;
}[] = [
  { code: "en", label: "EN", dir: "ltr", fontClass: "font-sans" },
  { code: "fa", label: "FA", dir: "rtl", fontClass: "font-farsi" },
  { code: "ar", label: "AR", dir: "rtl", fontClass: "font-arabic" },
];

export const langDir: Record<Locale, "ltr" | "rtl"> = {
  en: "ltr",
  fa: "rtl",
  ar: "rtl",
};

/* ───── Nav ───── */
export const nav = {
  products: { en: "Products", fa: "سرویس‌ها", ar: "المنتجات" },
  pricing: { en: "Pricing", fa: "قیمت‌گذاری", ar: "التسعير" },
  calculator: { en: "Calculator", fa: "ماشین حساب", ar: "الحاسبة" },
  mcp: {
    en: "Lexoya MCP",
    fa: "MCP لکسویا",
    ar: "MCP لكسويا",
  },
  docs: { en: "Docs", fa: "مستندات", ar: "الوثائق" },
  devops: { en: "DevOps", fa: "دواپس", ar: "ديف أوبس" },
  organizations: { en: "Organizations", fa: "سازمان‌ها", ar: "المؤسسات" },
  status: { en: "Status", fa: "وضعیت", ar: "الحالة" },
  getStarted: { en: "Get Started", fa: "شروع کنید", ar: "ابدأ الآن" },
  loginRegister: {
    en: "Login / Register",
    fa: "ورود / ثبت‌نام",
    ar: "تسجيل الدخول / التسجيل",
  },
  contactUs: { en: "Contact Us", fa: "تماس با ما", ar: "اتصل بنا" },
};

export const productsDropdown: Record<
  string,
  { label: Record<Locale, string>; desc: Record<Locale, string> }
> = {
  vps: {
    label: {
      en: "Cloud Servers (VPS)",
      fa: "سرورهای ابری (VPS)",
      ar: "خوادم سحابية (VPS)",
    },
    desc: {
      en: "High-performance virtual private servers",
      fa: "سرورهای مجازی با منابع اختصاصی و عملکرد بالا",
      ar: "خوادم خاصة افتراضية عالية الأداء",
    },
  },
  gpu: {
    label: { en: "GPU Servers", fa: "سرورهای GPU", ar: "خوادم GPU" },
    desc: {
      en: "Dedicated GPU compute for AI and rendering",
      fa: "سرور با کارت گرافیک اختصاصی جهت کارکردهای هوش مصنوعی و رندرینگ",
      ar: "حوسبة GPU مخصصة للذكاء الاصطناعي والعرض",
    },
  },
  paas: {
    label: {
      en: "Cloud Platform / PaaS",
      fa: "پلتفرم ابری (PaaS)",
      ar: "منصة سحابية / PaaS",
    },
    desc: {
      en: "Full-stack platform for rapid deployment",
      fa: "دیپلوی اپلیکیشن از گیت، بدون مدیریت سرور",
      ar: "منصة كاملة للنشر السريع",
    },
  },
  database: {
    label: {
      en: "Managed Database",
      fa: "دیتابیس مدیریت‌شده",
      ar: "قاعدة بيانات مُدارة",
    },
    desc: {
      en: "HA database clusters with automated backups",
      fa: "دیتابیس آماده با رپلیکا و پشتیبان‌گیری خودکار",
      ar: "مجموعات قواعد بيانات عالية التوفر مع نسخ احتياطية آلية",
    },
  },
  storage: {
    label: {
      en: "Cloud Storage (S3)",
      fa: "فضای ذخیره‌سازی ابری (S3)",
      ar: "تخزين سحابي (S3)",
    },
    desc: {
      en: "Scalable object storage with S3-compatible API",
      fa: "فضای ذخیره‌سازی S3 کاملاً سازگار با استاندارد AWS S3",
      ar: "تخزين كائنات قابل للتوسع مع API متوافق مع S3",
    },
  },
  backup: {
    label: { en: "Backup", fa: "پشتیبان‌گیری", ar: "النسخ الاحتياطي" },
    desc: {
      en: "Automated backup and disaster recovery",
      fa: "پشتیبان‌گیری خودکار و بازیابی سریع و راحت",
      ar: "نسخ احتياطي آلية واستعادة الكوارث",
    },
  },
  security: {
    label: {
      en: "Security Services",
      fa: "خدمات امنیتی",
      ar: "الخدمات الأمنية",
    },
    desc: {
      en: "Penetration testing and security assessments",
      fa: "تست نفوذ و ارزیابی امنیتی",
      ar: "اختبار الاختراق وتقييمات أمنية",
    },
  },
  devops: {
    label: { en: "DevOps Services", fa: "خدمات دواپس", ar: "خدمات ديف أوبس" },
    desc: {
      en: "CI/CD, monitoring, and infrastructure as code",
      fa: "CI/CD، مانیتورینگ، کلاسترهای اختصاصی، اوپن استک، کوبرنتیز، سف",
      ar: "CI/CD والمراقبة والبنية التحتية كرمز",
    },
  },
  org: {
    label: {
      en: "Organizational Services",
      fa: "خدمات سازمانی",
      ar: "الخدمات المؤسسية",
    },
    desc: {
      en: "Multi-account management and enterprise governance",
      fa: "مدیریت متمرکز حساب‌ها و حاکمیت سازمانی، بکپارچه‌سازی سامانه‌ها",
      ar: "إدارة متعددة الحسابات والحوكمة المؤسسية",
    },
  },
  monitoring: {
    label: { en: "Monitoring", fa: "مانیتورینگ", ar: "المراقبة" },
    desc: {
      en: "Real-time observability and alerting",
      fa: "مانیتورینگ، مدیریت لاگ و هشداردهی سریع",
      ar: "مراقبة في الوقت الفعلي والتنبيه",
    },
  },
  consulting: {
    label: { en: "Consulting", fa: "مشاوره", ar: "الاستشارات" },
    desc: {
      en: "Expert architecture and migration consulting",
      fa: "مشاوره معماری فنی، مهاجرت ابری، شبکه و میکروسرویس",
      ar: "استشارات الخبراء في الهندسة والهجرة",
    },
  },
  quickapps: {
    label: {
      en: "Quick Apps",
      fa: "برنامه‌های آماده",
      ar: "التطبيقات السريعة",
    },
    desc: {
      en: "One-click deploy of 30+ popular apps",
      fa: "دیپلوی با یک کلیک بیش از ۳۰ برنامه محبوب",
      ar: "نشر بنقرة واحدة لأكثر من ٣٠ تطبيقًا شائعًا",
    },
  },
  streaming: {
    label: { en: "Streaming Server", fa: "سرور استریم", ar: "خادم بث" },
    desc: {
      en: "Dedicated streaming servers",
      fa: "سرورهای استریم اختصاصی",
      ar: "خوادم بث مخصصة",
    },
  },
  sentry: {
    label: { en: "Sentry", fa: "سنتری", ar: "سنتري" },
    desc: {
      en: "Error tracking and performance monitoring",
      fa: "ردیابی خطا و مانیتورینگ عملکرد",
      ar: "تتبع الأخطاء ومراقبة الأداء",
    },
  },
  mcp: {
    label: {
      en: "AI Assistants (MCP)",
      fa: "دستیارهای هوش مصنوعی (MCP)",
      ar: "مساعدو الذكاء الاصطناعي (MCP)",
    },
    desc: {
      en: "Manage your cloud from Claude, Cursor & AI agents",
      fa: "مدیریت ابر از طریق کلود، کرسر و عامل‌های هوش مصنوعی",
      ar: "أدر سحابتك من Claude وCursor ووكلاء الذكاء الاصطناعي",
    },
  },
};

/* ───── Hero ───── */
export const hero = {
  title1: {
    en: "Deploy without limits.",
    fa: "زیرساختِ پایدار",
    ar: "انشر بلا حدود.",
  },
  title2: {
    en: "Scale without borders.",
    fa: "کسب‌وکارِ ماندگار",
    ar: "وسّع بلا حدود.",
  },
  description: {
    en: "The cloud platform engineered for the next generation of applications. Deploy globally in seconds with automatic scaling, enterprise security, and sub-millisecond latency.",
    fa: "با آسودگی خاطر همه‌ی نیازهای زیرساختی خود را به ما بسپارید و تمرکز خود را روی خلق ارزش برای مشتریانتان بگذارید.",
    ar: "المنصة السحابية المصممة للجيل القادم من التطبيقات. انشر عالميًا في ثوانٍ مع التوسع التلقائي والأمان المؤسسي وزمن استجابة أقل من ملي ثانية.",
  },
  ctaPrimary: {
    en: "Start for Free",
    fa: "رایگان شروع کنید",
    ar: "ابدأ الآن",
  },
  ctaPrimaryUrl: {
    en: "https://console.lexoya.com/login",
    fa: "https://console.lexoya.com/login",
    ar: "https://console.lexoya.com/login",
  },
  ctaSecondary: {
    en: "Talk to Sales",
    fa: "ارتباط با فروش",
    ar: "التحدث إلى المبيعات",
  },
};

/* ───── Trusted By ───── */
export const trustedBy = {
  prefix: { en: "Trusted by ", fa: "همراهِ ", ar: "موثوق من قبل " },
  highlight: {
    en: "best developers and businesses",
    fa: "پیشروترین تیم‌ها و کسب‌وکارهای",
    ar: "أفضل المطورين والشركات",
  },
  suffix: { en: " in Iran", fa: " ایرانی", ar: " في إيران" },
};

/* ───── Features (home) ───── */
export const features = {
  subtitle: { en: "Products", fa: "محصولات", ar: "المنتجات" },
  heading: {
    en: "Everything you need to",
    fa: "همه چیز برای",
    ar: "كل ما تحتاجه لـ",
  },
  highlighted: {
    en: "ship faster",
    fa: "یک زیر ساخت بهتر",
    ar: "النشر بشكل أسرع",
  },
  description: {
    en: "From deployment to monitoring, Lexoya provides every tool you need to build, run, and scale your applications.",
    fa: "از دیپلوی تا مانیتورینگ، Lexoya همه ابزارهای لازم برای ساخت، اجرا و مقیاس‌دهی برنامه‌های شما را فراهم می‌کند.",
    ar: "من النشر إلى المراقبة، توفر Lexoya كل أداة تحتاجها لبناء وتشغيل وتوسيع تطبيقاتك.",
  },
  readMore: { en: "Read more", fa: "بیشتر بخوانید", ar: "اقرأ المزيد" },
  items: {
    vps: {
      title: {
        en: "Cloud Servers (VPS)",
        fa: "سرورهای ابری (VPS)",
        ar: "خوادم سحابية (VPS)",
      },
      description: {
        en: "High-performance virtual private servers with dedicated resources and full root access.",
        fa: "سرور مجازی با منابع و سیستم‌عامل دلخواه، دسترسی روت و عملکرد بالا.",
        ar: "خوادم خاصة افتراضية عالية الأداء مع موارد مخصصة ووصول كامل للجذر.",
      },
      details: {
        en: "Deploy instantly with SSD NVMe storage, 99.99% uptime SLA, and your choice of Linux distributions. Scale CPU, RAM, and storage on demand with zero downtime.",
        fa: "سرور را با منابع و سیستم عامل دلخواه در لحظه بسازید و با دسترسی روت با منابع اختصاصی از آن استفاده کنید. هر زمان که نیاز داشتید می‌توانید منابع سرور را افزایش داده و با بکاپ‌گیری خودکار بدون نگرانی سرویس‌های خود را در آن مستقر کنید.",
        ar: "انشر فورًا مع تخزين SSD NVMe واتفاقية مستوى خدمة ٩٩.٩٩٪ واختيار توزيعات لينكس.",
      },
    },
    gpu: {
      title: { en: "GPU Servers", fa: "سرورهای GPU", ar: "خوادم GPU" },
      description: {
        en: "Dedicated GPU compute for AI training, rendering, and HPC workloads.",
        fa: "سرورهای پر قدرت دارای کارت گرافیک اختصاصی برای هوش مصنوعی، رندر و HPC.",
        ar: "حوسبة GPU مخصصة لتدريب الذكاء الاصطناعي والعرض.",
      },
      details: {
        en: "Access NVIDIA A100, H100, and L40S GPUs with high-bandwidth interconnects. Reserve daily or monthly, scale to multi-node clusters.",
        fa: "دسترسی به کارت‌گرافیک‌های قدرتمند برای مصارف هوش‌مصنوعی و ... با منابع و سیستم عامل دلخواه به مدت نامحدود در اختیار شما قرار می‌گیرد. از این سرورها برای اجرای مدل‌های هوش‌مصنوعی، ترینینگ و فاین تیونینگ مدل‌ها، اجرای همزمان چند مدل در کنار هم و هر نوع کاربرد دیگر استفاده کنید.",
        ar: "الوصول إلى NVIDIA A100 وH100 وL40S مع وصلات عالية النطاق. احجز يوميًا أو شهريًا.",
      },
    },
    paas: {
      title: {
        en: "Cloud Platform / PaaS",
        fa: "پلتفرم ابری (PaaS)",
        ar: "منصة سحابية / PaaS",
      },
      description: {
        en: "A fully managed platform to build, deploy, and scale applications effortlessly.",
        fa: "از git push تا دیپلوی در سراسر جهان در چند ثانیه. مقیاس خودکار، CI/CD داخلی، SSL مدیریت‌شده و کلی add-on.",
        ar: "منصة مُدارة بالكامل لبناء ونشر وتوسيع التطبيقات بسهولة.",
      },
      details: {
        en: "From git push to global deployment in seconds. Automatic scaling, built-in CI/CD, managed SSL, and a rich ecosystem of add-ons.",
        fa: "پلتفرمی کاملاً مدیریت‌شده برای ساختن، دیپلوی کردن و اسکیل کردن برنامه‌های شما. به هر زبانی که برنامه‌ی خود را نوشته باشید در لکسویا با چند کلیک می‌توانید این برنامه را اجرا کنید. کل چرخه‌ی CI/CD را می‌توانید با کمترین دردسر برای پروژه‌ی خود فراهم کنید.",
        ar: "من git push إلى نشر عالمي في ثوانٍ. توسع تلقائي، CI/CD مدمج، SSL مُدار.",
      },
    },
    database: {
      title: {
        en: "Managed Database",
        fa: "دیتابیس مدیریت‌شده",
        ar: "قاعدة بيانات مُدارة",
      },
      description: {
        en: "High-availability database clusters with automated backups and replication.",
        fa: "خوشه‌های دیتابیس با دسترسی بالا، پشتیبان خودکار و replica.",
        ar: "مجموعات قواعد بيانات عالية التوفر مع نسخ احتياطية آلية.",
      },
      details: {
        en: "Support for PostgreSQL, MySQL, MariaDB, and Redis. Automated failover, point-in-time recovery, read replicas.",
        fa: "انواع دیتابیس‌های PostgreSQL، MySQL، MariaDB، Redis، Kafka، Elasticsearchو MongoDB بصورت مدیریت شده و آماده‌ی استفاده فقط با یک کلیک در اختیار شما قرار می‌گیرد. بکاپ‌گیری خودکار، رپلیکا read-only و automatic failover بخشی از امکانات دیتابیس‌های مدیریت شده می‌باشند.",
        ar: "دعم PostgreSQL وMySQL وMariaDB وRedis. تجاوز تلقائي واسترجاع نقطة زمنية.",
      },
    },
    storage: {
      title: {
        en: "Cloud Storage (S3)",
        fa: "فضای ذخیره‌سازی ابری (S3)",
        ar: "تخزين سحابي (S3)",
      },
      description: {
        en: "Scalable, durable object storage with a fully S3-compatible API.",
        fa: "فضای ذخیره S3 مقیاس‌پذیر با API کاملاً سازگار با S3.",
        ar: "تخزين كائنات قابل للتوسع مع API متوافق مع S3.",
      },
      details: {
        en: "Store and retrieve any amount of data with 5 nines of durability. S3-compatible APIs and CDN integration.",
        fa: "فایل‌های خود را به راحتی و با هر ظرفیتی که نیاز دارید می‌توانید در S3 لکسویا ذخیره کنید. هر زمان که به فضای بیشتری نیاز داشتید می‌توانید باکت خود را افزایش دهید. لایه کش رایگان S3 دانلود فایل‌ها از فضای ذخیره‌سازی را بسیار بالاتر می‌برد. امکان بکاپ‌گیری و میرور کردن باکت‌ها در مکان دیگر نیز فراهم شده است.",
        ar: "خزّن واسترجع أي كمية من البيانات. APIs متوافقة مع S3.",
      },
    },
    backup: {
      title: { en: "Backup", fa: "پشتیبان‌گیری", ar: "النسخ الاحتياطي" },
      description: {
        en: "Automated backup and disaster recovery for your critical data and systems.",
        fa: "پشتیبان‌گیری خودکار و بازیابی اطلاعات حیاتی.",
        ar: "نسخ احتياطية آلية واستعادة الكوارث.",
      },
      details: {
        en: "Schedule automated backups, retain snapshots across regions, and restore in seconds. Cross-region replication.",
        fa: "با تنظیم بکاپ‌گیری خودکار می‌توانید از هر کدام از دیتابیس‌ها، دیسک‌های ابری و تنظیمات نرم‌افزارهای خود در زمان‌های دلخواه بکاپ تهیه کنید و بکاپ را در چند نقطه جغرافیایی مختلف ذخیره کنید. امکان بازیابی از روی بکاپ نیز به راحتی و با زدن یک دکمه وجود دارد.",
        ar: "جدولة النسخ الاحتياطية الآلية والاحتفاظ بالقطات.",
      },
    },
    security: {
      title: {
        en: "Security Services",
        fa: "خدمات امنیتی",
        ar: "الخدمات الأمنية",
      },
      description: {
        en: "Enterprise-grade penetration testing and vulnerability assessment.",
        fa: "تست نفوذ در سطح سازمانی و ارزیابی آسیب‌پذیری.",
        ar: "اختبار اختراق على مستوى المؤسسات.",
      },
      details: {
        en: "Protect your infrastructure with comprehensive security services — from simulated attacks to real-time threat detection.",
        fa: "با خدمات امنیتی جامع از زیرساخت خود محافظت کنید. از شبیه‌سازی حملات تا تشخیص تهدید در لحظه.",
        ar: "احمِ بنيتك التحتية بخدمات أمنية شاملة.",
      },
    },
    devops: {
      title: { en: "DevOps Services", fa: "خدمات دوآپس", ar: "خدمات ديف أوبس" },
      description: {
        en: "CI/CD pipelines, infrastructure as code, and comprehensive monitoring.",
        fa: "تمامی خدمات دواپسی مورد نیاز شما از راه اندازی CI/CD، مانیتورینگ، کلاستر اختصاصی کوبرنتیز، راه‌اندازی Ceph، آموزش تیم فنی، مشاوره در زمینه‌های مختلف زیرساختی را می‌توانید از تیم با تجربه و با سابقه‌ی لکسویا درخواست کنید.",
        ar: "خطوط أنابيب CI/CD والبنية التحتية كرمز.",
      },
      details: {
        en: "Automate your entire workflow with managed CI/CD, Terraform providers, and Kubernetes integration.",
        fa: "با راه‌اندازی فرهنگ دواپس در شرکت یا تیم خود می‌توانید با خیال راحت وضعیت توسعه در تیم خود را مدیریت کنید و همچنین فرایند استقرار اتوماتیک زمان ارائه نسخه جدید را به حداقل می‌رساند. مدیرعامل و مدیرفنی سازمان‌ها از دواپس برای مدیریت بهتر تیم فنی و استانداردسازی نحوه‌ی ارتباط ابزارها و سامانه‌ها استفاده می‌کنند. بدون دواپس مدیریت تیم فنی مصیبت است!",
        ar: "أتمتة سير العمل بالكامل مع CI/CD مُدار.",
      },
    },
    org: {
      title: {
        en: "Organizational Services",
        fa: "خدمات سازمانی",
        ar: "الخدمات المؤسسية",
      },
      description: {
        en: "Enterprise-grade multi-account management with consolidated billing.",
        fa: "یکپارچه‌سازی سامانه‌های مختلف سازمان‌ها و پیاده‌سازی سامانه احراز هویت متمرکز و همچنین انجام پیش‌نیازهای امنیتی مورد نیاز برای اعمال سطح دسترسی‌های دقیق برای افراد سازمان از جمله خدماتی است که لکسویا برای سازمان‌ها ارائه می‌کند.",
        ar: "إدارة متعددة الحسابات مع فواتير موحدة.",
      },
      details: {
        en: "Centrally manage teams, projects, and budgets. Role-based access control and audit logs.",
        fa: "در لکسویا می‌توانید تیم‌های خود را بصورت جداگانه تعریف کنید و بودجه‌ی تیم‌ها را مستقل مدیریت کنید و گزارش عملکرد هر تیم را بصورت جداگانه دریافت کنید. لکسویا با استفاده از هوش مصنوعی با تحلیل عملکرد تیم شما به شما گزارش عملکرد زیرساختی ارائه می‌کند و پیشنهاداتی را جهت بهبود به شما ارائه می‌دهد.",
        ar: "إدارة الفرق والمشاريع والميزانيات مركزيًا.",
      },
    },
    monitoring: {
      title: { en: "Monitoring", fa: "مانیتورینگ", ar: "المراقبة" },
      description: {
        en: "Real-time observability and alerting for your entire infrastructure.",
        fa: "مانیتورینگ و آلرتینگ لحظه‌ای زیرساخت شما",
        ar: "مراقبة وتنبيه في الوقت الفعلي.",
      },
      details: {
        en: "Comprehensive monitoring with metrics, logs, and traces. Custom dashboards and intelligent alerts.",
        fa: "مانیتورینگ جامع تمامی اتفاقات درون زیرساخت شما، ارائه داشبوردهای جذاب و جامع، تنظیم آلرت برای مواقع بحرانی و همچنین ارائه tracing برای مشاهده‌ی مسیر درخواست‌ها درون زیرساخت.",
        ar: "مراقبة شاملة مع المقاييس والسجلات والتتبعات.",
      },
    },
    consulting: {
      title: { en: "Consulting", fa: "مشاوره", ar: "الاستشارات" },
      description: {
        en: "Expert architecture review, migration planning, and cloud optimization.",
        fa: "بررسی معماری، برنامه‌ریزی مهاجرت و بهینه‌سازی ابری.",
        ar: "مراجعة الخبراء للهندسة وتخطيط الهجرة.",
      },
      details: {
        en: "Our certified architects help you design scalable systems and optimize costs.",
        fa: " در صورتی که برای معماری و انتخاب ابزارها و همچنین تهیه سخت‌افزار، نرم‌افزار و هر گونه تصمیمی در زمینه‌ی زیرساخت خوب به کمک نیاز دارید می‌توانید از خدمات مشاوره‌ی تخصصی ما استفاده کنید. معماران خبره ما کمک می‌کنند سیستم‌های مقیاس‌پذیر طراحی کنید و هزینه‌ها را بهینه کنید.",
        ar: "مهندسونا المعتمدون يساعدونكم في تصميم أنظمة قابلة للتوسع.",
      },
    },
    quickapps: {
      title: {
        en: "Quick Apps",
        fa: "برنامه‌های آماده",
        ar: "التطبيقات السريعة",
      },
      description: {
        en: "One-click deploy of 30+ popular open-source applications.",
        fa: "با یک کلیک بیش از ۳۰ برنامه متن‌باز محبوب را دیپلوی کنید.",
        ar: "نشر بنقرة واحدة لأكثر من ٣٠ تطبيقًا مفتوح المصدر.",
      },
      details: {
        en: "Launch databases, CMS, analytics, collaboration tools — all pre-configured. From WordPress to GitLab.",
        fa: "برای استفاده از ابزارهای تحت وب نیاز نیست که مراحل نصب و نگهداری را خودتان انجام دهید. در لکسویا با یک کلیک می‌توانید هر نرم‌افزاری را از لیست انتخاب کرده و نصب کنید و شروع به استفاده از آن نمایید. دغدغه‌ی نگهداری را به ما بسپارید. در صورتی که نرم‌افزاری را نیاز دارید که در لیست ما مشاهده‌ نمی‌کنید با تیم فنی ما تماس بگیرید تا در کمترین زمان آن نرم‌افزار را به لیست  اضافه کنیم تا بتوانید از آن استفاده کنید.",
        ar: "شغّل قواعد بيانات وCMS وتحليلات وأدوات تعاون.",
      },
    },
    streaming: {
      title: { en: "Streaming Server", fa: "سرور استریم", ar: "خادم بث" },
      description: {
        en: "High-performance streaming servers for media delivery.",
        fa: "سرورهای استریم با عملکرد بالا.",
        ar: "خوادم بث عالية الأداء لتوصيل الوسائط.",
      },
      details: {
        en: "Dedicated bandwidth, global CDN, and optimized infrastructure for video and audio streaming.",
        fa: "در صورتی که نیاز به بستری برای لایو استریم و ری استریم دارید می‌توانید از بستری لایو استریم لکسویا استفاده کنید. ما در این سرویس با بهترین کیفیت استریم‌های ویدئویی شما را دریافت می‌کنید و پس از بهینه‌سازی در اختیار شما قرار داده و یا به سمت پلتفرم‌های دیگر هدایت می‌کنیم.",
        ar: "نطاق ترددي مخصص و CDN عالمي وبنية تحتية محسنة لبث الفيديو والصوت.",
      },
    },
    sentry: {
      title: { en: "Sentry", fa: "سنتری", ar: "سنتري" },
      description: {
        en: "Error tracking and performance monitoring for your applications.",
        fa: "ردیابی خطا و مانیتورینگ عملکرد برای برنامه‌های شما.",
        ar: "تتبع الأخطاء ومراقبة الأداء لتطبيقاتك.",
      },
      details: {
        en: "Identify, diagnose, and fix issues in real time. Shared or dedicated instances available.",
        fa: "شناسایی، تشخیص و رفع مشکلات در زمان واقعی. سنتری به صورت اشتراکی یا اختصاصی موجود است.",
        ar: "تحديد وتشخيص وإصلاح المشكلات في الوقت الفعلي. تتوفر مثيلات مشتركة أو مخصصة.",
      },
    },
  },
};

/* ───── Footer ───── */
export const footer = {
  tagline: {
    en: "The cloud platform for the next generation of applications.",
    fa: "پلتفرمی برای همه نیازهای زیرساختی شما",
    ar: "المنصة السحابية للجيل القادم من التطبيقات.",
  },
  products: { en: "Products", fa: "محصولات", ar: "المنتجات" },
  quickApps: {
    en: "Quick Apps",
    fa: "برنامه‌های آماده",
    ar: "التطبيقات السريعة",
  },
  projectTypes: {
    en: "Project Types",
    fa: "تکنولوژی‌ها",
    ar: "أنواع المشاريع",
  },
  databases: { en: "Databases", fa: "دیتابیس", ar: "قواعد البيانات" },
  explore: { en: "Explore", fa: "لکسویا", ar: "استكشف" },
  aboutUs: { en: "About Us", fa: "درباره ما", ar: "معلومات عنا" },
  blog: { en: "Blog", fa: "وبلاگ", ar: "المدونة" },
  contactUs: { en: "Contact Us", fa: "تماس با ما", ar: "اتصل بنا" },
  privacy: { en: "Privacy", fa: "حریم خصوصی", ar: "الخصوصية" },
  terms: {
    en: "Terms & Agreements",
    fa: "قوانین و مرام‌نامه‌ها",
    ar: "الشروط والاتفاقيات",
  },
  servicesStatus: {
    en: "Services Status",
    fa: "وضعیت سرویس‌ها",
    ar: "حالة الخدمات",
  },
  allRights: {
    en: "All rights reserved.",
    fa: "بخشی از حقوق محفوظ است. نه همش:)",
    ar: "جميع الحقوق محفوظة.",
  },
  viewAll: { en: "All", fa: "همه", ar: "الكل" },
};

/* ───── Why Lexoya ───── */
export const whyLexoya = {
  subtitle: { en: "Why Lexoya?", fa: "چرا لکسویا؟", ar: "لماذا لکسویا؟" },
  heading: {
    en: "Built for teams that",
    fa: "ساخته شده برای تیم‌های",
    ar: "مبني للفرق التي",
  },
  highlighted: { en: "demand more", fa: "چابک", ar: "تطلب المزيد" },
  description: {
    en: "Thousands of companies choose Lexoya to power their most critical applications. Here is why.",
    fa: "هزاران شرکت برای حیاتی‌ترین برنامه‌هایشان لکسویا را انتخاب می‌کنند. به این دلایل.",
    ar: "آلاف الشركات تختار لکسویا لتشغيل تطبيقاتها الأكثر حساسية. إليكم السبب.",
  },
  reasons: {
    blazing: {
      title: {
        en: "Blazing Performance",
        fa: "عملکرد فوق‌العاده سریع",
        ar: "أداء فائق",
      },
      desc: {
        en: "Lightning-fast infrastructure with sub-millisecond latency and optimized compute that accelerates every request.",
        fa: "زیرساخت فوق‌سریع با تأخیر زیر میلی‌ثانیه و پردازش بهینه. هر درخواست با سرعت جواب می‌دهد.",
        ar: "بنية تحتية فائقة السرعة مع زمن استجابة أقل من ملي ثانية وحوسبة محسّنة تسرّع كل طلب.",
      },
    },
    requests: {
      title: {
        en: "100K+ Requests / s",
        fa: "چند میلیون درخواست در ثانیه",
        ar: "١٠٠ألف+ طلب/ثانية",
      },
      desc: {
        en: "Architected to effortlessly manage high-load traffic spikes, processing over 100,000 requests per second.",
        fa: "با لکسویا زیرساخت خود را برای پاسخگویی به چند میلیون درخواست در ثانیه آماده کنید.",
        ar: "مُصمم لإدارة ارتفاعات حركة المرور العالية بسهولة، ومعالجة أكثر من ١٠٠,٠٠٠ طلب في الثانية.",
      },
    },
    scale: {
      title: {
        en: "Unlimited Scale",
        fa: "مقیاس‌پذیری بی‌نهایت",
        ar: "توسع غير محدود",
      },
      desc: {
        en: "Scale your business as much as you want. There is no limit in here.",
        fa: "بیزنس خود را هر اندازه که دوست دارید گسترش دهید. خیالتان راحت. هیچ محدودیتی وجود ندارد.",
        ar: "وسّع أعمالك بقدر ما تريد. لا توجد حدود هنا.",
      },
    },
    k8s: {
      title: {
        en: "1,000+ K8s Pods",
        fa: "۱,۰۰۰+ پاد K8s",
        ar: "١,٠٠٠+ حجرة K8s",
      },
      desc: {
        en: "Battle-tested Kubernetes orchestration at massive scale — thousands of pods managed across production clusters.",
        fa: "ارکستراسیون Kubernetes در مقیاس عظیم. هزاران pod در خوشه‌های تولیدی.",
        ar: "تنسيق Kubernetes مجرّب على نطاق واسع - آلاف الحجرات المدارة عبر مجموعات إنتاجية.",
      },
    },
    devops: {
      title: {
        en: "10+ Years Experience",
        fa: "بیش از ۱۰ سال تجربه",
        ar: "١٠+ سنوات خبرة",
      },
      desc: {
        en: "Nearly a decade of hands-on experience in DevOps, Kubernetes, and cloud infrastructure.",
        fa: "نزدیک یک دهه تجربه عملی در DevOps، Kubernetes و زیرساخت ابری.",
        ar: "ما يقرب من عقد من الخبرة العملية في ديف أوبس وKubernetes والبنية التحتية السحابية.",
      },
    },
    everything: {
      title: {
        en: "Everything You Need",
        fa: "همه نیاز‌های شما در یک پلتفورم",
        ar: "كل ما تحتاجه",
      },
      desc: {
        en: "From virtual servers and GPU compute to managed databases and DevOps tooling — one platform.",
        fa: "از سرور مجازی و GPU تا دیتابیس مدیریت‌شده و ابزار DevOps. همه در یک پلتفرم.",
        ar: "من الخوادم الافتراضية وحوسبة GPU إلى قواعد البيانات المُدارة وأدوات ديف أوبس - منصة واحدة.",
      },
    },
    multidb: {
      title: {
        en: "Multi-Technology Support",
        fa: "پشتیبانی از انواع تکنولوژی‌ها",
        ar: "دعم قواعد بيانات متعددة",
      },
      desc: {
        en: "Full support for Persian and international databases including PostgreSQL, MySQL, MariaDB, and Redis.",
        fa: "از انواع تکنولوژی‌ها پشتیبانی می کنیم. انواع دیتابیس، انواع پروژه‌ها، انواع سرورها.",
        ar: "دعم كامل لقواعد البيانات الفارسية والدولية بما في ذلك PostgreSQL وMySQL وMariaDB وRedis.",
      },
    },
    support: {
      title: {
        en: "24 / 7 Support",
        fa: "پشتیبانی ۲۴/۷",
        ar: "دعم على مدار الساعة",
      },
      desc: {
        en: "Round-the-clock expert support staffed by real engineers who know infrastructure inside out.",
        fa: "پشتیبانی ۲۴ ساعته. همیشه در کنار شما هستیم.",
        ar: "دعم خبير على مدار الساعة يعمل به مهندسون حقيقيون يعرفون البنية التحتية من الداخل والخارج.",
      },
    },
    custom: {
      title: {
        en: "You Ask, We Deliver",
        fa: "شما بخواهید، ما تحویل می‌دهیم",
        ar: "أنت تطلب، نحن ننفذ",
      },
      desc: {
        en: "Tell us what you need and we will build it — from a single feature to a complete platform.",
        fa: "هدف ما رفع نیاز شماست. اگر نیاز به ساخت سرویس جدیدی باشد برایتان می‌سازیم.",
        ar: "أخبرنا بما تحتاجه وسنبنيه - من ميزة واحدة إلى منصة كاملة.",
      },
    },
  },
};

/* ───── CTA Form ───── */
export const cta = {
  formTitle: { en: "Submit a Request", fa: "درخواست مشاوره", ar: "تقديم طلب" },
  formSubtitle: {
    en: "Fill in the details and we will get back to you",
    fa: "اطلاعات را وارد کنید. با شما تماس می‌گیریم.",
    ar: "املأ التفاصيل وسنرد عليك",
  },
  submitBtn: { en: "Submit Request", fa: "درخواست مشاوره", ar: "تقديم الطلب" },
  formLabels: {
    fullName: {
      en: "Full Name *",
      fa: "نام و نام خانوادگی *",
      ar: "الاسم الكامل *",
    },
    contactNumber: {
      en: "Contact Number *",
      fa: "شماره تماس *",
      ar: "رقم الاتصال *",
    },
    email: { en: "Email *", fa: "ایمیل *", ar: "البريد الإلكتروني *" },
    businessName: {
      en: "Business Name *",
      fa: "نام کسب‌وکار *",
      ar: "اسم النشاط التجاري *",
    },
    product: {
      en: "Product You Need *",
      fa: "محصول مورد نیاز *",
      ar: "المنتج الذي تحتاجه *",
    },
    requestType: {
      en: "Request Type *",
      fa: "نوع درخواست *",
      ar: "نوع الطلب *",
    },
    message: { en: "Your Message *", fa: "پیام شما *", ar: "رسالتك *" },
  },
  placeholders: {
    fullName: { en: "John Doe", fa: "نام و نام خانوادگی", ar: "الاسم الكامل" },
    contactNumber: {
      en: "+98 992 011 5977",
      fa: "+۹۸ ۹۱۲ ۳۴۵ ۶۷۸۹",
      ar: "+٩٨ ٩١٢ ٣٤٥ ٦٧٨٩",
    },
    email: {
      en: "john@company.com",
      fa: "example@company.com",
      ar: "example@company.com",
    },
    businessName: { en: "Acme Corp", fa: "نام شرکت", ar: "اسم الشركة" },
    product: { en: "Select a product", fa: "انتخاب محصول", ar: "اختر منتجًا" },
    requestType: {
      en: "Select request type",
      fa: "انتخاب نوع درخواست",
      ar: "اختر نوع الطلب",
    },
    message: {
      en: "Describe your requirements...",
      fa: "نیازهای خود را توضیح دهید...",
      ar: "صف متطلباتك...",
    },
  },
  requestTypes: {
    consulting: {
      en: "The consulting before buying",
      fa: "مشاوره قبل از خرید",
      ar: "الاستشارة قبل الشراء",
    },
    price: {
      en: "The price and the method of paying",
      fa: "قیمت و روش پرداخت",
      ar: "السعر وطريقة الدفع",
    },
    organizational: {
      en: "Requesting organizational services",
      fa: "درخواست خدمات سازمانی",
      ar: "طلب الخدمات المؤسسية",
    },
    other: { en: "Others", fa: "سایر", ar: "أخرى" },
  },
  successMsg: {
    en: "Your request has been sent successfully.",
    fa: "درخواست شما با موفقیت ثبت شد.",
    ar: "تم إرسال طلبك بنجاح.",
  },
  errorMsg: {
    en: "An error occurred. Please try again.",
    fa: "خطایی رخ داد. لطفاً دوباره تلاش کنید.",
    ar: "حدث خطأ. يرجى المحاولة مرة أخرى.",
  },
};

/* ───── CTA Section ───── */
export const ctaSection = {
  heading1: { en: "Need help or", fa: "نیاز به کمک یا", ar: "هل تحتاج إلى" },
  heading2: {
    en: "expert consulting?",
    fa: "مشاوره تخصصی دارید؟",
    ar: "استشارات الخبراء؟",
  },
  description: {
    en: "Have a question about our services or your infrastructure? Our engineers are here to guide you, answer your questions, and help you choose the right solution.",
    fa: "در مورد خدمات یا زیرساخت خود سوال دارید؟ مهندسان ما برای راهنمایی، استعلام قیمت و انتخاب بهترین راه‌حل در کنار شما هستند.",
    ar: "هل لديك سؤال حول خدماتنا أو بنيتك التحتية؟ مهندسونا جاهزون لإرشادك والإجابة عن أسئلتك ومساعدتك على اختيار الحل المناسب.",
  },
  callUs: { en: "Call Us", fa: "تماس بگیرید", ar: "اتصل بنا" },
  submitRequest: {
    en: "Submit a Request",
    fa: "درخواست مشاوره",
    ar: "تقديم طلب",
  },
  supportNote: {
    en: "Talk to a real engineer, not an automated bot",
    fa: "گفت‌وگو با مهندس واقعی، نه ربات",
    ar: "تحدث مع مهندس حقيقي، لا روبوت",
  },
};

/* ───── Product Landing ───── */
export const productLanding = {
  getStarted: {
    en: "Get Started",
    fa: "شروع کنید",
    ar: "ابدأ الآن",
  },
  exploreAll: {
    en: "See what is included",
    fa: "قابلیت‌ها را ببینید",
    ar: "اطّلع على الميزات",
  },
  everythingHeader: {
    en: "Everything you get with",
    fa: "قابلیت‌های",
    ar: "كل ما تحصل عليه مع",
  },
  everythingDesc: {
    en: "Enterprise-grade features designed to give you the performance, reliability, and control you need.",
    fa: "ویژگی‌های سطح سازمانی برای عملکرد، قابلیت اطمینان و کنترلی که نیاز دارید.",
    ar: "ميزات على مستوى المؤسسات مصممة لمنحك الأداء والموثوقية والتحكم الذي تحتاجه.",
  },
};

/* ───── About ───── */
export const about = {
  subtitle: {
    en: "About Lexoya",
    fa: "درباره لکسویا",
    ar: "معلومات عن لكسويا",
  },
  heading1: {
    en: "Built by engineers,",
    fa: "ساخته شده توسط مهندسان،",
    ar: "بُني بواسطة مهندسين،",
  },
  heading2: { en: "for engineers", fa: "برای مهندسان", ar: "للمهندسين" },
  para1: {
    en: "Lexoya is an Iranian cloud provider built for businesses and companies that need infrastructure they can rely on.",
    fa: "لکسویا یک ارائه‌دهنده ابری ایرانی است که برای کسب‌وکارها و شرکت‌هایی ساخته شده که به زیرساختی قابل اتکا نیاز دارند.",
    ar: "لکسویا مزوّد سحابي إيراني بُني للشركات والمؤسسات التي تحتاج إلى بنية تحتية يمكن الاعتماد عليها.",
  },
  para2: {
    en: "We focus on real engineering: a team that has run large-scale infrastructure and knows the challenges of scaling and reliability firsthand. Instead of empty promises and invented numbers, we offer real resources, transparent pricing, and direct access to engineers.",
    fa: "تمرکز ما روی مهندسی واقعی است: تیمی که زیرساخت‌های بزرگ را اداره کرده و چالش‌های مقیاس و پایداری را از نزدیک می‌شناسد. به‌جای وعده‌های توخالی و آمارسازی، منابع واقعی، قیمت شفاف و دسترسی مستقیم به مهندس ارائه می‌دهیم.",
    ar: "نركّز على الهندسة الحقيقية: فريق أدار بنى تحتية واسعة النطاق ويعرف تحديات التوسع والموثوقية عن قرب. بدلًا من الوعود الفارغة والأرقام الوهمية، نقدّم موارد حقيقية وتسعيرًا شفافًا ووصولًا مباشرًا إلى المهندسين.",
  },
  companyInfo: {
    headquarters: {
      label: { en: "Headquarters", fa: "دفتر مرکزی", ar: "المقر الرئيسي" },
      value: { en: "Qom, Iran", fa: "قم، ایران", ar: "قم، إيران" },
    },
    dataCenters: {
      label: { en: "Data Centers", fa: "دیتاسنتر", ar: "مراكز البيانات" },
      value: { en: "Iran & Germany", fa: "ایران و آلمان", ar: "إيران وألمانيا" },
    },
    customers: {
      label: { en: "Customers", fa: "مشتریان", ar: "العملاء" },
      value: { en: "4,000+", fa: "بیش از ۴۰۰۰", ar: "أكثر من ٤٠٠٠" },
    },
    uptime: {
      label: { en: "Uptime SLA", fa: "SLA آپتایم", ar: "SLA وقت التشغيل" },
      value: "99.99%",
    },
  },
  engineers: {
    subtitle: { en: "Our Engineers", fa: "مهندسان ما", ar: "مهندسونا" },
    heading1: {
      en: "Built by engineers who",
      fa: "ساخته‌شده توسط مهندسانی که",
      ar: "بُني بأيدي مهندسين",
    },
    heading2: {
      en: "know scale",
      fa: "مقیاس را می‌شناسند",
      ar: "يعرفون التوسع",
    },
    desc: {
      en: "Our engineers have worked at major Iranian companies such as Eitaa and Basalam, where they operated high-traffic infrastructure and solved real scaling, reliability, and performance challenges.",
      fa: "مهندسان ما در شرکت‌های بزرگ ایرانی مانند ایتا و باسلام کار کرده‌اند؛ جایی که زیرساخت‌های پرترافیک را اداره کردند و چالش‌های واقعی مقیاس، پایداری و عملکرد را حل کردند.",
      ar: "عمل مهندسونا في شركات إيرانية كبرى مثل إيتا وباسلام، حيث أداروا بنى تحتية عالية الحركة وحلّوا تحديات حقيقية في التوسع والموثوقية والأداء.",
    },
    points: [
      {
        en: "Experience running high-traffic, large-scale systems",
        fa: "تجربه اداره سیستم‌های پرترافیک در مقیاس بزرگ",
        ar: "خبرة في إدارة أنظمة عالية الحركة واسعة النطاق",
      },
      {
        en: "Hands-on with reliability, scaling, and performance tuning",
        fa: "تجربه عملی پایداری، مقیاس‌پذیری و بهینه‌سازی عملکرد",
        ar: "خبرة عملية في الموثوقية والتوسع وضبط الأداء",
      },
      {
        en: "Direct engineer-to-engineer support — no ticket queues",
        fa: "پشتیبانی مستقیم مهندس به مهندس، بدون صف تیکت",
        ar: "دعم مباشر من مهندس إلى مهندس بلا طوابير تذاكر",
      },
      {
        en: "Enterprise-focused: we build for businesses and teams",
        fa: "متمرکز بر سازمان‌ها: برای کسب‌وکارها و تیم‌ها می‌سازیم",
        ar: "تركيز على المؤسسات: نبني للشركات والفرق",
      },
    ],
  },
  tech: {
    subtitle: {
      en: "Technology & Infrastructure",
      fa: "فناوری و زیرساخت",
      ar: "التقنية والبنية التحتية",
    },
    heading1: { en: "Modern infrastructure,", fa: "زیرساخت مدرن،", ar: "بنية تحتية حديثة،" },
    heading2: { en: "engineered to scale", fa: "مهندسی‌شده برای مقیاس", ar: "مهندَسة للتوسع" },
    desc: {
      en: "Our platform runs on Kubernetes: containers are orchestrated automatically for high availability, rolling updates, and elastic scaling. On top of it we provide managed databases, S3-compatible object storage, GPU compute, and CI/CD.",
      fa: "پلتفرم ما روی کوبرنتیز اجرا می‌شود: کانتینرها به‌صورت خودکار برای دسترسی بالا، به‌روزرسانی بدون قطعی و مقیاس کشسان ارکستر می‌شوند. روی همین بستر، دیتابیس مدیریت‌شده، ذخیره‌سازی سازگار با S3، محاسبات GPU و CI/CD ارائه می‌دهیم.",
      ar: "تعمل منصتنا على Kubernetes: تُنسّق الحاويات تلقائيًا لتوفّر عالٍ وتحديثات متدرجة وتوسع مرن. وفوقها نوفّر قواعد بيانات مُدارة وتخزينًا متوافقًا مع S3 وحوسبة GPU وCI/CD.",
    },
    note: {
      en: "Kubernetes-native platform",
      fa: "پلتفرم مبتنی بر کوبرنتیز",
      ar: "منصة مبنية على Kubernetes",
    },
  },
  logos: {
    trusted: {
      en: "Companies that trust us",
      fa: "شرکت‌هایی که به ما اعتماد کرده‌اند",
      ar: "شركات تثق بنا",
    },
  },
  beliefsHeading1: { en: "What we", fa: "آنچه", ar: "ما" },
  beliefsHeading2: { en: "believe", fa: "باور داریم", ar: "نؤمن به" },
  beliefsDesc: {
    en: "These principles guide every decision we make.",
    fa: "این اصول مبنای همه تصمیم‌های ماست.",
    ar: "هذه المبادئ توجه كل قرار نتخذه.",
  },
  beliefs: {
    trust: {
      title: {
        en: "Infrastructure is trust",
        fa: "زیرساخت اعتماد است",
        ar: "البنية التحتية هي الثقة",
      },
      desc: {
        en: "When you host with us, you are trusting us with your business. We take that seriously.",
        fa: "وقتی پیش ما میزبانی می‌کنید، کسب‌وکارتان را به ما می‌سپارید. این را جدی می‌گیریم.",
        ar: "عندما تستضيف عندنا، فإنك تثق بنا في عملك. نأخذ ذلك على محمل الجد.",
      },
    },
    support: {
      title: {
        en: "Support should be human",
        fa: "پشتیبانی باید انسانی باشد",
        ar: "الدعم يجب أن يكون بشريًا",
      },
      desc: {
        en: "No chatbots, no ticket queues. Every support request reaches an actual engineer.",
        fa: "نه ربات چت، نه صف تیکت. هر درخواست به یک مهندس واقعی می‌رسد.",
        ar: "لا روبوتات محادثة، ولا قوائم تذاكر. كل طلب دعم يصل إلى مهندس حقيقي.",
      },
    },
    pricing: {
      title: {
        en: "Pricing must be transparent",
        fa: "قیمت‌گذاری باید شفاف باشد",
        ar: "التسعير يجب أن يكون شفافًا",
      },
      desc: {
        en: "You should never need a calculator to understand your bill. No surprise fees.",
        fa: "برای فهم صورتحساب نباید ماشین حساب بیاورید. هزینه غافلگیرکننده نداریم.",
        ar: "لا يجب أن تحتاج أبدًا إلى آلة حاسبة لفهم فاتورتك. لا رسوم مفاجئة.",
      },
    },
    performance: {
      title: {
        en: "Performance is not optional",
        fa: "عملکرد اختیاری نیست",
        ar: "الأداء ليس اختياريًا",
      },
      desc: {
        en: "Every layer of our stack is optimized for speed. We eliminate bottlenecks so your applications run as fast as possible.",
        fa: "همه لایه‌های stack برای سرعت بهینه شده. گلوگاه‌ها را حذف می‌کنیم تا برنامه‌هایتان تا حد ممکن سریع باشند.",
        ar: "كل طبقة في بنيتنا محسّنة للسرعة. نزيل الاختناقات حتى تعمل تطبيقاتك بأسرع ما يمكن.",
      },
    },
    scale: {
      title: {
        en: "Scale without lock-in",
        fa: "مقیاس بدون قفل",
        ar: "توسع بدون تقييد",
      },
      desc: {
        en: "Month-to-month billing, no long-term contracts, and the freedom to change plans anytime.",
        fa: "صورت‌حساب ماهانه، بدون قرارداد بلندمدت. هر وقت خواستید plan را عوض کنید.",
        ar: "فوترة شهرية، بدون عقود طويلة الأجل، وحرية تغيير الخطط في أي وقت.",
      },
    },
    community: {
      title: {
        en: "Community drives progress",
        fa: "جامعه پیشرفت را هدایت می‌کند",
        ar: "المجتمع يقود التقدم",
      },
      desc: {
        en: "We grow alongside our customers. Your feedback shapes our roadmap.",
        fa: "کنار مشتری‌هایمان رشد می‌کنیم. بازخورد شما مسیر ما را مشخص می‌کند.",
        ar: "ننمو جنبًا إلى جنب مع عملائنا. ملاحظاتكم تشكل خارطة طريقنا.",
      },
    },
  },
};

/* ───── Contact ───── */
export const contact = {
  subtitle: { en: "Contact Us", fa: "تماس با ما", ar: "اتصل بنا" },
  heading1: { en: "We are here to", fa: "ما اینجا هستیم تا", ar: "نحن هنا لـ" },
  heading2: { en: "help", fa: "کمک کنیم", ar: "المساعدة" },
  description: {
    en: "Have a question about our services? Need help choosing the right plan? Our team is ready to assist you.",
    fa: "از خدمات ما سوال دارید؟ برای انتخاب پلن مناسب کمک می‌خواهید؟ تیم ما آماده کمک است.",
    ar: "هل لديك سؤال حول خدماتنا؟ هل تحتاج مساعدة في اختيار الخطة المناسبة؟ فريقنا مستعد لمساعدتك.",
  },
  socialFollow: {
    en: "Follow us on social media:",
    fa: "ما را در شبکه‌های اجتماعی دنبال کنید:",
    ar: "تابعنا على وسائل التواصل الاجتماعي:",
  },
  formTitle: {
    en: "Send us a message",
    fa: "برای ما پیام بفرستید",
    ar: "أرسل لنا رسالة",
  },
  formSubtitle: {
    en: "Fill out the form and we will get back to you within 2 hours.",
    fa: "فرم درخواست خود را پر کنید تا متخصصان ما با شما تماس بگیرند.",
    ar: "املأ النموذج وسنرد عليك خلال ساعتين.",
  },
  formLabels: {
    fullName: {
      en: "Full Name *",
      fa: "نام و نام خانوادگی *",
      ar: "الاسم الكامل *",
    },
    email: { en: "Email *", fa: "ایمیل *", ar: "البريد الإلكتروني *" },
    subject: { en: "Subject *", fa: "موضوع *", ar: "الموضوع *" },
    message: { en: "Message *", fa: "پیام *", ar: "الرسالة *" },
  },
  formPlaceholders: {
    fullName: { en: "John Doe", fa: "نام و نام خانوادگی", ar: "الاسم الكامل" },
    email: {
      en: "john@company.com",
      fa: "example@company.com",
      ar: "example@company.com",
    },
    subject: {
      en: "How can we help you?",
      fa: "چگونه می‌توانیم کمک کنیم؟",
      ar: "كيف يمكننا مساعدتك؟",
    },
    message: {
      en: "Tell us more about your needs...",
      fa: "درباره نیازهای خود بیشتر بگویید...",
      ar: "أخبرنا المزيد عن احتياجاتك...",
    },
  },
  submitBtn: { en: "Send Message", fa: "ارسال پیام", ar: "إرسال الرسالة" },
  required: { en: "Required", fa: "الزامی", ar: "مطلوب" },
  contactMethods: {
    phone: { title: { en: "Phone", fa: "تلفن", ar: "الهاتف" } },
    email: { title: { en: "Email", fa: "ایمیل", ar: "البريد الإلكتروني" } },
  },
};

/* ───── Pricing ───── */
export const pricing = {
  heroKicker: { en: "Pricing", fa: "قیمت‌ها و پلن‌ها", ar: "التسعير" },
  heroTitle: { en: "Simple, transparent", fa: "قیمت‌گذاری", ar: "تسعير" },
  heroHighlight: { en: "pricing", fa: "شفاف سرویس‌ها", ar: "بسيط وشفاف" },
  heroDesc: {
    en: "Transparent, pay-as-you-go pricing tailored for your workload. Choose the plan that best fits your needs.",
    fa: "قیمت‌گذاری شفاف و منصفانه. پلنی را انتخاب کنید که بهترین تناسب را با نیازهایتان دارد.",
    ar: "تسعير شفاف ودفع حسب الاستخدام مصمم خصيصًا لعبء عملك. اختر الخطة التي تناسب احتياجاتك.",
  },
  calculatorCta: {
    en: "Open the cost calculator",
    fa: "ماشین حساب هزینه",
    ar: "افتح حاسبة التكلفة",
  },
  categories: {
    servers: {
      label: { en: "Cloud Servers", fa: "سرور مجازی ابری", ar: "خوادم سحابية" },
      desc: {
        en: "VPS with dedicated resources",
        fa: "VPS با منابع اختصاصی",
        ar: "VPS بموارد مخصصة",
      },
    },
    gpu: {
      label: { en: "GPU Servers", fa: "سرورهای GPU", ar: "خوادم GPU" },
      desc: {
        en: "AI & rendering compute",
        fa: "محاسبات هوش مصنوعی و رندر",
        ar: "حوسبة الذكاء الاصطناعي والرندر",
      },
    },
    storage: {
      label: { en: "S3 Storage", fa: "ذخیره‌سازی S3", ar: "تخزين S3" },
      desc: {
        en: "Object storage with fixed monthly plans",
        fa: "ذخیره‌سازی شیء با پلن‌های ماهانه ثابت",
        ar: "تخزين كائنات بخطط شهرية ثابتة",
      },
    },
    paas: {
      label: { en: "Cloud Platform", fa: "قیمت‌گذاری", ar: "منصة سحابية" },
      desc: {
        en: "PaaS for any project type",
        fa: "PaaS برای هر نوع پروژه",
        ar: "PaaS لأي نوع مشروع",
      },
    },
    database: {
      label: { en: "Databases", fa: "دیتابیس‌ها", ar: "قواعد البيانات" },
      desc: {
        en: "Managed databases",
        fa: "دیتابیس‌های مدیریت‌شده",
        ar: "قواعد بيانات مُدارة",
      },
    },
    quickapps: {
      label: {
        en: "Quick Apps",
        fa: "برنامه‌های آماده",
        ar: "التطبيقات السريعة",
      },
      desc: {
        en: "One-click app deployment",
        fa: "دیپلوی برنامه با یک کلیک",
        ar: "نشر التطبيق بنقرة واحدة",
      },
    },
    streaming: {
      label: { en: "Streaming", fa: "استریم", ar: "البث" },
      desc: {
        en: "Dedicated streaming servers",
        fa: "سرورهای استریم اختصاصی",
        ar: "خوادم بث مخصصة",
      },
    },
    sentry: {
      label: { en: "Sentry", fa: "سنتری", ar: "سنتري" },
      desc: { en: "Error tracking", fa: "ردیابی خطا", ar: "تتبع الأخطاء" },
    },
  },
  vps: {
    heading: { en: "Cloud Servers", fa: "قیمت‌گذاری", ar: "تسعير" },
    highlighted: { en: "pricing", fa: "سرورهای ابری", ar: "الخوادم السحابية" },
    description: {
      en: "High-performance virtual private servers with dedicated resources. Deploy instantly with SSD NVMe storage and your choice of Linux distributions.",
      fa: "سرورهای مجازی با عملکرد بالا و منابع اختصاصی. تحویل فوری با ذخیره‌سازی SSD/NVMe و سیستم‌عامل انتخابی شما.",
      ar: "خوادم افتراضية خاصة عالية الأداء بموارد مخصصة. انشر فورًا مع تخزين SSD NVMe.",
    },
  },
  gpu: {
    heading: { en: "GPU Servers", fa: "قیمت‌گذاری", ar: "تسعير" },
    highlighted: { en: "pricing", fa: "سرورهای GPU", ar: "خوادم GPU" },
    description: {
      en: "Dedicated NVIDIA GPU servers for AI training, 3D rendering, video processing, and HPC workloads.",
      fa: "سرورهای GPU اختصاصی NVIDIA برای آموزش هوش مصنوعی، رندر سه‌بعدی و پردازش‌های سنگین.",
      ar: "خوادم GPU مخصصة من NVIDIA لتدريب الذكاء الاصطناعي والرندر ثلاثي الأبعاد.",
    },
  },
  s3: {
    heading: { en: "S3 object storage", fa: "قیمت‌گذاری", ar: "تسعير" },
    highlighted: { en: "pricing", fa: "ذخیره‌سازی S3", ar: "تخزين كائنات S3" },
    description: {
      en: "Fixed monthly plans according to your storage tier. All plans backed by reliable HDD storage with built-in browser access.",
      fa: "پلن‌های ثابت ماهانه بر اساس سطح ذخیره‌سازی شما. همه پلن با ۳ رپلیکا از فایل‌ها، لایه‌ی کش پر سرعت و مطمئن.",
      ar: "خطط شهرية ثابتة وفقًا لمستوى التخزين الخاص بك. جميع الخطط مدعومة بتخزين HDD موثوق مع وصول مدمج عبر المتصفح.",
    },
    recommended: { en: "Recommended", fa: "محبوب ترین", ar: "موصى به" },
    tomanMonth: { en: "Toman/month", fa: "تومان/ماهانه", ar: "تومان/الشهر" },
    getStarted: { en: "Get Started", fa: "شروع کنید", ar: "ابدأ الآن" },
  },
  paas: {
    heading: { en: "Cloud Platform", fa: "قیمت‌گذاری", ar: "تسعير" },
    highlighted: {
      en: "resource pricing",
      fa: "دیتابیس‌ها",
      ar: "المنصة السحابية",
    },
    description: {
      en: "Select your framework or language to instantly calculate resource costs. Our PaaS handles all the underlying infrastructure, letting you scale seamlessly from prototype to production.",
      fa: "فریم‌ورک یا زبان خود را انتخاب کنید تا هزینه منابع فورا محاسبه شود. پلتفرم ابری ما تمام زیرساخت‌ها را مدیریت می‌کند تا به راحتی از نسخه اولیه تا مقیاس میلیونی اوج بگیرید.",
      ar: "حدد إطار العمل أو لغتك لحساب تكاليف الموارد فوراً. تدير منصتنا السحابية كافة البنية التحتية لتتوسع بسهولة من النموذج الأولي إلى الإنتاج.",
    },
  },
  qa: {
    heading: { en: "Quick Apps", fa: "قیمت‌گذاری", ar: "تسعير" },
    highlighted: {
      en: "resource pricing",
      fa: "برنامه‌های آماده",
      ar: "التطبيقات السريعة",
    },
    description: {
      en: "Choose from dozens of pre-configured open-source applications to forecast your hosting expenses. Each app is deployed in an optimized, ready-to-use environment.",
      fa: "برنامه‌ی دلخواه خود را از میان ده‌ها برنامه متن‌باز از پیش آماده شده انتخاب کنید تا هزینه‌ی آن را ببینید. هر برنامه در یک محیط بهینه و اختصاصی راه‌اندازی می‌شود.",
      ar: "اختر من بين عشرات التطبيقات مفتوحة المصدر المجهزة مسبقاً لتوقع نفقات الاستضافة. يتم نشر كل تطبيق في بيئة محسنة وجاهزة للاستخدام.",
    },
  },
  db: {
    heading: { en: "Managed Databases", fa: "قیمت‌گذاری", ar: "تسعير" },
    highlighted: {
      en: "resource pricing",
      fa: "دیتابیس‌ها",
      ar: "قواعد البيانات المُدارة",
    },
    description: {
      en: "Select a database engine to see pricing based on your required compute and storage. Our DBaaS offers built-in high availability and automated backups.",
      fa: "دیتابیس مورد نظر خود را انتخاب کنید تا قیمت‌ها را بر اساس پردازش و حافظه موردنیازتان ببینید. دیتابیس ابری ما دارای دسترسی بالا (HA) و پشتیبان‌گیری خودکار است.",
      ar: "حدد محرك قاعدة بيانات لرؤية الأسعار بناءً على متطلبات الحوسبة والتخزين. توفر قواعد بياناتنا السحابية توفراً عالياً ونسخاً احتياطية تلقائية.",
    },
  },
  cta: {
    heading: {
      en: "Not sure which plan fits your",
      fa: "مطمئن نیستید کدام پلن مناسب",
      ar: "لست متأكدًا أي خطة تناسب",
    },
    highlight: {
      en: "workload?",
      fa: "بار کاری شماست؟",
      ar: "عبء العمل الخاص بك؟",
    },
    description: {
      en: "Our engineering team will help you choose the right configuration and optimize your infrastructure for performance and cost.",
      fa: "تیم فنی ما کمک می‌کند پیکربندی مناسب را انتخاب کنید و زیرساخت را برای عملکرد و هزینه بهینه کنید.",
      ar: "سيساعدك فريقنا الهندسي في اختيار التكوين المناسب وتحسين بنيتك التحتية من حيث الأداء والتكلفة.",
    },
    talkToEngineer: {
      en: "Talk to an Engineer",
      fa: "ارتباط با تیم فنی",
      ar: "تحدث إلى مهندس",
    },
    contactSales: {
      en: "Contact Sales",
      fa: "تماس با فروش",
      ar: "اتصل بالمبيعات",
    },
  },
  cardLabels: {
    cpu: { en: "CPU", fa: "پردازنده", ar: "المعالج" },
    ram: { en: "RAM", fa: "RAM", ar: "RAM" },
    storage: { en: "Storage", fa: "ذخیره‌سازی", ar: "التخزين" },
    traffic: { en: "Traffic", fa: "ترافیک", ar: "حركة المرور" },
    locations: { en: "Locations", fa: "مکان‌ها", ar: "المواقع" },
    or: { en: "or", fa: "یا", ar: "أو" },
    recommended: { en: "Recommended", fa: "محبوب ترین", ar: "موصى به" },
    contactSales: {
      en: "Contact Sales",
      fa: "تماس با فروش",
      ar: "اتصل بالمبيعات",
    },
    orderNow: { en: "Order Now", fa: "ثبت سفارش", ar: "اطلب الآن" },
    heading: {
      en: "Simple, transparent pricing",
      fa: "قیمت‌گذاری ساده و شفاف",
      ar: "تسعير بسيط وشفاف",
    },
    highlighted: {
      en: "for every scale",
      fa: "برای همه پلن‌ها",
      ar: "لكل مقياس",
    },
    description: {
      en: "Choose the plan that fits your workload. All plans include 24/7 support, monitoring, and a 7-day satisfaction guarantee.",
      fa: "پلن مناسب با کار خود را انتخاب کنید. همه پلن‌ها با منابع اختصاصی و پهنای باند نامحدود ارائه می‌شوند.",
      ar: "اختر الخطة التي تناسب عبء عملك. جميع الخطط تتضمن دعمًا على مدار الساعة ومراقبة وضمان استرداد لمدة ٧ أيام.",
    },
    perMonth: { en: "Toman/monthly", fa: "تومان/ماهانه", ar: "تومان/الشهر" },
    perDay: { en: "Toman/daily", fa: "تومان/روزانه", ar: "تومان/يوميًا" },
    contactForPrice: {
      en: "Contact us for pricing",
      fa: "قیمت با تماس با پشتیبانی",
      ar: "السعر بالتواصل مع الدعم",
    },
    livePrice: {
      en: "Live price",
      fa: "قیمت لحظه‌ای",
      ar: "سعر مباشر",
    },
  },
  s3Features: {
    heading: {
      en: "Built-in S3 browser (upload, download & manage)",
      fa: "مرورگر S3 داخلی (آپلود، دانلود و مدیریت)",
      ar: "متصفح S3 مدمج (رفع وتنزيل وإدارة)",
    },
    hdd: {
      en: "Reliable HDD storage",
      fa: "ذخیره‌سازی HDD مطمئن",
      ar: "تخزين HDD موثوق",
    },
    ha: {
      en: "High Availability (HA)",
      fa: "دسترسی بالا (HA)",
      ar: "توفر عالي (HA)",
    },
    durability: {
      en: "99.99% durability",
      fa: "دوام ۹۹.۹۹٪",
      ar: "%99.99 متانة",
    },
    retrieval: {
      en: "Instant object retrieval",
      fa: "بازیابی فوری اشیاء",
      ar: "استرجاع فوري للكائنات",
    },
    planDesc25: {
      en: "Ideal for personal projects, small websites, and testing environments.",
      fa: "مناسب برای پروژه‌های شخصی، وبسایت‌های کوچک و محیط‌های آزمایشی.",
      ar: "مثالي للمشاريع الشخصية والمواقع الصغيرة وبيئات الاختبار.",
    },
    planDesc50: {
      en: "Great for growing applications and small business data storage.",
      fa: "عالی برای برنامه‌های در حال رشد و ذخیره‌سازی داده‌های کسب‌وکار کوچک.",
      ar: "رائع للتطبيقات المتنامية وتخزين بيانات الأعمال الصغيرة.",
    },
    planDesc100: {
      en: "Perfect for medium businesses, application data, and media libraries.",
      fa: "عالی برای کسب‌وکارهای متوسط، داده‌های برنامه و کتابخانه‌های رسانه.",
      ar: "مثالي للشركات المتوسطة وبيانات التطبيقات ومكتبات الوسائط.",
    },
    planDesc250: {
      en: "Designed for large applications, backups, and media archives.",
      fa: "طراحی شده برای برنامه‌های بزرگ، پشتیبان‌گیری و آرشیو رسانه.",
      ar: "مصمم للتطبيقات الكبيرة والنسخ الاحتياطية وأرشيفات الوسائط.",
    },
    planDesc500: {
      en: "Built for enterprise data, compliance archives, and high-volume storage.",
      fa: "ساخته شده برای داده‌های سازمانی، آرشیو انطباق و ذخیره‌سازی با حجم بالا.",
      ar: "مبني للبيانات المؤسسية وأرشيفات الامتثال والتخزين عالي الحجم.",
    },
    planDesc1000: {
      en: "For data-intensive workloads, large-scale backups, and content delivery.",
      fa: "برای بارهای کاری داده‌محور، پشتیبان‌گیری در مقیاس بزرگ و تحویل محتوا.",
      ar: "لأعباء العمل المكثفة بالبيانات والنسخ الاحتياطية واسعة النطاق وتوصيل المحتوى.",
    },
    planDesc2000: {
      en: "Massive storage for data lakes, analytics, and multi-region replication.",
      fa: "ذخیره‌سازی عظیم برای داده‌لِیک‌ها، تحلیل و تکرار چندمنطقه‌ای.",
      ar: "تخزين ضخم لبحيرات البيانات والتحليلات والتكرار متعدد المناطق.",
    },
    planDescEnterprise: {
      en: "Enterprise-scale storage for data warehouses and large-scale compliance.",
      fa: "ذخیره‌سازی مقیاس سازمانی برای انبارهای داده و انطباق در مقیاس بزرگ.",
      ar: "تخزين على نطاق مؤسسي لمستودعات البيانات والامتثال واسع النطاق.",
    },
  },
  calculator: {
    estimate: {
      en: "Estimate your monthly cost",
      fa: "هزینه ماهانه خود را تخمین بزنید",
      ar: "قدّر تكلفتك الشهرية",
    },
    close: {
      en: "Close calculator",
      fa: "بستن ماشین حساب",
      ar: "إغلاق الآلة الحاسبة",
    },
    cpuCores: { en: "CPU Cores", fa: "هسته‌های CPU", ar: "أنوية المعالج" },
    ramGb: { en: "RAM (GB)", fa: "RAM (GB)", ar: "RAM (GB)" },
    storageGb: { en: "Storage (GB)", fa: "حافظه (GB)", ar: "التخزين (GB)" },
    replicas: { en: "Replicas", fa: "رپلیکاها", ar: "النسخ المكررة" },
    costBreakdown: {
      en: "Cost Breakdown",
      fa: "تفکیک هزینه",
      ar: "تفصيل التكلفة",
    },
    baseFee: { en: "Base fee", fa: "هزینه پایه", ar: "الرسوم الأساسية" },
    estimatedTotal: {
      en: "Estimated total",
      fa: "تخمین مجموع",
      ar: "المجموع المقدر",
    },
    irrMonth: { en: "IRR/month", fa: "تومان/ماهانه", ar: "تومان/الشهر" },
    deploy: { en: "Deploy", fa: "دیپلوی کنید", ar: "انشر" },
    replicasLabel: {
      en: "{n} replica|{n} replicas",
      fa: "{n} رپلیکا|{n} رپلیکا",
      ar: "{n} نسخة|{n} نسخ",
    },
  },
  locations: {
    iran: { en: "Iran", fa: "ایران", ar: "إيران" },
    standardserver: {
      en: "Standard Server",
      fa: "سرور استاندارد",
      ar: "خادم قياسي",
    },
    premiumserver: {
      en: "Premium Server",
      fa: "سرور پرمیوم",
      ar: "خادم متميز",
    },
    europe: { en: "Europe", fa: "اروپا", ar: "أوروبا" },
    usAsia: {
      en: "US & Asia",
      fa: "آمریکا و آسیا",
      ar: "الولايات المتحدة وآسيا",
    },
    germany: { en: "Germany", fa: "آلمان", ar: "ألمانيا" },
    finland: { en: "Finland", fa: "فنلاند", ar: "فنلندا" },
    us: { en: "US", fa: "آمریکا", ar: "الولايات المتحدة" },
    singapore: { en: "Singapore", fa: "سنگاپور", ar: "سنغافورة" },
  },
};

/* ───── Privacy ───── */
export const privacy = {
  subtitle: { en: "Legal", fa: "قانونی", ar: "قانوني" },
  heading: {
    en: "Privacy Policy",
    fa: "سیاست حریم خصوصی",
    ar: "سياسة الخصوصية",
  },
  lastUpdated: {
    en: "Last updated:",
    fa: "آخرین به‌روزرسانی:",
    ar: "آخر تحديث:",
  },
  intro: {
    en: "At Lexoya, we take your privacy seriously. This policy describes how we collect, use, store, and protect your personal information.",
    fa: "در Lexoya حریم خصوصی شما برای ما مهم است. این خط‌مشی توضیح می‌دهد چطور اطلاعات شخصی شما را جمع‌آوری، استفاده، ذخیره و محافظت می‌کنیم.",
    ar: "في Lexoya، نأخذ خصوصيتك على محمل الجد. تصف هذه السياسة كيفية جمع واستخدام وتخزين وحماية معلوماتك الشخصية.",
  },
  sections: {
    infoCollect: {
      title: {
        en: "Information We Collect",
        fa: "اطلاعاتی که جمع‌آوری می‌کنیم",
        ar: "المعلومات التي نجمعها",
      },
    },
    infoUse: {
      title: {
        en: "How We Use Your Information",
        fa: "چگونه از اطلاعات شما استفاده می‌کنیم",
        ar: "كيف نستخدم معلوماتك",
      },
    },
    storage: {
      title: {
        en: "Data Storage & Processing",
        fa: "ذخیره و پردازش داده",
        ar: "تخزين البيانات ومعالجتها",
      },
    },
    sharing: {
      title: {
        en: "Data Sharing & Disclosure",
        fa: "اشتراک‌گذاری و افشای داده",
        ar: "مشاركة البيانات والإفصاح",
      },
    },
    rights: {
      title: {
        en: "Your Rights & Choices",
        fa: "حقوق و انتخاب‌های شما",
        ar: "حقوقك وخياراتك",
      },
    },
    cookies: {
      title: {
        en: "Cookies & Tracking",
        fa: "کوکی‌ها و ردیابی",
        ar: "الكوكيز والتتبع",
      },
    },
    security: {
      title: {
        en: "Security Measures",
        fa: "اقدامات امنیتی",
        ar: "التدابير الأمنية",
      },
    },
    changes: {
      title: {
        en: "Changes to This Policy",
        fa: "تغییرات در این سیاست",
        ar: "التغييرات في هذه السياسة",
      },
    },
    contact: { title: { en: "Contact Us", fa: "تماس با ما", ar: "اتصل بنا" } },
  },
};

/* ───── Docs ───── */
export const docs = {
  subtitle: { en: "Documentation", fa: "مستندات", ar: "الوثائق" },
  heading: {
    en: "Lexoya Documentation",
    fa: "مستندات Lexoya",
    ar: "وثائق Lexoya",
  },
  intro: {
    en: "How billing works across Lexoya services. This guide details how resources are billed rather than specific, static prices.",
    fa: "نحوه صورت‌حساب‌گیری در سرویس‌های Lexoya. این راهنما نحوه محاسبه هزینه منابع را توضیح می‌دهد، نه قیمت‌های ثابت.",
    ar: "كيفية عمل الفوترة عبر خدمات Lexoya. يوضح هذا الدليل كيفية فوترة الموارد وليس الأسعار الثابتة.",
  },
  modelLabel: { en: "Billing model", fa: "مدل صورت‌حساب", ar: "نموذج الفوترة" },
  sections: {
    projects: {
      title: {
        en: "Projects (PaaS & QuickApps)",
        fa: "پروژه‌ها (PaaS و QuickApps)",
        ar: "المشاريع (PaaS و QuickApps)",
      },
      model: {
        en: "Pay-As-You-Go (PAYG)",
        fa: "پرداخت به میزان مصرف (PAYG)",
        ar: "الدفع حسب الاستخدام (PAYG)",
      },
    },
    vps: {
      title: {
        en: "Cloud Servers (VPS)",
        fa: "سرورهای ابری (VPS)",
        ar: "الخوادم السحابية (VPS)",
      },
      model: {
        en: "Monthly Subscription",
        fa: "اشتراک ماهانه",
        ar: "اشتراك شهري",
      },
    },
    gpu: {
      title: {
        en: "GPU Servers",
        fa: "سرورهای GPU",
        ar: "خوادم GPU",
      },
      model: {
        en: "Time-Based Reservations (Daily & Monthly)",
        fa: "رزرو زمانی (روزانه و ماهانه)",
        ar: "حجوزات زمنية (يومي وشهري)",
      },
    },
    storage: {
      title: {
        en: "Object Storage (S3)",
        fa: "فضای ذخیره‌سازی آبجکت (S3)",
        ar: "تخزين الكائنات (S3)",
      },
      model: {
        en: "Monthly Subscription",
        fa: "اشتراک ماهانه",
        ar: "اشتراك شهري",
      },
    },
    traffic: {
      title: {
        en: "Network Traffic",
        fa: "ترافیک شبکه",
        ar: "حركة مرور الشبكة",
      },
      model: {
        en: "Hourly Overage",
        fa: "مصرف مازاد ساعتی",
        ar: "الاستهلاك الزائد بالساعة",
      },
    },
    orchestrator: {
      title: {
        en: "Account Balance & Billing Orchestrator",
        fa: "موجودی حساب و ارکستراتور صورت‌حساب",
        ar: "رصيد الحساب ومنسق الفوترة",
      },
      model: {
        en: "Unified Wallet",
        fa: "کیف پول یکپارچه",
        ar: "محفطة موحدة",
      },
    },
  },
};

/* ───── Resource unit prices ───── */
export const resourcePrices = {
  heading: { en: "Resource", fa: "قیمت", ar: "تسعير" },
  highlighted: { en: "unit prices", fa: "واحد منابع", ar: "الوحدات" },
  descriptionLead: { en: "Every resource is billed ", fa: "هزینه هر منبع به‌صورت ", ar: "تُحتسب كل مورد " },
  descriptionHighlight: { en: "hourly", fa: "ساعتی", ar: "بالساعة" },
  descriptionTail: {
    en: ", per unit. No minimums, no hidden fees.",
    fa: " و بر اساس واحد محاسبه می‌شود. بدون حداقل مصرف و بدون هزینه پنهان.",
    ar: " ولكل وحدة. بلا حد أدنى وبلا رسوم خفية.",
  },
  columns: {
    resource: { en: "Resource", fa: "منبع", ar: "المورد" },
    unit: { en: "Unit", fa: "واحد", ar: "الوحدة" },
    hourly: { en: "Hourly", fa: "ساعتی", ar: "بالساعة" },
    monthly: { en: "Monthly", fa: "ماهانه", ar: "شهريًا" },
  },
  iranCluster: { en: "Iran cluster", fa: "کلاستر ایران", ar: "عنقود إيران" },
  foreignCluster: { en: "Germany cluster", fa: "کلاستر آلمان", ar: "عنقود ألمانيا" },
  rows: {
    cpu: { en: "CPU", fa: "پردازنده", ar: "المعالج" },
    ram: { en: "RAM", fa: "حافظه (RAM)", ar: "الذاكرة (RAM)" },
    disk: { en: "Disk (NVMe)", fa: "دیسک (NVMe)", ar: "القرص (NVMe)" },
    traffic: { en: "Traffic", fa: "ترافیک", ar: "الترافيك" },
    s3: { en: "S3 storage", fa: "ذخیره‌سازی S3", ar: "تخزين S3" },
    pipeline: { en: "Pipeline fee", fa: "هزینه پایپ‌لاین", ar: "رسوم خط الأنابيب" },
  },
  units: {
    perCore: { en: "per core", fa: "هر هسته", ar: "لكل نواة" },
    perGB: { en: "per GB", fa: "هر گیگابایت", ar: "لكل جيجابايت" },
    perUnit: { en: "per unit", fa: "هر عدد", ar: "لكل وحدة" },
  },
  currency: { en: "Toman", fa: "تومان", ar: "تومان" },
  variable: { en: "Variable", fa: "متغیر", ar: "متغيّر" },
  monthlyNote: {
    en: "Monthly prices are the hourly rate × 720 hours.",
    fa: "قیمت ماهانه حاصل‌ضرب نرخ ساعتی در ۷۲۰ ساعت است.",
    ar: "الأسعار الشهرية هي السعر بالساعة × 720 ساعة.",
  },
};

/* ───── Cost Calculator ───── */
export const calculatorPage = {
  kicker: {
    en: "Cost Calculator",
    fa: "ماشین حساب هزینه",
    ar: "حاسبة التكلفة",
  },
  titleLead: {
    en: "Estimate your ",
    fa: "هزینه ماهانه ",
    ar: "قدّر تكلفة ",
  },
  titleHighlight: {
    en: "monthly cost",
    fa: "زیرساخت خود را تخمین بزنید",
    ar: "بنيتك السحابية شهريًا",
  },
  description: {
    en: "Add PaaS projects, managed databases, and quick apps. Tune CPU, RAM, storage, and replicas to see your monthly cost update live. Every resource is billed hourly.",
    fa: "پروژه‌های PaaS، دیتابیس‌های مدیریت‌شده و برنامه‌های آماده را اضافه کنید. CPU، RAM، حافظه و رپلیکا را تنظیم کنید تا هزینه ماهانه به‌صورت زنده به‌روز شود. هزینه هر منبع به‌صورت ساعتی محاسبه می‌شود.",
    ar: "أضف مشاريع PaaS وقواعد البيانات المُدارة والتطبيقات السريعة. اضبط المعالج والذاكرة والتخزين والنسخ المكررة لترى التكلفة الشهرية تتحدث مباشرة. تُحتسب كل الموارد بالساعة.",
  },
  iran: { en: "Iran", fa: "ایران", ar: "إيران" },
  foreign: { en: "Germany", fa: "آلمان", ar: "ألمانيا" },
  livePrices: { en: "Live prices", fa: "قیمت‌های لحظه‌ای", ar: "الأسعار المباشرة" },
  fallbackNotice: {
    en: "Standard rates",
    fa: "نرخ‌های استاندارد",
    ar: "الأسعار القياسية",
  },
  pickerTitle: { en: "Add products", fa: "افزودن سرویس", ar: "إضافة الخدمات" },
  tabs: {
    paas: { en: "PaaS projects", fa: "پروژه‌های PaaS", ar: "مشاريع PaaS" },
    database: { en: "Databases", fa: "دیتابیس‌ها", ar: "قواعد البيانات" },
    quickapp: { en: "Quick apps", fa: "برنامه‌های آماده", ar: "التطبيقات السريعة" },
  },
  tabsDesc: {
    paas: {
      en: "Deploy any runtime — React, Node, Laravel, Docker and more.",
      fa: "هر رانتایم را دیپلوی کنید — React، Node، Laravel، Docker و بیشتر.",
      ar: "انشر أي بيئة تشغيل — React وNode وLaravel وDocker وغيرها.",
    },
    database: {
      en: "Managed engines with backups, replication, and failover.",
      fa: "موتورهای مدیریت‌شده با بکاپ، رپلیکا و فِیل‌اور.",
      ar: "محركات مُدارة مع نسخ احتياطي وتكرار وتجاوز الفشل.",
    },
    quickapp: {
      en: "One-click open-source apps, pre-configured and maintained by us.",
      fa: "برنامه‌های متن‌باز با یک کلیک، آماده و نگهداری‌شده توسط ما.",
      ar: "تطبيقات مفتوحة المصدر بنقرة واحدة، جاهزة ومُدارة من قبلنا.",
    },
  },
  add: { en: "Add", fa: "افزودن", ar: "إضافة" },
  remove: { en: "Remove", fa: "حذف", ar: "إزالة" },
  itemsTitle: { en: "Your configuration", fa: "پیکربندی شما", ar: "إعدادك" },
  itemsEmptyTitle: {
    en: "No products added yet",
    fa: "هنوز سرویسی اضافه نشده است",
    ar: "لم تُضف أي خدمات بعد",
  },
  itemsEmptyDesc: {
    en: "Pick a product above to start estimating your monthly cost.",
    fa: "یک سرویس از بالا انتخاب کنید تا تخمین هزینه ماهانه شما شروع شود.",
    ar: "اختر خدمة من الأعلى لبدء تقدير تكلفتك الشهرية.",
  },
  clearAll: { en: "Clear all", fa: "پاک کردن همه", ar: "مسح الكل" },
  summaryTitle: { en: "Cost estimate", fa: "تخمین هزینه", ar: "تقدير التكلفة" },
  summaryEmptyTitle: {
    en: "Your estimate is empty",
    fa: "تخمین شما خالی است",
    ar: "تقديرك فارغ",
  },
  summaryEmptyDesc: {
    en: "Add a product to see the cost breakdown and total here.",
    fa: "یک سرویس اضافه کنید تا تفکیک هزینه و مجموع اینجا نمایش داده شود.",
    ar: "أضف خدمة لعرض تفصيل التكلفة والمجموع هنا.",
  },
  fields: {
    cpu: { en: "CPU cores", fa: "هسته‌های CPU", ar: "أنوية المعالج" },
    ram: { en: "RAM (GB)", fa: "RAM (GB)", ar: "RAM (GB)" },
    storage: { en: "Storage (GB)", fa: "حافظه (GB)", ar: "التخزين (GB)" },
    replicas: { en: "Replicas", fa: "رپلیکاها", ar: "النسخ المكررة" },
  },
  rows: {
    cpu: { en: "CPU", fa: "پردازنده", ar: "المعالج" },
    ram: { en: "RAM", fa: "حافظه (RAM)", ar: "الذاكرة (RAM)" },
    storage: { en: "Storage (NVMe)", fa: "حافظه (NVMe)", ar: "التخزين (NVMe)" },
  },
  totalLabel: { en: "Estimated total", fa: "تخمین مجموع", ar: "المجموع المقدر" },
  monthly: { en: "/mo", fa: "/ماه", ar: "/شهر" },
  currency: { en: "Toman", fa: "تومان", ar: "تومان" },
  billedNote: {
    en: "Monthly prices are the hourly rate × 720 hours. Final billing follows actual usage.",
    fa: "قیمت ماهانه حاصل‌ضرب نرخ ساعتی در ۷۲۰ ساعت است. صورت‌حساب نهایی بر اساس مصرف واقعی محاسبه می‌شود.",
    ar: "الأسعار الشهرية هي السعر بالساعة × 720 ساعة. تُحتسب الفاتورة النهائية حسب الاستخدام الفعلي.",
  },
  startBuilding: { en: "Start building", fa: "شروع کنید", ar: "ابدأ الآن" },
  contactSales: { en: "Contact sales", fa: "تماس با فروش", ar: "تواصل مع المبيعات" },
  faqHeading: { en: "Cost calculator ", fa: "ماشین حساب هزینه ", ar: "حاسبة التكلفة " },
  faqHighlighted: { en: "FAQ", fa: "سوالات متداول", ar: "الأسئلة الشائعة" },
  faqDescription: {
    en: "Everything you need to know about estimating your Lexoya bill.",
    fa: "هر آنچه برای تخمین صورت‌حساب لکسویا باید بدانید.",
    ar: "كل ما تحتاج معرفته لتقدير فاتورتك في لكسويا.",
  },
  faq: [
    {
      q: {
        en: "How is my estimate calculated?",
        fa: "تخمین هزینه چگونه محاسبه می‌شود؟",
        ar: "كيف يُحتسب تقديري؟",
      },
      a: {
        en: "We multiply the hourly unit price of CPU, RAM, and NVMe storage by 720 hours and by the number of replicas in each product. The total is a monthly estimate, not an invoice.",
        fa: "نرخ ساعتی CPU، RAM و حافظه NVMe را در ۷۲۰ ساعت و در تعداد رپلیکاهای هر سرویس ضرب می‌کنیم. مجموع یک تخمین ماهانه است، نه صورت‌حساب.",
        ar: "نضرب السعر بالساعة للمعالج والذاكرة وتخزين NVMe في 720 ساعة وفي عدد النسخ المكررة لكل خدمة. المجموع تقدير شهري وليس فاتورة.",
      },
    },
    {
      q: {
        en: "Do the Germany prices differ from Iran?",
        fa: "آیا قیمت‌های آلمان با ایران تفاوت دارد؟",
        ar: "هل تختلف أسعار ألمانيا عن إيران؟",
      },
      a: {
        en: "Yes. Foreign clusters apply a price multiplier to the same base hourly rates. Switch the cluster toggle to compare both regions.",
        fa: "بله. کلاسترهای خارجی یک ضریب قیمت روی همان نرخ‌های پایه ساعتی اعمال می‌کنند. با تغییر کلید کلاستر هر دو منطقه را مقایسه کنید.",
        ar: "نعم. تطبّق العناقيد الأجنبية معامل تسعير على نفس الأسعار الأساسية بالساعة. بدّل العنقود لمقارنة المنطقتين.",
      },
    },
    {
      q: {
        en: "Is this estimate binding?",
        fa: "آیا این تخمین قطعی است؟",
        ar: "هل هذا التقدير ملزم؟",
      },
      a: {
        en: "No. It is a close estimate based on current public rates. Actual billing follows your real usage, and traffic or pipeline fees may apply.",
        fa: "خیر. این یک تخمین نزدیک بر اساس نرخ‌های عمومی فعلی است. صورت‌حساب واقعی بر اساس مصرف شما محاسبه می‌شود و ممکن است هزینه ترافیک یا پایپ‌لاین نیز اعمال شود.",
        ar: "لا. إنه تقدير قريب مبني على الأسعار العامة الحالية. تتبع الفاتورة الفعلية استخدامك الحقيقي، وقد تُطبّق رسوم الترافيك أو خط الأنابيب.",
      },
    },
  ],
};

/* ───── Terms ───── */
export const terms = {
  subtitle: { en: "Legal", fa: "قانونی", ar: "قانوني" },
  heading: {
    en: "Terms & Conditions",
    fa: "قوانین و شرایط",
    ar: "الشروط والأحكام",
  },
  lastUpdated: {
    en: "Last updated:",
    fa: "آخرین به‌روزرسانی:",
    ar: "آخر تحديث:",
  },
  intro: {
    en: "These Terms and Conditions govern your use of Lexoya cloud infrastructure services. Please read them carefully before creating an account.",
    fa: "این قوانین و شرایط استفاده از خدمات زیرساخت ابری Lexoya را تعیین می‌کند. لطفاً قبل از ساخت حساب با دقت بخوانید.",
    ar: "تحكم هذه الشروط والأحكام استخدامك لخدمات البنية التحتية السحابية لـ Lexoya. يرجى قراءتها بعناية قبل إنشاء حساب.",
  },
  sections: {
    acceptance: {
      title: {
        en: "Acceptance of Terms",
        fa: "پذیرش شرایط",
        ar: "قبول الشروط",
      },
    },
    service: {
      title: {
        en: "Service Description",
        fa: "توضیحات خدمات",
        ar: "وصف الخدمات",
      },
    },
    account: {
      title: {
        en: "Account Registration & Security",
        fa: "ثبت حساب و امنیت",
        ar: "تسجيل الحساب والأمان",
      },
    },
    acceptableUse: {
      title: {
        en: "Acceptable Use",
        fa: "استفاده مجاز",
        ar: "الاستخدام المقبول",
      },
    },
    billing: {
      title: {
        en: "Payment & Billing",
        fa: "پرداخت و صورتحساب",
        ar: "الدفع والفواتير",
      },
    },
    sla: {
      title: {
        en: "Service Level Agreement (SLA)",
        fa: "توافقنامه سطح خدمات (SLA)",
        ar: "اتفاقية مستوى الخدمة (SLA)",
      },
    },
    dataOwnership: {
      title: {
        en: "Data Ownership & Retention",
        fa: "مالکیت و نگهداری داده",
        ar: "ملكية البيانات والاحتفاظ",
      },
    },
    liability: {
      title: {
        en: "Limitation of Liability",
        fa: "محدودیت مسئولیت",
        ar: "حدود المسؤولية",
      },
    },
    termination: { title: { en: "Termination", fa: "فسخ", ar: "الإنهاء" } },
    governingLaw: {
      title: { en: "Governing Law", fa: "قانون حاکم", ar: "القانون الحاكم" },
    },
    changes: {
      title: {
        en: "Changes to Terms",
        fa: "تغییرات در شرایط",
        ar: "التغييرات في الشروط",
      },
    },
  },
};

/* ───── Product Landing: VPS ───── */
export const productVPS = {
  title: {
    en: "Powerful cloud servers. No compromises.",
    fa: "سرورهای ابری قدرتمند",
    ar: "خوادم سحابية قوية. دون مساومة.",
  },
  subtitle: {
    en: "Cloud Servers (VPS)",
    fa: "سرورهای ابری (VPS)",
    ar: "خوادم سحابية (VPS)",
  },
  description: {
    en: "High-performance virtual private servers with dedicated resources, full root access, and instant scalability. Deploy in seconds with SSD NVMe storage and your choice of Linux distributions — in Iran, Europe, or the US.",
    fa: "سرورهای مجازی با عملکرد بالا با منابع اختصاصی، دسترسی روت کامل و مقیاس‌پذیری سریع. در چند ثانیه با انتخاب سیستم‌عامل، موقعیت (ایران، اروپا یا آمریکا) و پلن منابع مورد نظر سرور را بسازید و از آن استفاده کنید.",
    ar: "خوادم افتراضية خاصة عالية الأداء بموارد مخصصة، وصول كامل للجذر، وقابلية توسع فورية. انشر في ثوانٍ مع تخزين SSD NVMe ومجموعة مختارة من توزيعات لينكس — في إيران أو أوروبا أو الولايات المتحدة.",
  },
  features: [
    {
      title: {
        en: "Dedicated Resources",
        fa: "منابع اختصاصی",
        ar: "موارد مخصصة",
      },
      description: {
        en: "Full CPU, RAM, and SSD NVMe storage dedicated to your server. No overselling, no noisy neighbors — consistent performance every time.",
        fa: "CPU، RAM و حافظه SSD NVMe کامل اختصاصی به سرور شما. بدون فروش بیش از حد، بدون همسایه‌های پر سر و صدا — عملکرد ثابت در هر بار.",
        ar: "CPU و RAM وتخزين SSD NVMe كامل مخصص لخادمك. لا بيع زائد، لا جيران مزعجين — أداء ثابت في كل مرة.",
      },
    },
    {
      title: { en: "Instant Scaling", fa: "مقیاس‌پذیری فوری", ar: "توسع فوري" },
      description: {
        en: "Scale CPU, RAM, and storage up or down in real time with zero downtime. Pay only for what you use, when you use it.",
        fa: "مقیاس CPU، RAM و حافظه را در زمان واقعی با صفر downtime افزایش یا کاهش دهید. فقط برای آنچه استفاده می‌کنید پرداخت کنید.",
        ar: "وسّع أو قلّص CPU و RAM والتخزين في الوقت الفعلي بدون توقف. ادفع فقط مقابل ما تستخدمه عندما تستخدمه.",
      },
    },
    {
      title: {
        en: "Unlimited Bandwidth",
        fa: "پهنای باند نامحدود",
        ar: "نطاق ترددي غير محدود",
      },
      description: {
        en: "All international servers come with unlimited bandwidth. Local servers include generous traffic allowances with affordable overage rates.",
        fa: "همه سرورهای بین‌المللی با پهنای باند نامحدود ارائه می‌شوند. سرورهای داخلی شامل ترافیک سخاوتمندانه با نرخ‌های اضافه مقرون به صرفه هستند.",
        ar: "جميع الخوادم الدولية تأتي بنطاق ترددي غير محدود. تشمل الخوادم المحلية حصص حركة مرور سخية بأسعار إضافية معقولة.",
      },
    },
    {
      title: {
        en: "Multiple Locations",
        fa: "مکان‌های متعدد",
        ar: "مواقع متعددة",
      },
      description: {
        en: "Deploy in Iran, Europe, or the US. Choose the region closest to your users for the lowest latency and fastest load times.",
        fa: "در ایران، اروپا یا آمریکا دیپلوی کنید. منطقه نزدیک‌تر به کاربران خود را برای کمترین تأخیر و سریع‌ترین زمان بارگذاری انتخاب کنید.",
        ar: "انشر في إيران أو أوروبا أو الولايات المتحدة. اختر المنطقة الأقرب لمستخدميك لأقل زمن وصول وأسرع أوقات تحميل.",
      },
    },
    {
      title: {
        en: "Clean & Tested IPs",
        fa: "IPهای تمیز و تست شده",
        ar: "عناوين IP نظيفة ومختبرة",
      },
      description: {
        en: "Every server gets a clean, tested IP address. No blacklisted ranges, no delivery issues — your reputation starts with clean infrastructure.",
        fa: "هر سرور یک آدرس IP تمیز و تست شده دریافت می‌کند. بدون محدوده‌های لیست سیاه، بدون مشکلات تحویل — اعتبار شما با زیرساخت تمیز شروع می‌شود.",
        ar: "كل خادم يحصل على عنوان IP نظيف ومختبر. لا نطاقات محظورة، لا مشاكل في التسليم — سمعتك تبدأ ببنية تحتية نظيفة.",
      },
    },
    {
      title: {
        en: "Monitoring Dashboard",
        fa: "داشبورد مانیتورینگ",
        ar: "لوحة مراقبة",
      },
      description: {
        en: "Real-time visibility into CPU, RAM, disk, and network usage from your control panel. Track performance trends and plan capacity with confidence.",
        fa: "نمایش واقعی مصرف CPU، RAM، دیسک و شبکه از پنل کنترل خود. روندهای عملکرد را ردیابی و ظرفیت را با اطمینان برنامه‌ریزی کنید.",
        ar: "رؤية فورية لاستخدام CPU و RAM والقرص والشبكة من لوحة التحكم الخاصة بك. تتبع اتجاهات الأداء وخطط للسعة بثقة.",
      },
    },
    {
      title: {
        en: "ARM & x86 Architectures",
        fa: "معماری ARM و x86",
        ar: "معماريات ARM و x86",
      },
      description: {
        en: "Choose between powerful x86 processors for general workloads or energy-efficient ARM for specialized applications. Both with SSD NVMe storage.",
        fa: "بین پردازنده‌های قدرتمند x86 برای کارهای عمومی یا ARM برای برنامه‌های تخصصی انتخاب کنید. هر دو با حافظه‌های پر سرعت SSD/NVMe.",
        ar: "اختر بين معالجات x86 القوية للأعباء العامة أو ARM الموفرة للطاقة للتطبيقات المتخصصة. كلاهما مع تخزين SSD NVMe.",
      },
    },
    {
      title: {
        en: "24/7 Expert Support",
        fa: "پشتیبانی ۲۴/۷",
        ar: "دعم خبير على مدار الساعة",
      },
      description: {
        en: "Reach our engineering team anytime via Telegram, Eita, phone, or email. No bots, no tickets — real engineers who solve real problems.",
        fa: "هر زمان از طریق تلگرام، بله یا تلفن با تیم مهندسی ما در تماس باشید. بدون ربات، بدون تیکت — مهندسان واقعی که مشکلات واقعی را حل می‌کنند.",
        ar: "تواصل مع فريقنا الهندسي في أي وقت عبر Telegram أو Eita أو الهاتف أو البريد الإلكتروني. لا روبوتات، لا تذاكر — مهندسون حقيقيون يحلون مشاكل حقيقية.",
      },
    },
  ],
  benefits: [
    {
      title: {
        en: "Monthly Billing",
        fa: "صورتحساب ماهانه",
        ar: "فوترة شهرية",
      },
      desc: {
        en: "Pay month-to-month with no long-term contracts. Transparent pricing with no hidden fees.",
        fa: "ماه به ماه بدون قراردادهای بلندمدت پرداخت کنید. قیمت‌گذاری شفاف بدون هزینه‌های پنهان.",
        ar: "ادفع شهريًا بدون عقود طويلة الأجل. تسعير شفاف بدون رسوم مخفية.",
      },
    },
    {
      title: { en: "Instant Provisioning", fa: "تحویل فوری", ar: "تجهيز فوري" },
      desc: {
        en: "Your server is ready in under 60 seconds. Deploy and connect immediately.",
        fa: "در هر ساعت از شبانه‌روز بعد از سفارش، سرور شما در کمتر از ۹۰ ثانیه آماده شده و تحویل شما می‌شوند.",
        ar: "خادمك جاهز في أقل من 60 ثانية. انشر وتصل فورًا.",
      },
    },
    {
      title: {
        en: "Resource Flexibility",
        fa: "انعطاف‌پذیری منابع",
        ar: "مرونة الموارد",
      },
      desc: {
        en: "Change your plan anytime. Scale resources up or down without migrating.",
        fa: "هر زمان می‌توانید پلن خود را تغییر دهید و چند ثانیه بعد سرور به منابع جدید ارتقاء می‌یابد.",
        ar: "غيّر خطتك في أي وقت. وسّع أو قلّص الموارد دون ترحيل.",
      },
    },
    {
      title: {
        en: "99.99% Uptime SLA",
        fa: "SLA پایداری ۹۹.۹۹٪",
        ar: "SLA وقت تشغيل 99.99%",
      },
      desc: {
        en: "Enterprise-grade infrastructure backed by a strong service level agreement.",
        fa: "متعهد می‌شویم که بالاترین سطح کیفیت و پایداری را به شما ارائه دهیم.",
        ar: "بنية تحتية من الدرجة المؤسسية مدعومة باتفاقية مستوى خدمة قوية.",
      },
    },
  ],
};

/* ───── Product Landing: GPU ───── */
export const productGPU = {
  title: {
    en: "Supercharge your compute with dedicated GPUs.",
    fa: "محاسبات خود را با GPUهای اختصاصی تقویت کنید.",
    ar: "عزّز حوسبتك مع وحدات معالجة رسومية مخصصة.",
  },
  subtitle: { en: "GPU Servers", fa: "سرورهای GPU", ar: "خوادم GPU" },
  description: {
    en: "Dedicated NVIDIA GPU servers for AI training, 3D rendering, video processing, and HPC workloads. Access the latest accelerators with flexible daily or monthly pricing — in Iran and international locations.",
    fa: "سرورهای GPU اختصاصی NVIDIA برای آموزش هوش مصنوعی، رندر سه‌بعدی، پردازش ویدئو و بارهای کاری HPC. با قیمت‌گذاری انعطاف‌پذیر روزانه یا ماهانه — در ایران و مکان‌های بین‌المللی.",
    ar: "خوادم GPU مخصصة من NVIDIA لتدريب AI والرندر ثلاثي الأبعاد ومعالجة الفيديو وأعباء HPC. احصل على أحدث المسرعات بتسعير مرن باليوم أو الشهر — في إيران ومواقع دولية.",
  },
  features: [
    {
      title: {
        en: "Dedicated GPU Resources",
        fa: "منابع GPU اختصاصی",
        ar: "موارد GPU مخصصة",
      },
      description: {
        en: "Full GPU, CPU, RAM, and storage dedicated to your workload. No sharing, no contention — consistent performance for the most demanding tasks.",
        fa: "GPU، CPU، RAM و حافظه کامل اختصاصی به بار کاری شما. بدون اشتراک، بدون رقابت — عملکرد ثابت برای سخت‌ترین وظایف.",
        ar: "GPU و CPU و RAM وتخزين كامل مخصص لعبء عملك. لا مشاركة، لا تنافس — أداء ثابت لأكثر المهام تطلبًا.",
      },
    },
    {
      title: {
        en: "NVIDIA GPUs",
        fa: "GPUهای NVIDIA",
        ar: "وحدات معالجة رسومية NVIDIA",
      },
      description: {
        en: "Access the latest NVIDIA accelerators including A100, H100, L40S, and RTX 4090. High-bandwidth interconnects for multi-GPU training.",
        fa: "به آخرین شتاب‌دهنده‌های NVIDIA شامل A100، H100، L40S و RTX 4090 دسترسی داشته باشید. اتصالات با پهنای باند بالا برای آموزش چند GPU.",
        ar: "احصل على أحدث مسرعات NVIDIA بما في ذلك A100 و H100 و L40S و RTX 4090. وصلات عالية النطاق الترددي لتدريب متعدد وحدات GPU.",
      },
    },
    {
      title: {
        en: "Daily & Monthly Plans",
        fa: "پلن‌های روزانه و ماهانه",
        ar: "خطط يومية وشهرية",
      },
      description: {
        en: "Reserve high-performance GPU instances for specific durations. Pay upfront for daily or monthly use, combining both server and GPU costs.",
        fa: "نمونه‌های GPU با عملکرد بالا را برای مدت‌های مشخص رزرو کنید. برای استفاده روزانه یا ماهانه از پیش پرداخت کنید، که شامل هزینه‌های سرور و GPU می‌شود.",
        ar: "احجز مثيلات GPU عالية الأداء لفترات محددة. ادفع مقدمًا للاستخدام اليومي أو الشهري، يجمع بين تكاليف الخادم و GPU.",
      },
    },
    {
      title: {
        en: "Test Before You Buy",
        fa: "قبل از خرید تست کنید",
        ar: "اختبر قبل الشراء",
      },
      description: {
        en: "Test your GPU server before finalizing the purchase. Verify performance, compatibility, and speed — only commit when you are satisfied.",
        fa: "سرور GPU خود را قبل از نهایی‌سازی خرید تست کنید. عملکرد، سازگاری و سرعت را تأیید کنید — فقط زمانی که راضی هستید متعهد شوید.",
        ar: "اختبر خادم GPU قبل إتمام الشراء. تحقق من الأداء والتوافق والسرعة — التزم فقط عندما تكون راضيًا.",
      },
    },
    {
      title: {
        en: "Software Installation",
        fa: "نصب نرم‌افزار",
        ar: "تثبيت البرامج",
      },
      description: {
        en: "We install your required tools and frameworks — CUDA, PyTorch, TensorFlow, Blender, or any custom software. Ready to use out of the box.",
        fa: "ما ابزارها و چارچوب‌های مورد نیاز شما را نصب می‌کنیم — CUDA، PyTorch، TensorFlow، Blender یا هر نرم‌افزار سفارشی. آماده استفاده.",
        ar: "نقوم بتثبيت الأدوات والأطر المطلوبة — CUDA و PyTorch و TensorFlow و Blender أو أي برنامج مخصص. جاهز للاستخدام فورًا.",
      },
    },
    {
      title: {
        en: "Linux & Windows Support",
        fa: "پشتیبانی لینوکس و ویندوز",
        ar: "دعم لينكس وويندوز",
      },
      description: {
        en: "Choose between Ubuntu, Debian, CentOS, Windows Server, and more. Full administrative access with RDP or SSH.",
        fa: "بین Ubuntu، Debian، CentOS، Windows Server و موارد دیگر انتخاب کنید. دسترسی مدیریت کامل با RDP یا SSH.",
        ar: "اختر بين Ubuntu و Debian و CentOS و Windows Server والمزيد. وصول إداري كامل عبر RDP أو SSH.",
      },
    },
    {
      title: {
        en: "Multi-GPU Clusters",
        fa: "خوشه‌های چند GPU",
        ar: "مجموعات متعددة GPU",
      },
      description: {
        en: "Scale to multi-node GPU clusters for distributed training. High-speed interconnects between nodes for seamless parallel computing.",
        fa: "به خوشه‌های GPU چند گره‌ای برای آموزش توزیع‌شده مقیاس کنید. اتصالات پرسرعت بین گره‌ها برای محاسبات موازی یکپارچه.",
        ar: "وسّع إلى مجموعات GPU متعددة العقد للتدريب الموزع. وصلات عالية السرعة بين العقد لحوسبة متوازية سلسة.",
      },
    },
    {
      title: {
        en: "Iran & International",
        fa: "ایران و بین‌المللی",
        ar: "إيران ودولي",
      },
      description: {
        en: "Deploy GPU servers in Iran or international locations. Choose based on your latency requirements and data residency needs.",
        fa: "سرورهای GPU را در ایران یا مکان‌های بین‌المللی دیپلوی کنید. بر اساس نیازهای تأخیر و الزامات اقامت داده انتخاب کنید.",
        ar: "انشر خوادم GPU في إيران أو مواقع دولية. اختر بناءً على متطلبات زمن الوصول واحتياجات إقامة البيانات.",
      },
    },
  ],
  benefits: [
    {
      title: {
        en: "Time-Based Reservations",
        fa: "رزروهای مبتنی بر زمان",
        ar: "حجوزات تعتمد على الوقت",
      },
      desc: {
        en: "Reserve your GPU instances for specific durations. Daily or monthly options for predictable workloads.",
        fa: "نمونه‌های GPU خود را برای مدت‌های مشخص رزرو کنید. گزینه‌های روزانه یا ماهانه برای بارهای کاری قابل پیش‌بینی.",
        ar: "احجز مثيلات GPU لفترات محددة. خيارات يومية أو شهرية لأعباء العمل المتوقعة.",
      },
    },
    {
      title: {
        en: "Pre-installed Tools",
        fa: "ابزارهای از پیش نصب شده",
        ar: "أدوات مثبتة مسبقًا",
      },
      desc: {
        en: "CUDA, cuDNN, PyTorch, TensorFlow — we set it up so you can start immediately.",
        fa: "CUDA، cuDNN، PyTorch، TensorFlow — ما آن را تنظیم می‌کنیم تا شما بلافاصله شروع کنید.",
        ar: "CUDA و cuDNN و PyTorch و TensorFlow — نقوم بإعدادها لتبدأ فورًا.",
      },
    },
    {
      title: {
        en: "Dedicated Resources",
        fa: "منابع اختصاصی",
        ar: "موارد مخصصة",
      },
      desc: {
        en: "Your GPU is yours alone. No sharing, no contention, no variability.",
        fa: "GPU شما تنها متعلق به شماست. بدون اشتراک، بدون رقابت، بدون تغییرپذیری.",
        ar: "GPU خاص بك وحدك. لا مشاركة، لا تنافس، لا تباين.",
      },
    },
    {
      title: { en: "Expert Support", fa: "پشتیبانی متخصص", ar: "دعم خبير" },
      desc: {
        en: "Our team helps with GPU setup, driver issues, and performance optimization.",
        fa: "تیم ما در راه‌اندازی GPU، مشکلات درایور و بهینه‌سازی عملکرد کمک می‌کند.",
        ar: "يساعد فريقنا في إعداد GPU ومشاكل التعريفات وتحسين الأداء.",
      },
    },
  ],
};

/* ───── Product Landing: PaaS ───── */
export const productPaaS = {
  title: {
    en: "Push the code. The app goes live.",
    fa: "کد را بفرستید، اپلیکیشن بالا می‌آید",
    ar: "ادفع الكود، ويعمل التطبيق",
  },
  subtitle: {
    en: "Cloud Platform",
    fa: "پلتفرم ابری",
    ar: "منصة سحابية",
  },
  description: {
    en: "No server, no SSL setup, no deploy script to babysit. Connect the repository and every push brings up a fresh release. Scaling and domains are ours; you pay for the resources you pick.",
    fa: "سرور و اسکریپت دیپلوی لازم نیست. ریپازیتوری را وصل می‌کنید و هر بار که کد را می‌فرستید، نسخه‌ی تازه بالا می‌آید. مقیاس‌دهی و دامنه هم دست ماست؛ شما فقط برای منابعی که انتخاب می‌کنید پول می‌دهید.",
    ar: "لا خادم ولا إعداد SSL ولا سكربت نشر تصونه. اربط المستودع، وكل push يرفع إصدارًا جديدًا. التوسع والنطاقات علينا، وتدفع مقابل الموارد التي تختارها.",
  },
  projectTypesHeading: {
    en: "What can you deploy?",
    fa: "چه چیزی می‌توانید دیپلوی کنید؟",
    ar: "ما الذي يمكنك نشره؟",
  },
  projectTypesDesc: {
    en: "From a plain HTML page to Laravel, Django and Docker. If the project has a Dockerfile, anything else runs here too.",
    fa: "از یک صفحه‌ی HTML ساده تا لاراول، جنگو و داکر. اگر پروژه Dockerfile داشته باشد، هر چیز دیگری هم اینجا بالا می‌آید.",
    ar: "من صفحة HTML بسيطة إلى Laravel وDjango وDocker. وإن كان للمشروع Dockerfile فعندنا يعمل أي شيء آخر أيضًا.",
  },
  features: [
    {
      title: {
        en: "Deploy from git",
        fa: "دیپلوی از گیت",
        ar: "النشر من Git",
      },
      description: {
        en: "Connect GitHub, GitLab or Gitea. Every push builds and releases, and each stage has a log you can read.",
        fa: "ریپازیتوری گیت‌هاب، گیت‌لب یا گیتیا را وصل کنید. هر push بیلد و ریلیز می‌شود و لاگ هر مرحله را می‌بینید.",
        ar: "اربط GitHub أو GitLab أو Gitea. كل push يُبنى ويُنشر، ولكل مرحلة سجل تقرأه.",
      },
    },
    {
      title: {
        en: "Free subdomain with SSL",
        fa: "ساب‌دامین رایگان با SSL",
        ar: "نطاق فرعي مجاني مع SSL",
      },
      description: {
        en: "Every project gets an HTTPS address the moment it deploys, so you do not need a domain to start. Attach your own whenever you want.",
        fa: "هر پروژه همان لحظه یک آدرس HTTPS می‌گیرد؛ برای شروع لازم نیست دامنه بخرید. دامنه‌ی اختصاصی را هر وقت خواستید وصل کنید.",
        ar: "يحصل كل مشروع فورًا على عنوان HTTPS، فلا تحتاج نطاقًا للبدء. ويمكنك ربط نطاقك متى شئت.",
      },
    },
    {
      title: { en: "Scaling", fa: "مقیاس‌دهی", ar: "التوسع" },
      description: {
        en: "Raise the replica count and traffic spreads across instances. Add resources for a spike, then take them back once traffic drops.",
        fa: "تعداد نسخه‌ها را بالا ببرید تا ترافیک بین آن‌ها پخش شود. برای ترافیک سنگین منابع اضافه کنید و وقتی ترافیک خوابید دوباره کم کنید.",
        ar: "ارفع عدد النسخ ليتوزع الحمل عليها، وأضف موارد وقت الذروة ثم قلّصها بعد أن تهدأ الحركة.",
      },
    },
    {
      title: {
        en: "Docker or a ready runtime",
        fa: "داکر یا رانتایم آماده",
        ar: "Docker أو بيئة جاهزة",
      },
      description: {
        en: "Hand over a Dockerfile, or let the framework template handle it. Background workers run as their own project.",
        fa: "اگر Dockerfile دارید همان را بدهید؛ وگرنه قالب آماده‌ی فریم‌ورک کار را انجام می‌دهد. ورکر هم به‌صورت پروژه‌ی جدا اجرا می‌شود.",
        ar: "سلّم Dockerfile، أو اترك قالب الإطار يتولى ذلك. ويعمل العامل الخلفي كمشروع منفصل.",
      },
    },
    {
      title: { en: "High availability", fa: "دسترس‌پذیری بالا", ar: "توفر عالٍ" },
      description: {
        en: "Keep several instances running. If one drops, traffic moves to the healthy ones without you stepping in.",
        fa: "چند نسخه از اپلیکیشن بالا می‌ماند. اگر یکی از کار بیفتد، ترافیک بدون دخالت شما به نسخه‌های سالم می‌رود.",
        ar: "أبقِ عدة نسخ تعمل. وإن سقطت واحدة انتقل الحمل إلى السليمة دون تدخل منك.",
      },
    },
    {
      title: { en: "Germany cluster", fa: "خوشه‌ی آلمان", ar: "مجموعة ألمانيا" },
      description: {
        en: "For projects that depend on services and APIs outside Iran, run them in the Germany cluster.",
        fa: "برای پروژه‌هایی که به سرویس‌ها و APIهای خارج از ایران وابسته‌اند، پروژه را در خوشه‌ی آلمان اجرا کنید.",
        ar: "للمشاريع المرتبطة بخدمات وواجهات خارج إيران، شغّلها في مجموعة ألمانيا.",
      },
    },
    {
      title: {
        en: "Resource monitoring",
        fa: "مانیتورینگ منابع",
        ar: "مراقبة الموارد",
      },
      description: {
        en: "Watch CPU, memory, disk and network live, so you add capacity before users feel the slowdown.",
        fa: "مصرف CPU، رم، دیسک و شبکه را زنده ببینید تا قبل از اینکه کاربر کندی را حس کند، منابع را اضافه کنید.",
        ar: "راقب المعالج والذاكرة والقرص والشبكة مباشرة، لتضيف الموارد قبل أن يشعر المستخدم بالبطء.",
      },
    },
    {
      title: {
        en: "Hourly billing",
        fa: "پرداخت ساعتی",
        ar: "فوترة ساعية",
      },
      description: {
        en: "You pay for the resources you allocate to the project, by the hour. Shrink a quiet project and the bill follows.",
        fa: "فقط برای منابعی که برای پروژه تعیین کرده‌اید پول می‌دهید، ساعتی. پروژه‌ی کم‌ترافیک را کوچک‌تر کنید تا هزینه هم پایین بیاید.",
        ar: "تدفع مقابل الموارد التي تحددها للمشروع، بالساعة. صغّر مشروعًا هادئًا فينخفض الفاتورة تبعًا.",
      },
    },
  ],
  benefits: [
    {
      title: {
        en: "Release by push",
        fa: "ریلیز با push",
        ar: "إصدار بضغطة push",
      },
      desc: {
        en: "No manual upload, no deploy script to maintain.",
        fa: "بدون آپلود دستی و بدون اسکریپت دیپلوی که خودتان نگه دارید.",
        ar: "بلا رفع يدوي وبلا سكربت نشر تصونه بنفسك.",
      },
    },
    {
      title: {
        en: "SSL and domain",
        fa: "SSL و دامنه",
        ar: "SSL والنطاق",
      },
      desc: {
        en: "A free HTTPS subdomain, and your own domain with one DNS record.",
        fa: "ساب‌دامین رایگان با HTTPS، و دامنه‌ی اختصاصی با یک رکورد DNS.",
        ar: "نطاق فرعي مجاني بـHTTPS، ونطاقك الخاص بسجل DNS واحد.",
      },
    },
    {
      title: { en: "Iran or Germany", fa: "ایران یا آلمان", ar: "إيران أو ألمانيا" },
      desc: {
        en: "Pick the cluster that fits your audience and your dependencies.",
        fa: "خوشه را بر اساس مخاطب و وابستگی‌های پروژه انتخاب کنید.",
        ar: "اختر المجموعة حسب جمهورك وتبعيات مشروعك.",
      },
    },
    {
      title: {
        en: "Engineer support",
        fa: "پشتیبانی مهندسی",
        ar: "دعم هندسي",
      },
      desc: {
        en: "Telegram, Eita, phone and email. A person answers, not a bot.",
        fa: "تلگرام، ایتا، تلفن و ایمیل. جواب را از یک آدم می‌گیرید، نه ربات.",
        ar: "Telegram وEita والهاتف والبريد. يجيبك إنسان لا روبوت.",
      },
    },
  ],
};

/* ───── Product Landing: Database ───── */
export const productDatabase = {
  title: {
    en: "Managed databases. Zero operations.",
    fa: "دیتابیس مدیریت‌شده. بدون عملیات.",
    ar: "قواعد بيانات مُدارة. بدون عمليات.",
  },
  subtitle: {
    en: "Managed Database (DBaaS)",
    fa: "دیتابیس مدیریت‌شده (DBaaS)",
    ar: "قاعدة بيانات مُدارة (DBaaS)",
  },
  description: {
    en: "High-availability database clusters with automated backups, replication, and monitoring. Launch PostgreSQL, MySQL, MariaDB, or Redis in seconds — fully managed and always online.",
    fa: "خوشه‌های دیتابیس با دسترسی بالا با پشتیبان‌گیری خودکار، تکرار و مانیتورینگ. PostgreSQL، MySQL، MariaDB یا Redis را در چند ثانیه راه‌اندازی کنید — کاملاً مدیریت‌شده و همیشه آنلاین.",
    ar: "مجموعات قواعد بيانات عالية التوفر بنسخ احتياطية تلقائية وتكرار ومراقبة. أطلق PostgreSQL أو MySQL أو MariaDB أو Redis في ثوانٍ — مُدارة بالكامل ومتصلة دائمًا.",
  },
  heading: {
    en: "Supported Database Engines",
    fa: "موتورهای دیتابیس پشتیبانی شده",
    ar: "محركات قواعد البيانات المدعومة",
  },
  subheading: {
    en: "Choose from a wide range of database engines — all fully managed, optimized, and ready to deploy.",
    fa: "از طیف وسیعی از موتورهای دیتابیس انتخاب کنید — همه کاملاً مدیریت‌شده، بهینه و آماده دیپلوی.",
    ar: "اختر من مجموعة واسعة من محركات قواعد البيانات — جميعها مُدارة بالكامل ومحسّنة وجاهزة للنشر.",
  },
  features: [
    {
      title: {
        en: "One-Click Setup",
        fa: "راه‌اندازی با یک کلیک",
        ar: "إعداد بنقرة واحدة",
      },
      description: {
        en: "Deploy a production-ready database cluster with a single click. Choose your engine, set your resources, and connect — no manual configuration.",
        fa: "یک خوشه دیتابیس آماده تولید را با یک کلیک دیپلوی کنید. موتور خود را انتخاب کنید، منابع خود را تنظیم کنید و متصل شوید — بدون پیکربندی دستی.",
        ar: "انشر مجموعة قواعد بيانات جاهزة للإنتاج بنقرة واحدة. اختر محركك، حدد مواردك، وتصل — بدون تكوين يدوي.",
      },
    },
    {
      title: {
        en: "High Availability (HA)",
        fa: "دسترسی بالا (HA)",
        ar: "توفر عالي (HA)",
      },
      description: {
        en: "Automatic failover, read replicas, and multi-zone deployment. Your database stays online even if an entire data center goes down.",
        fa: "failover خودکار، read replicas و دیپلوی چند منطقه‌ای. دیتابیس شما حتی اگر یک مرکز داده کامل از کار بیفتد آنلاین می‌ماند.",
        ar: "تجاوز تلقائي للفشل ونسخ قراءة ونشر متعدد المناطق. قاعدة بياناتك تبقى متصلة حتى إذا تعطل مركز بيانات بأكمله.",
      },
    },
    {
      title: {
        en: "Automated Backups",
        fa: "پشتیبان‌گیری خودکار",
        ar: "نسخ احتياطية تلقائية",
      },
      description: {
        en: "Multi-layer automated backups with customizable schedules. Point-in-time recovery, cross-region snapshots, and one-click restore.",
        fa: "پشتیبان‌گیری خودکار چندلایه با زمان‌بندی قابل تنظیم. بازیابی نقطه‌ای، snapshotهای بین منطقه‌ای و بازیابی با یک کلیک.",
        ar: "نسخ احتياطية تلقائية متعددة الطبقات بجداول زمنية قابلة للتخصيص. استعادة نقطة زمنية ولقطات عبر المناطق واستعادة بنقرة واحدة.",
      },
    },
    {
      title: {
        en: "Multiple Engines",
        fa: "موتورهای متعدد",
        ar: "محركات متعددة",
      },
      description: {
        en: "Support for PostgreSQL, MySQL, MariaDB, and Redis. Choose the right database for your workload — all managed, all optimized.",
        fa: "پشتیبانی از PostgreSQL، MySQL، MariaDB و Redis. دیتابیس مناسب برای بار کاری خود را انتخاب کنید — همه مدیریت‌شده، همه بهینه.",
        ar: "دعم PostgreSQL و MySQL و MariaDB و Redis. اختر قاعدة البيانات المناسبة لعبء عملك — جميعها مُدارة، جميعها محسّنة.",
      },
    },
    {
      title: {
        en: "Vertical Scaling",
        fa: "مقیاس‌دهی عمودی",
        ar: "توسع عمودي",
      },
      description: {
        en: "Increase or decrease CPU, RAM, and storage in real time. No migration, no downtime — just instant resource adjustment.",
        fa: "CPU، RAM و حافظه را در زمان واقعی افزایش یا کاهش دهید. بدون مهاجرت، بدون downtime — فقط تنظیم منابع فوری.",
        ar: "زد أو قلّل CPU و RAM والتخزين في الوقت الفعلي. لا ترحيل، لا توقف — مجرد تعديل فوري للموارد.",
      },
    },
    {
      title: {
        en: "Performance Monitoring",
        fa: "مانیتورینگ عملکرد",
        ar: "مراقبة الأداء",
      },
      description: {
        en: "Track query performance, connection counts, replication lag, and resource usage. Pre-built dashboards for every database engine.",
        fa: "عملکرد query، تعداد اتصالات، تأخیر تکرار و مصرف منابع را ردیابی کنید. داشبوردهای از پیش ساخته شده برای هر موتور دیتابیس.",
        ar: "تتبع أداء الاستعلامات وعدد الاتصالات وتأخير التكرار واستخدام الموارد. لوحات تحكم مبنية مسبقًا لكل محرك قاعدة بيانات.",
      },
    },
    {
      title: {
        en: "Easy Integration",
        fa: "یکپارچه‌سازی آسان",
        ar: "تكامل سهل",
      },
      description: {
        en: "Connect your database to any Lexoya service — VPS, PaaS, or S3 — with pre-configured connection strings and firewall rules.",
        fa: "دیتابیس خود را به هر سرویس Lexoya — VPS، PaaS یا S3 — با رشته‌های اتصال از پیش پیکربندی شده و قوانین فایروال متصل کنید.",
        ar: "اربط قاعدة بياناتك بأي خدمة Lexoya — VPS أو PaaS أو S3 — بسلاسل اتصال مكوّنة مسبقًا وقواعد جدار حماية.",
      },
    },
    {
      title: {
        en: "Pay-as-you-go",
        fa: "پرداخت به‌ازای مصرف",
        ar: "ادفع حسب الاستخدام",
      },
      description: {
        en: "Only pay for the storage and compute your database consumes. Scale down during quiet periods and up during traffic peaks.",
        fa: "فقط برای ذخیره‌سازی و محاسبات مصرفی دیتابیس خود پرداخت کنید. در دوره‌های خلوت کاهش و در اوج ترافیک افزایش دهید.",
        ar: "ادفع فقط مقابل التخزين والحوسبة التي تستهلكها قاعدة بياناتك. قلّل في فترات الهدوء وزد في أوقات ذروة حركة المرور.",
      },
    },
  ],
  benefits: [
    {
      title: {
        en: "One-Click Deploy",
        fa: "دیپلوی با یک کلیک",
        ar: "نشر بنقرة واحدة",
      },
      desc: {
        en: "Launch a production database in under a minute. No manual configuration needed.",
        fa: "یک دیتابیس تولیدی را در کمتر از یک دقیقه راه‌اندازی کنید. بدون نیاز به پیکربندی دستی.",
        ar: "أطلق قاعدة بيانات إنتاجية في أقل من دقيقة. لا حاجة لتكوين يدوي.",
      },
    },
    {
      title: { en: "HA Option", fa: "گزینه HA", ar: "خيار HA" },
      desc: {
        en: "Automatic failover and read replicas for mission-critical workloads.",
        fa: "failover خودکار و read replicas برای بارهای کاری حیاتی.",
        ar: "تجاوز تلقائي للفشل ونسخ قراءة لأعباء العمل الحرجة.",
      },
    },
    {
      title: {
        en: "Auto Backups",
        fa: "پشتیبان‌گیری خودکار",
        ar: "نسخ احتياطية تلقائية",
      },
      desc: {
        en: "Scheduled backups with point-in-time recovery. Your data, always protected.",
        fa: "پشتیبان‌گیری زمان‌بندی شده با بازیابی نقطه‌ای. داده‌های شما، همیشه محافظت شده.",
        ar: "نسخ احتياطية مجدولة مع استعادة نقطة زمنية. بياناتك، محمية دائمًا.",
      },
    },
    {
      title: {
        en: "Pay-as-you-go",
        fa: "پرداخت به‌ازای مصرف",
        ar: "ادفع حسب الاستخدام",
      },
      desc: {
        en: "Only pay for what you use. Scale resources up or down at any time.",
        fa: "فقط برای آنچه استفاده می‌کنید پرداخت کنید. منابع را هر زمان افزایش یا کاهش دهید.",
        ar: "ادفع فقط مقابل ما تستخدمه. وسّع أو قلّص الموارد في أي وقت.",
      },
    },
  ],
};

/* ───── Product Landing: Storage ───── */
export const productStorage = {
  title: {
    en: "Object storage that scales with you.",
    fa: "ذخیره‌سازی شیء که با شما مقیاس می‌شود.",
    ar: "تخزين كائنات يتوسع معك.",
  },
  subtitle: {
    en: "Cloud Storage (S3)",
    fa: "فضای ذخیره‌سازی ابری (S3)",
    ar: "تخزين سحابي (S3)",
  },
  description: {
    en: "Scalable, durable, and secure object storage with a fully S3-compatible API. Store any amount of data — from a few files to petabytes — and access it from anywhere with high performance.",
    fa: "ذخیره‌سازی شیء مقیاس‌پذیر، بادوام و امن با API کاملاً سازگار با S3. هر مقدار داده — از چند فایل تا پتابایت — ذخیره کنید و از هر جا با عملکرد بالا به آن دسترسی داشته باشید.",
    ar: "تخزين كائنات قابل للتوسع ودائم وآمن بواجهة API متوافقة تمامًا مع S3. خزّن أي كمية من البيانات — من بضع ملفات إلى بيتابايت — واصل إليها من أي مكان بأداء عالٍ.",
  },
  features: [
    {
      title: {
        en: "S3-Compatible API",
        fa: "API سازگار با S3",
        ar: "واجهة API متوافقة مع S3",
      },
      description: {
        en: "Fully compatible with the S3 API. Use any S3 SDK, CLI tool, or library — no code changes needed. Seamless integration with your existing workflow.",
        fa: "کاملاً سازگار با API S3. از هر SDK S3، ابزار CLI یا کتابخانه استفاده کنید — بدون نیاز به تغییر کد. یکپارچه‌سازی یکپارچه با گردش کار موجود شما.",
        ar: "متوافقة تمامًا مع API S3. استخدم أي SDK أو أداة CLI أو مكتبة S3 — لا حاجة لتغيير الكود. تكامل سلس مع سير عملك الحالي.",
      },
    },
    {
      title: {
        en: "Unlimited Scaling",
        fa: "مقیاس‌پذیری نامحدود",
        ar: "توسع غير محدود",
      },
      description: {
        en: "Store from a few gigabytes to petabytes. No capacity planning, no manual sharding — simply upload and let the platform handle the rest.",
        fa: "از چند گیگابایت تا پتابایت ذخیره کنید. بدون برنامه‌ریزی ظرفیت، بدون sharding دستی — فقط آپلود کنید و بگذارید پلتفرم بقیه را مدیریت کند.",
        ar: "خزّن من بضع غيغابايت إلى بيتابايت. لا تخطيط للسعة، لا تجزئة يدوية — просто حمّل ودع المنصة تتولى الباقي.",
      },
    },
    {
      title: {
        en: "Enterprise Security",
        fa: "امنیت سازمانی",
        ar: "أمان مؤسسي",
      },
      description: {
        en: "Encryption at rest and in transit. Server-side encryption with customer-managed keys. Access control policies and audit logging for every operation.",
        fa: "رمزگذاری در حالت سکون و در حال انتقال. رمزگذاری سمت سرور با کلیدهای مدیریت شده توسط مشتری. سیاست‌های کنترل دسترسی و ثبت حسابرسی برای هر عملیات.",
        ar: "تشفير في حالة السكون وأثناء النقل. تشفير من جانب الخادم بمفاتيح يديرها العميل. سياسات التحكم في الوصول وتسجيل التدقيق لكل عملية.",
      },
    },
    {
      title: { en: "Global Access", fa: "دسترسی جهانی", ar: "وصول عالمي" },
      description: {
        en: "Access your data from anywhere in the world with low latency. CDN integration for fast content delivery to users across the globe.",
        fa: "از هر جای جهان با تأخیر کم به داده‌های خود دسترسی داشته باشید. یکپارچه‌سازی CDN برای تحویل سریع محتوا به کاربران در سراسر جهان.",
        ar: "اصل إلى بياناتك من أي مكان في العالم بزمن وصول منخفض. تكامل CDN لتوصيل محتوى سريع للمستخدمين حول العالم.",
      },
    },
    {
      title: {
        en: "Lifecycle Management",
        fa: "مدیریت چرخه حیات",
        ar: "إدارة دورة الحياة",
      },
      description: {
        en: "Automate data lifecycle with policies for archiving, retention, and deletion. Transition data to colder storage tiers and expire old data automatically.",
        fa: "چرخه حیات داده را با سیاست‌های بایگانی، نگهداری و حذف خودکار کنید. داده‌ها را به ردیف‌های ذخیره‌سازی سردتر منتقل و داده‌های قدیمی را به طور خودکار منقضی کنید.",
        ar: "أتمتة دورة حياة البيانات بسياسات للأرشفة والاحتفاظ والحذف. انقل البيانات إلى مستويات تخزين أبرد وانتهي صلاحية البيانات القديمة تلقائيًا.",
      },
    },
    {
      title: {
        en: "Tool Integration",
        fa: "یکپارچه‌سازی ابزار",
        ar: "تكامل الأدوات",
      },
      description: {
        en: "Connect with popular tools like FileStash, Cyberduck, and S3-compatible backup software. Manage your storage with the tools you already use.",
        fa: "با ابزارهای محبوب مانند FileStash، Cyberduck و نرم‌افزار پشتیبان‌گیری سازگار با S3 متصل شوید. ذخیره‌سازی خود را با ابزارهایی که قبلاً استفاده می‌کنید مدیریت کنید.",
        ar: "اتصل بالأدوات الشائعة مثل FileStash و Cyberduck وبرامج النسخ الاحتياطي المتوافقة مع S3. أدِر تخزينك بالأدوات التي تستخدمها بالفعل.",
      },
    },
    {
      title: {
        en: "Versioning & Backup",
        fa: "نسخه‌بندی و پشتیبان‌گیری",
        ar: "الإصدارات والنسخ الاحتياطي",
      },
      description: {
        en: "Object versioning protects against accidental deletion. Automated backups with cross-region replication ensure your data is always recoverable.",
        fa: "نسخه‌بندی اشیاء در برابر حذف تصادفی محافظت می‌کند. پشتیبان‌گیری خودکار با تکرار بین منطقه‌ای تضمین می‌کند داده‌های شما همیشه قابل بازیابی هستند.",
        ar: "إصدارات الكائنات تحمي من الحذف العرضي. النسخ الاحتياطية التلقائية مع التكرار عبر المناطق تضمن أن بياناتك قابلة للاسترداد دائمًا.",
      },
    },
    {
      title: {
        en: "Monthly Subscription",
        fa: "اشتراک ماهانه",
        ar: "اشتراك شهري",
      },
      description: {
        en: "Fixed monthly plans according to your allocated storage tier. HDD and SSD options available for different performance needs.",
        fa: "پلن‌های ثابت ماهانه بر اساس سطح ذخیره‌سازی تخصیص یافته شما. گزینه‌های HDD و SSD برای نیازهای عملکرد مختلف.",
        ar: "خطط شهرية ثابتة وفقًا لمستوى التخزين المخصص لك. خيارات HDD و SSD متوفرة لاحتياجات الأداء المختلفة.",
      },
    },
  ],
  benefits: [
    {
      title: { en: "S3-Compatible", fa: "سازگار با S3", ar: "متوافق مع S3" },
      desc: {
        en: "Use any S3 SDK or tool. Zero code changes required.",
        fa: "از هر SDK یا ابزار S3 استفاده کنید. بدون نیاز به تغییر کد.",
        ar: "استخدم أي SDK أو أداة S3. لا حاجة لتغيير الكود.",
      },
    },
    {
      title: {
        en: "Unlimited Capacity",
        fa: "ظرفیت نامحدود",
        ar: "سعة غير محدودة",
      },
      desc: {
        en: "From gigabytes to petabytes. Scale without limits.",
        fa: "از گیگابایت تا پتابایت. بدون محدودیت مقیاس دهید.",
        ar: "من غيغابايت إلى بيتابايت. توسع بدون حدود.",
      },
    },
    {
      title: {
        en: "Enterprise Security",
        fa: "امنیت سازمانی",
        ar: "أمان مؤسسي",
      },
      desc: {
        en: "Encrypted at rest and in transit. Full access control.",
        fa: "رمزگذاری شده در حالت سکون و در حال انتقال. کنترل دسترسی کامل.",
        ar: "مشفر في حالة السكون وأثناء النقل. تحكم وصول كامل.",
      },
    },
    {
      title: {
        en: "Monthly Subscription",
        fa: "اشتراک ماهانه",
        ar: "اشتراك شهري",
      },
      desc: {
        en: "Fixed monthly plans based on tier. HDD and SSD options.",
        fa: "پلن‌های ماهانه ثابت بر اساس سطح. گزینه‌های HDD و SSD.",
        ar: "خطط شهرية ثابتة بناءً على المستوى. خيارات HDD و SSD.",
      },
    },
  ],
};

/* ───── Product Landing: Backup ───── */
export const productBackup = {
  title: {
    en: "Your data, always protected.",
    fa: "داده‌های شما، همیشه محافظت شده.",
    ar: "بياناتك، محمية دائمًا.",
  },
  subtitle: {
    en: "Backup & Disaster Recovery",
    fa: "پشتیبان‌گیری و بازیابی فاجعه",
    ar: "النسخ الاحتياطي والتعافي من الكوارث",
  },
  description: {
    en: "Automated backup and disaster recovery for your critical data and systems. Multi-layer protection, cross-region replication, and instant recovery — so you never lose sleep over data loss.",
    fa: "پشتیبان‌گیری خودکار و بازیابی فاجعه برای داده‌ها و سیستم‌های حیاتی شما. حفاظت چندلایه، تکرار بین منطقه‌ای و بازیابی فوری — تا هرگز نگران از دست رفتن داده نباشید.",
    ar: "نسخ احتياطي تلقائي وتعافي من الكوارث لبياناتك وأنظمتك الحرجة. حماية متعددة الطبقات وتكرار عبر المناطق واسترداد فوري — حتى لا تقلق أبدًا بشأن فقدان البيانات.",
  },
  features: [
    {
      title: {
        en: "Automated Scheduling",
        fa: "زمان‌بندی خودکار",
        ar: "جدولة تلقائية",
      },
      description: {
        en: "Schedule backups by the hour, day, or week. Set retention policies and let the system handle the rest — no manual intervention needed.",
        fa: "پشتیبان‌گیری را بر اساس ساعت، روز یا هفته زمان‌بندی کنید. سیاست‌های نگهداری را تنظیم کنید و بگذارید سیستم بقیه را مدیریت کند — بدون نیاز به دخالت دستی.",
        ar: "جدول النسخ الاحتياطية بالساعة أو اليوم أو الأسبوع. حدد سياسات الاحتفاظ ودع النظام يتولى الباقي — لا حاجة للتدخل اليدوي.",
      },
    },
    {
      title: {
        en: "Cross-Region Replication",
        fa: "تکرار بین منطقه‌ای",
        ar: "تكرار عبر المناطق",
      },
      description: {
        en: "Replicate backups across multiple geographic regions. If one region goes down, your data is safe and recoverable from another location.",
        fa: "پشتیبان‌ها را در چندین منطقه جغرافیایی تکرار کنید. اگر یک منطقه از کار بیفتد، داده‌های شما در مکان دیگری امن و قابل بازیابی هستند.",
        ar: "كرّر النسخ الاحتياطية عبر مناطق جغرافية متعددة. إذا تعطلت منطقة، بياناتك آمنة وقابلة للاسترداد من موقع آخر.",
      },
    },
    {
      title: {
        en: "Immutable Backups",
        fa: "پشتیبان‌های تغییرناپذیر",
        ar: "نسخ احتياطية غير قابلة للتغيير",
      },
      description: {
        en: "Write-once, read-many (WORM) backup storage. Once written, backups cannot be modified or deleted during the retention period — ransomware protection built in.",
        fa: "ذخیره‌سازی پشتیبان یک‌بار نوشتن، چندبار خواندن (WORM). پس از نوشتن، پشتیبان‌ها در طول دوره نگهداری قابل تغییر یا حذف نیستند — محافظت در برابر باج‌افزار داخلی.",
        ar: "تخزين نسخ احتياطية WORM (كتابة مرة، قراءة متعددة). بمجرد الكتابة، لا يمكن تعديل أو حذف النسخ الاحتياطية خلال فترة الاحتفاظ — حماية مدمجة ضد برامج الفدية.",
      },
    },
    {
      title: {
        en: "Point-in-Time Recovery",
        fa: "بازیابی نقطه‌ای",
        ar: "استعادة نقطة زمنية",
      },
      description: {
        en: "Restore your data to any point within the retention window. Recover from accidental deletions, corruption, or malicious activity in seconds.",
        fa: "داده‌های خود را به هر نقطه در پنجره نگهداری بازیابی کنید. از حذف تصادفی، خرابی یا فعالیت مخرب در چند ثانیه بازیابی کنید.",
        ar: "استعد بياناتك إلى أي نقطة ضمن نافذة الاحتفاظ. تعاف من الحذف العرضي أو التلف أو النشاط الضار في ثوانٍ.",
      },
    },
    {
      title: {
        en: "Multi-Layer Backup",
        fa: "پشتیبان‌گیری چندلایه",
        ar: "نسخ احتياطي متعدد الطبقات",
      },
      description: {
        en: "Configure multiple backup targets — local SSD, S3 storage, and cross-region — for defense in depth. Lose one layer, restore from another.",
        fa: "چندین هدف پشتیبان — SSD محلی، ذخیره‌سازی S3 و بین منطقه‌ای — برای دفاع عمیق پیکربندی کنید. یک لایه را از دست بدهید، از دیگری بازیابی کنید.",
        ar: "كوّن أهداف نسخ احتياطي متعددة — SSD محلي وتخزين S3 وعبر المناطق — للدفاع المتعمق. افقد طبقة، استعد من أخرى.",
      },
    },
    {
      title: {
        en: "Compliance Ready",
        fa: "آماده انطباق",
        ar: "جاهز للامتثال",
      },
      description: {
        en: "Meet SOC 2, ISO 27001, and GDPR requirements with automated compliance reports, audit trails, and configurable retention policies.",
        fa: "نیازهای SOC 2، ISO 27001 و GDPR را با گزارش‌های انطباق خودکار، مسیرهای حسابرسی و سیاست‌های نگهداری قابل تنظیم برآورده کنید.",
        ar: "استوفِ متطلبات SOC 2 و ISO 27001 و GDPR بتقارير امتثال تلقائية ومسارات تدقيق وسياسات احتفاظ قابلة للتكوين.",
      },
    },
    {
      title: {
        en: "One-Click Restore",
        fa: "بازیابی با یک کلیک",
        ar: "استعادة بنقرة واحدة",
      },
      description: {
        en: "Restore entire systems or individual files with a single click. No complex recovery procedures — just select the backup and restore.",
        fa: "سیستم‌های کامل یا فایل‌های فردی را با یک کلیک بازیابی کنید. بدون روش‌های بازیابی پیچیده — فقط پشتیبان را انتخاب و بازیابی کنید.",
        ar: "استعد أنظمة كاملة أو ملفات فردية بنقرة واحدة. لا إجراءات استرداد معقدة — просто اختر النسخة الاحتياطية واستعد.",
      },
    },
    {
      title: {
        en: "Integrated with Lexoya",
        fa: "یکپارچه با Lexoya",
        ar: "متكامل مع Lexoya",
      },
      description: {
        en: "Backup your VPS snapshots, database dumps, and S3 buckets — all from the same control panel. One platform, complete data protection.",
        fa: "از snapshotهای VPS، dumpهای دیتابیس و bucketهای S3 خود پشتیبان بگیرید — همه از یک پنل کنترل. یک پلتفرم، حفاظت کامل از داده.",
        ar: "انسخ احتياطيًا لقطات VPS وتفريغات قاعدة بيانات وحاويات S3 — كل ذلك من لوحة التحكم نفسها. منصة واحدة، حماية كاملة للبيانات.",
      },
    },
  ],
  benefits: [
    {
      title: { en: "Fully Automated", fa: "کاملاً خودکار", ar: "آلي بالكامل" },
      desc: {
        en: "Set it and forget it. Automatic scheduling with customizable policies.",
        fa: "تنظیم کنید و فراموش کنید. زمان‌بندی خودکار با سیاست‌های قابل تنظیم.",
        ar: "اضبطه وانسَه. جدولة تلقائية بسياسات قابلة للتخصيص.",
      },
    },
    {
      title: {
        en: "Ransomware Proof",
        fa: "مقاوم در برابر باج‌افزار",
        ar: "مقاوم لبرامج الفدية",
      },
      desc: {
        en: "Immutable backups that cannot be modified or deleted by attackers.",
        fa: "پشتیبان‌های تغییرناپذیر که توسط مهاجمان قابل تغییر یا حذف نیستند.",
        ar: "نسخ احتياطية غير قابلة للتغيير لا يمكن تعديلها أو حذفها من قبل المهاجمين.",
      },
    },
    {
      title: { en: "Multi-Region", fa: "چند منطقه‌ای", ar: "متعدد المناطق" },
      desc: {
        en: "Data replicated across geographic regions for maximum resilience.",
        fa: "داده‌ها در مناطق جغرافیایی برای حداکثر انعطاف‌پذیری تکرار می‌شوند.",
        ar: "بيانات مكررة عبر مناطق جغرافية لأقصى مرونة.",
      },
    },
    {
      title: { en: "Instant Recovery", fa: "بازیابی فوری", ar: "استرداد فوري" },
      desc: {
        en: "Restore entire systems or single files with one click.",
        fa: "سیستم‌های کامل یا فایل‌های تکی را با یک کلیک بازیابی کنید.",
        ar: "استعد أنظمة كاملة أو ملفات فردية بنقرة واحدة.",
      },
    },
  ],
};

/* ───── Product Landing: Security ───── */
export const productSecurity = {
  title: {
    en: "Enterprise security. On-demand.",
    fa: "امنیت سازمانی. بر اساس تقاضا.",
    ar: "أمان مؤسسي. حسب الطلب.",
  },
  subtitle: {
    en: "Security Services",
    fa: "خدمات امنیتی",
    ar: "الخدمات الأمنية",
  },
  description: {
    en: "Protect your infrastructure with enterprise-grade security services — from penetration testing and vulnerability assessment to 24/7 SOC monitoring and incident response.",
    fa: "از زیرساخت خود با خدمات امنیتی درجه سازمانی محافظت کنید — از تست نفوذ و ارزیابی آسیب‌پذیری تا مانیتورینگ SOC ۲۴/۷ و پاسخ به حادثه.",
    ar: "احمِ بنيتك التحتية بخدمات أمان من الدرجة المؤسسية — من اختبار الاختراق وتقييم الثغرات إلى مراقبة SOC على مدار الساعة والاستجابة للحوادث.",
  },
  features: [
    {
      title: {
        en: "Penetration Testing",
        fa: "تست نفوذ",
        ar: "اختبار الاختراق",
      },
      description: {
        en: "Simulate real-world cyber attacks against your infrastructure, applications, and APIs. Our certified ethical hackers uncover vulnerabilities before malicious actors do.",
        fa: "حملات سایبری واقعی را علیه زیرساخت، برنامه‌ها و APIهای خود شبیه‌سازی کنید. هکرهای اخلاقی دارای گواهینامه ما آسیب‌پذیری‌ها را قبل از عوامل مخرب کشف می‌کنند.",
        ar: "حاكِ هجمات إلكترونية واقعية ضد بنيتك التحتية وتطبيقاتك وواجهات API. يكشف قراصنتنا الأخلاقيون المعتمدون الثغرات قبل أن يفعلها الجهات الخبيثة.",
      },
    },
    {
      title: {
        en: "Vulnerability Assessment",
        fa: "ارزیابی آسیب‌پذیری",
        ar: "تقييم الثغرات",
      },
      description: {
        en: "Comprehensive scanning and analysis of your entire attack surface. Identify misconfigurations, missing patches, and weak security controls across your stack.",
        fa: "اسکن و تحلیل جامع از کل سطح حمله شما. پیکربندی‌های نادرست، patchهای از دست رفته و کنترل‌های امنیتی ضعیف را در سراسر پشته خود شناسایی کنید.",
        ar: "مسح وتحليل شامل لسطح الهجوم بأكمله. حدد التكوينات الخاطئة والتصحيحات المفقودة وضوابط الأمان الضعيفة عبر مجموعتك.",
      },
    },
    {
      title: {
        en: "Security Audit & Compliance",
        fa: "حسابرسی امنیتی و انطباق",
        ar: "تدقيق الأمان والامتثال",
      },
      description: {
        en: "Align your infrastructure with ISO 27001, SOC 2, GDPR, and PCI DSS standards. Get a detailed roadmap to compliance with actionable remediation steps.",
        fa: "زیرساخت خود را با استانداردهای ISO 27001، SOC 2، GDPR و PCI DSS هماهنگ کنید. یک نقشه راه دقیق برای انطباق با مراحل اصلاحی عملی دریافت کنید.",
        ar: "وائم بنيتك التحتية مع معايير ISO 27001 و SOC 2 و GDPR و PCI DSS. احصل على خارطة طريق مفصلة للامتثال بخطوات علاجية قابلة للتنفيذ.",
      },
    },
    {
      title: {
        en: "Web Application Firewall (WAF)",
        fa: "فایروال برنامه وب (WAF)",
        ar: "جدار حماية تطبيقات الويب (WAF)",
      },
      description: {
        en: "Deploy intelligent WAF rules that protect your applications from SQL injection, XSS, CSRF, and OWASP Top 10 threats with minimal false positives.",
        fa: "قوانین WAF هوشمندی را دیپلوی کنید که از برنامه‌های شما در برابر SQL injection، XSS، CSRF و تهدیدات OWASP Top 10 با حداقل مثبت کاذب محافظت می‌کند.",
        ar: "انشر قواعد WAF ذكية تحمي تطبيقاتك من SQL injection و XSS و CSRF وتهديدات OWASP Top 10 بأقل عدد من النتائج الإيجابية الخاطئة.",
      },
    },
    {
      title: { en: "DDoS Protection", fa: "حفاظت DDoS", ar: "حماية DDoS" },
      description: {
        en: "Mitigate volumetric and application-layer DDoS attacks with automatic detection and real-time traffic filtering. L3-L7 protection with 99.99% uptime SLA.",
        fa: "حملات DDoS حجمی و لایه برنامه را با تشخیص خودکار و فیلتر ترافیک زمان واقعی کاهش دهید. حفاظت L3-L7 با SLA uptime ۹۹.۹۹٪.",
        ar: "خفّف هجمات DDoS الحجمية وطبقة التطبيق بكشف تلقائي وتصفية حركة مرور في الوقت الفعلي. حماية L3-L7 مع SLA وقت تشغيل 99.99%.",
      },
    },
    {
      title: {
        en: "Incident Response",
        fa: "پاسخ به حادثه",
        ar: "الاستجابة للحوادث",
      },
      description: {
        en: "24/7 incident response team ready to contain, investigate, and remediate security breaches. Minimize damage and restore operations with expert guidance.",
        fa: "تیم پاسخ به حادثه ۲۴/۷ آماده برای مهار، بررسی و رفع نقض‌های امنیتی. با راهنمایی متخصصان خسارت را به حداقل برسانید و عملیات را بازیابی کنید.",
        ar: "فريق استجابة للحوادث على مدار الساعة جاهز لاحتواء وانتهاكات الأمان والتحقيق فيها ومعالجتها. قلّل الضرر واستعد العمليات بتوجيه خبير.",
      },
    },
    {
      title: {
        en: "SOC-as-a-Service",
        fa: "SOC به عنوان سرویس",
        ar: "SOC كخدمة",
      },
      description: {
        en: "Round-the-clock security monitoring by our Security Operations Center. Real-time threat detection, log analysis, and proactive threat hunting.",
        fa: "مانیتورینگ امنیتی شبانه‌روزی توسط مرکز عملیات امنیتی ما. تشخیص تهدید زمان واقعی، تحلیل لاگ و شکار تهدید فعال.",
        ar: "مراقبة أمان على مدار الساعة من مركز عمليات الأمان لدينا. كشف التهديدات في الوقت الفعلي وتحليل السجلات وصيد التهديدات الاستباقي.",
      },
    },
    {
      title: {
        en: "Cloud Security Consulting",
        fa: "مشاوره امنیت ابری",
        ar: "استشارات أمان سحابي",
      },
      description: {
        en: "Architect and implement secure cloud environments on Lexoya or any platform. Zero-trust networks, encryption strategies, and security automation included.",
        fa: "محیط‌های ابری امن را در Lexoya یا هر پلتفرمی طراحی و پیاده‌سازی کنید. شبکه‌های zero-trust، استراتژی‌های رمزگذاری و اتوماسیون امنیتی شامل می‌شود.",
        ar: "صمّم وطبّق بيئات سحابية آمنة على Lexoya أو أي منصة. شبكات الثقة الصفرية واستراتيجيات التشفير وأتمتة الأمان مشمولة.",
      },
    },
  ],
  benefits: [
    {
      title: {
        en: "Certified Experts",
        fa: "متخصصان دارای گواهینامه",
        ar: "خبراء معتمدون",
      },
      desc: {
        en: "OSCP, CISSP, and CEH certified security professionals with years of hands-on experience.",
        fa: "متخصصان امنیتی دارای گواهینامه OSCP، CISSP و CEH با سال‌ها تجربه عملی.",
        ar: "محترفو أمان معتمدون OSCP و CISSP و CEH بسنوات من الخبرة العملية.",
      },
    },
    {
      title: {
        en: "Comprehensive Coverage",
        fa: "پوشش جامع",
        ar: "تغطية شاملة",
      },
      desc: {
        en: "From web apps to cloud infra — we test every layer of your stack.",
        fa: "از برنامه‌های وب تا زیرساخت ابری — ما هر لایه از پشته شما را تست می‌کنیم.",
        ar: "من تطبيقات الويب إلى البنية التحتية السحابية — نختبر كل طبقة من مجموعتك.",
      },
    },
    {
      title: {
        en: "Actionable Reports",
        fa: "گزارش‌های عملی",
        ar: "تقارير قابلة للتنفيذ",
      },
      desc: {
        en: "Detailed findings with clear remediation steps and priority ratings.",
        fa: "یافته‌های دقیق با مراحل اصلاحی واضح و رتبه‌بندی اولویت.",
        ar: "نتائج مفصلة بخطوات علاجية واضحة وتصنيفات أولوية.",
      },
    },
    {
      title: {
        en: "Ongoing Protection",
        fa: "حفاظت مداوم",
        ar: "حماية مستمرة",
      },
      desc: {
        en: "Continuous monitoring and re-testing to keep you safe from evolving threats.",
        fa: "مانیتورینگ مداوم و تست مجدد برای ایمن نگه داشتن شما از تهدیدات در حال تحول.",
        ar: "مراقبة مستمرة وإعادة اختبار لإبقائك آمنًا من التهديدات المتطورة.",
      },
    },
  ],
  penTestKicker: {
    en: "Penetration Testing",
    fa: "تست نفوذ",
    ar: "اختبار الاختراق",
  },
  penTestTitle: { en: "Comprehensive", fa: "جامع", ar: "شامل" },
  penTestHighlight: {
    en: "penetration testing",
    fa: "تست نفوذ",
    ar: "اختبار اختراق",
  },
  penTestDesc: {
    en: "Our certified security experts simulate real-world attacks against your infrastructure, applications, and APIs. We identify vulnerabilities, misconfigurations, and weak points before attackers do — and provide a clear roadmap to fix them.",
    fa: "متخصصان امنیتی دارای گواهینامه ما حملات واقعی را علیه زیرساخت، برنامه‌ها و APIهای شما شبیه‌سازی می‌کنند. ما آسیب‌پذیری‌ها، پیکربندی‌های نادرست و نقاط ضعف را قبل از مهاجمان شناسایی می‌کنیم — و یک نقشه راه واضح برای رفع آنها ارائه می‌دهیم.",
    ar: "يُحاكي خبراؤنا الأمنيون المعتمدون هجمات واقعية ضد بنيتك التحتية وتطبيقاتك وواجهات API. نحدد الثغرات والتكوينات الخاطئة ونقاط الضعف قبل المهاجمين — ونقدم خارطة طريق واضحة لإصلاحها.",
  },
  penTestBullets: [
    {
      en: "Full-scope black-box, grey-box, and white-box testing",
      fa: "تست black-box، grey-box و white-box با دامنه کامل",
      ar: "اختبار الصندوق الأسود والرمادي والأبيض كامل النطاق",
    },
    {
      en: "OWASP Top 10 and WSTG-compliant methodology",
      fa: "OWASP Top 10 و روش‌شناسی منطبق با WSTG",
      ar: "OWASP Top 10 ومنهجية متوافقة مع WSTG",
    },
    {
      en: "Detailed executive and technical reports",
      fa: "گزارش‌های اجرایی و فنی دقیق",
      ar: "تقارير تنفيذية وفنية مفصلة",
    },
    {
      en: "Free re-testing within 30 days of remediation",
      fa: "تست مجدد رایگان ظرف ۳۰ روز پس از اصلاح",
      ar: "إعادة اختبار مجانية خلال 30 يومًا من العلاج",
    },
  ],
  startingFrom: { en: "Starting from", fa: "شروع از", ar: "ابتداءً من" },
  toman: { en: "Toman", fa: "تومان", ar: "تومان" },
  perEngagement: {
    en: "per engagement — includes full report & remediation guidance",
    fa: "به‌ازای هر قرارداد — شامل گزارش کامل و راهنمای اصلاح",
    ar: "لكل تعاقد — يشمل التقرير الكامل وإرشادات العلاج",
  },
  callForHelp: {
    en: "Call for more help",
    fa: "برای کمک بیشتر تماس بگیرید",
    ar: "اتصل لمزيد من المساعدة",
  },
  speakWithTeam: {
    en: "Speak with our security team directly",
    fa: "مستقیماً با تیم امنیتی ما صحبت کنید",
    ar: "تحدث مع فريق الأمان لدينا مباشرة",
  },
  servicesKicker: { en: "Service Offerings", fa: "خدمات", ar: "عروض الخدمات" },
  servicesHeading: { en: "Every angle", fa: "همه زوایا", ar: "كل زاوية" },
  servicesHighlight: { en: "covered", fa: "پوشش داده شده", ar: "مغطاة" },
  servicesDesc: {
    en: "From network perimeter to application logic — our security services leave no stone unturned.",
    fa: "از محیط شبکه تا منطق برنامه — خدمات امنیتی ما هیچ سنگی را بی‌برگ‌نمی‌گذارند.",
    ar: "من محيط الشبكة إلى منطق التطبيق — خدمات الأمان لدينا لا تترك حجرًا دون تقليبه.",
  },
  services: [
    {
      title: {
        en: "External Penetration Testing",
        fa: "تست نفوذ خارجی",
        ar: "اختبار اختراق خارجي",
      },
      desc: {
        en: "Test perimeter defenses, firewalls, VPNs, and public-facing apps from an attacker's perspective.",
        fa: "دفاع محیطی، فایروال‌ها، VPNها و برنامه‌های عمومی را از دیدگاه مهاجم تست کنید.",
        ar: "اختبر دفاعات المحيط وجدران الحماية والشبكات الخاصة الافتراضية والتطبيقات المواجهة للجمهور من منظور المهاجم.",
      },
    },
    {
      title: {
        en: "Internal Penetration Testing",
        fa: "تست نفوذ داخلی",
        ar: "اختبار اختراق داخلي",
      },
      desc: {
        en: "Simulate insider threats and compromised accounts to assess internal network segmentation and access controls.",
        fa: "تهدیدات داخلی و حساب‌های به خطر افتاده را برای ارزیابی تقسیم‌بندی شبکه داخلی و کنترل‌های دسترسی شبیه‌سازی کنید.",
        ar: "حاكِ التهديدات الداخلية والحسابات المخترقة لتقييم تجزئة الشبكة الداخلية وضوابط الوصول.",
      },
    },
    {
      title: {
        en: "Web Application Testing",
        fa: "تست برنامه وب",
        ar: "اختبار تطبيقات الويب",
      },
      desc: {
        en: "Deep-dive testing of web applications including API security, authentication logic, and session management.",
        fa: "تست عمیق برنامه‌های وب شامل امنیت API، منطق احراز هویت و مدیریت جلسه.",
        ar: "اختبار معمق لتطبيقات الويب بما في ذلك أمان API ومنطق المصادقة وإدارة الجلسات.",
      },
    },
    {
      title: {
        en: "Mobile App Security",
        fa: "امنیت برنامه موبایل",
        ar: "أمان تطبيقات الجوال",
      },
      desc: {
        en: "Assess iOS and Android applications for insecure storage, weak cryptography, and runtime manipulation risks.",
        fa: "برنامه‌های iOS و Android را برای ذخیره‌سازی ناایمن، رمزنگاری ضعیف و خطرات دستکاری زمان اجرا ارزیابی کنید.",
        ar: "قيّم تطبيقات iOS و Android للتخزين غير الآمن والتشفير الضعيف ومخاطر التلاعب في وقت التشغيل.",
      },
    },
    {
      title: {
        en: "Cloud Security Review",
        fa: "بررسی امنیت ابری",
        ar: "مراجعة أمان سحابي",
      },
      desc: {
        en: "Audit cloud configurations for IAM misconfigurations, insecure storage, and network exposure.",
        fa: "پیکربندی‌های ابری را برای پیکربندی‌های نادرست IAM، ذخیره‌سازی ناایمن و قرار گرفتن در معرض شبکه حسابرسی کنید.",
        ar: "دقّق تكوينات السحاب بحثًا عن تكوينات IAM الخاطئة والتخزين غير الآمن والتعرض للشبكة.",
      },
    },
    {
      title: {
        en: "Social Engineering",
        fa: "مهندسی اجتماعی",
        ar: "الهندسة الاجتماعية",
      },
      desc: {
        en: "Test your team's security awareness through simulated phishing campaigns and physical social engineering.",
        fa: "آگاهی امنیتی تیم خود را از طریق کمپین‌های فیشینگ شبیه‌سازی شده و مهندسی اجتماعی فیزیکی تست کنید.",
        ar: "اختبر وعي فريقك الأمني عبر حملات تصيد محاكاة وهندسة اجتماعية مادية.",
      },
    },
  ],
  ctaTitle: {
    en: "Need a security assessment?",
    fa: "نیاز به ارزیابی امنیتی دارید؟",
    ar: "هل تحتاج إلى تقييم أمني؟",
  },
  ctaHighlight: {
    en: "We're here to help",
    fa: "ما اینجا هستیم تا کمک کنیم",
    ar: "نحن هنا للمساعدة",
  },
  ctaDesc: {
    en: "Get in touch with our security team for a free initial consultation and risk assessment.",
    fa: "برای مشاوره اولیه رایگان و ارزیابی ریسک با تیم امنیتی ما در تماس باشید.",
    ar: "تواصل مع فريق الأمان لدينا للحصول على استشارة أولية مجانية وتقييم مخاطر.",
  },
  submitRequest: {
    en: "Submit a Request",
    fa: "درخواست مشاوره",
    ar: "تقديم طلب",
  },
  responseTime: {
    en: "Our security engineers typically respond within 1 hour",
    fa: "مهندسان امنیتی ما معمولاً ظرف ۱ ساعت پاسخ می‌دهند",
    ar: "مهندسو الأمان لدينا يردون عادةً خلال ساعة واحدة",
  },
};

/* ───── Product Landing: Monitoring ───── */
export const productMonitoring = {
  title: {
    en: "See everything. Miss nothing.",
    fa: "همه چیز را ببینید. هیچ چیز را از دست ندهید.",
    ar: "ارَ كل شيء. لا تفوّت أي شيء.",
  },
  subtitle: {
    en: "Monitoring & Observability",
    fa: "مانیتورینگ و دیدپذیری",
    ar: "المراقبة والمرئية",
  },
  description: {
    en: "Comprehensive monitoring with metrics, logs, traces, and alerts across all your services. Get full visibility into performance, detect issues before they impact users, and ship with confidence.",
    fa: "مانیتورینگ جامع با معیارها، لاگ‌ها، رهگیری و هشدارها در تمام خدمات شما. دید کامل به عملکرد داشته باشید، مشکلات را قبل از تأثیر بر کاربران تشخیص دهید و با اطمینان عرضه کنید.",
    ar: "مراقبة شاملة بالمقاييس والسجلات والتتبعات والتنبيهات عبر جميع خدماتك. احصل على رؤية كاملة للأداء، اكتشف المشاكل قبل أن تؤثر على المستخدمين، وانشر بثقة.",
  },
  features: [
    {
      title: {
        en: "Real-Time Metrics",
        fa: "معیارهای زمان واقعی",
        ar: "مقاييس في الوقت الفعلي",
      },
      description: {
        en: "Track CPU, memory, disk, network, and application-level metrics in real time. Historical data with configurable retention for trend analysis.",
        fa: "معیارهای CPU، حافظه، دیسک، شبکه و سطح برنامه را در زمان واقعی ردیابی کنید. داده‌های تاریخی با نگهداری قابل تنظیم برای تحلیل روند.",
        ar: "تتبع مقاييس CPU والذاكرة والقرص والشبكة ومستوى التطبيق في الوقت الفعلي. بيانات تاريخية مع احتفاظ قابل للتكوين لتحليل الاتجاهات.",
      },
    },
    {
      title: {
        en: "Centralized Logging",
        fa: "لاگ‌گیری متمرکز",
        ar: "تسجيل مركزي",
      },
      description: {
        en: "Aggregate logs from all your services into a single searchable interface. Filter, query, and export logs with full-text search and structured parsing.",
        fa: "لاگ‌های همه خدمات خود را در یک رابط قابل جستجو جمع‌آوری کنید. لاگ‌ها را با جستجوی متن کامل و تجزیه ساختاریافته فیلتر، جستجو و صادر کنید.",
        ar: "اجمع سجلات جميع خدماتك في واجهة واحدة قابلة للبحث. صفّ واستعلم وصدر السجلات ببحث النص الكامل والتحليل المنظم.",
      },
    },
    {
      title: {
        en: "Distributed Tracing",
        fa: "رهگیری توزیع‌شده",
        ar: "التتبع الموزع",
      },
      description: {
        en: "Trace requests across microservices to identify bottlenecks and failures. End-to-end visibility into every request that flows through your system.",
        fa: "درخواست‌ها را در میکروسرویس‌ها رهگیری کنید تا گلوگاه‌ها و خرابی‌ها را شناسایی کنید. دید سرتاسری به هر درخواستی که از سیستم شما عبور می‌کند.",
        ar: "تتبع الطلبات عبر الخدمات المصغرة لتحديد الاختناقات والإخفاقات. رؤية شاملة لكل طلب يمر عبر نظامك.",
      },
    },
    {
      title: {
        en: "Custom Dashboards",
        fa: "داشبوردهای سفارشی",
        ar: "لوحات تحكم مخصصة",
      },
      description: {
        en: "Build custom dashboards with drag-and-drop widgets. Visualize the metrics that matter most to your team and share them with stakeholders.",
        fa: "داشبوردهای سفارشی با ویجت‌های کشیدن و رها کردن بسازید. معیارهایی که برای تیم شما مهم‌تر هستند را visualized کنید و با ذی‌نفعان به اشتراک بگذارید.",
        ar: "ابنِ لوحات تحكم مخصصة بأدوات السحب والإفلات. تصوّر المقاييس الأكثر أهمية لفريقك وشاركها مع أصحاب المصلحة.",
      },
    },
    {
      title: {
        en: "Intelligent Alerts",
        fa: "هشدارهای هوشمند",
        ar: "تنبيهات ذكية",
      },
      description: {
        en: "Set alert rules with dynamic thresholds that adapt to your usage patterns. Get notified via Telegram, email, or webhook when something needs attention.",
        fa: "قوانین هشدار با آستانه‌های پویا که با الگوهای استفاده شما تطبیق می‌یابند تنظیم کنید. از طریق تلگرام، ایمیل یا webhook مطلع شوید.",
        ar: "اضبط قواعد تنبيه بعتبات ديناميكية تتكيف مع أنماط استخدامك. احصل على إشعار عبر Telegram أو البريد الإلكتروني أو webhook عندما يحتاج شيء إلى اهتمام.",
      },
    },
    {
      title: {
        en: "Pre-Built Integrations",
        fa: "یکپارچه‌سازی‌های از پیش ساخته شده",
        ar: "تكاملات مبنية مسبقًا",
      },
      description: {
        en: "Out-of-the-box monitoring for Nginx, PostgreSQL, Redis, Docker, Kubernetes, and more. Deploy and get immediate visibility into your stack.",
        fa: "مانیتورینگ آماده برای Nginx، PostgreSQL، Redis، Docker، Kubernetes و موارد دیگر. دیپلوی کنید و بلافاصله دید به پشته خود دریافت کنید.",
        ar: "مراقبة جاهزة لـ Nginx و PostgreSQL و Redis و Docker و Kubernetes والمزيد. انشر واحصل على رؤية فورية لمجموعتك.",
      },
    },
    {
      title: {
        en: "Uptime Monitoring",
        fa: "مانیتورینگ uptime",
        ar: "مراقبة وقت التشغيل",
      },
      description: {
        en: "Monitor the availability of your services from multiple global locations. Get alerted the moment a service goes down, with automatic status page updates.",
        fa: "در دسترس بودن خدمات خود را از چندین مکان جهانی مانیتور کنید. به محض اینکه سرویسی از کار بیفتد، با به‌روزرسانی خودکار صفحه وضعیت مطلع شوید.",
        ar: "راقب توفر خدماتك من مواقع عالمية متعددة. احصل على تنبيه لحظة تعطل خدمة، مع تحديثات تلقائية لصفحة الحالة.",
      },
    },
    {
      title: { en: "Team Collaboration", fa: "همکاری تیمی", ar: "تعاون فريقي" },
      description: {
        en: "Share dashboards, alerts, and reports with your team. Role-based access ensures the right people see the right data.",
        fa: "داشبوردها، هشدارها و گزارش‌ها را با تیم خود به اشتراک بگذارید. دسترسی مبتنی بر نقش تضمین می‌کند افراد مناسب داده‌های مناسب را ببینند.",
        ar: "شارك لوحات التحكم والتنبيهات والتقارير مع فريقك. يضمن الوصول القائم على الأدوار أن الأشخاص المناسبين يرون البيانات المناسبة.",
      },
    },
  ],
  benefits: [
    {
      title: {
        en: "Full Observability",
        fa: "دیدپذیری کامل",
        ar: "مرئية كاملة",
      },
      desc: {
        en: "Metrics, logs, and traces in one unified platform.",
        fa: "معیارها، لاگ‌ها و رهگیری در یک پلتفرم یکپارچه.",
        ar: "مقاييس وسجلات وتتبعات في منصة واحدة موحدة.",
      },
    },
    {
      title: { en: "Smart Alerts", fa: "هشدارهای هوشمند", ar: "تنبيهات ذكية" },
      desc: {
        en: "Intelligent thresholds that adapt to your usage patterns.",
        fa: "آستانه‌های هوشمند که با الگوهای استفاده شما تطبیق می‌یابند.",
        ar: "عتبات ذكية تتكيف مع أنماط استخدامك.",
      },
    },
    {
      title: {
        en: "Pre-Built Dashboards",
        fa: "داشبوردهای از پیش ساخته شده",
        ar: "لوحات تحكم مبنية مسبقًا",
      },
      desc: {
        en: "Instant visibility into your most important services.",
        fa: "دید فوری به مهم‌ترین خدمات شما.",
        ar: "رؤية فورية لأهم خدماتك.",
      },
    },
    {
      title: { en: "Team Ready", fa: "آماده برای تیم", ar: "جاهز للفريق" },
      desc: {
        en: "Shared dashboards, RBAC, and collaboration tools.",
        fa: "داشبوردهای مشترک، RBAC و ابزارهای همکاری.",
        ar: "لوحات تحكم مشتركة و RBAC وأدوات تعاون.",
      },
    },
  ],
};

/* ───── Product Landing: Quick Apps ───── */
export const productQuickApps = {
  title: {
    en: "Deploy apps in one click.",
    fa: "برنامه‌ها را با یک کلیک دیپلوی کنید.",
    ar: "انشر التطبيقات بنقرة واحدة.",
  },
  subtitle: {
    en: "Quick Apps",
    fa: "برنامه‌های آماده",
    ar: "التطبيقات السريعة",
  },
  description: {
    en: "Launch popular open-source applications instantly — from CMS to analytics and collaboration tools. Fully managed, pre-configured, and ready in seconds.",
    fa: "برنامه‌های متن‌باز محبوب را فوراً راه‌اندازی کنید — از CMS تا ابزارهای تحلیل و همکاری. کاملاً مدیریت‌شده، از پیش پیکربندی شده و آماده در چند ثانیه.",
    ar: "أطلق تطبيقات مفتوحة المصدر شائعة فورًا — من CMS إلى أدوات التحليل والتعاون. مُدارة بالكامل، مكوّنة مسبقًا، وجاهزة في ثوانٍ.",
  },
  heading: {
    en: "Available Quick Apps",
    fa: "برنامه‌های آماده موجود",
    ar: "التطبيقات السريعة المتاحة",
  },
  subheading: {
    en: "Choose from popular open-source applications — all pre-configured, managed, and ready to deploy in seconds.",
    fa: "از برنامه‌های متن‌باز محبوب انتخاب کنید — همه از پیش پیکربندی شده، مدیریت‌شده و آماده دیپلوی در چند ثانیه.",
    ar: "اختر من تطبيقات مفتوحة المصدر شائعة — جميعها مكوّنة مسبقًا ومُدارة وجاهزة للنشر في ثوانٍ.",
  },
  features: [
    {
      title: {
        en: "One-Click Deploy",
        fa: "دیپلوی با یک کلیک",
        ar: "نشر بنقرة واحدة",
      },
      description: {
        en: "Launch any application with a single click. No manual setup, no configuration — just pick your app and go.",
        fa: "هر برنامه‌ای را با یک کلیک راه‌اندازی کنید. بدون راه‌اندازی دستی، بدون پیکربندی — فقط برنامه خود را انتخاب کنید و بروید.",
        ar: "أطلق أي تطبيق بنقرة واحدة. لا إعداد يدوي، لا تكوين — просто اختر تطبيقك وانطلق.",
      },
    },
    {
      title: {
        en: "Pre-Configured Stack",
        fa: "پشته از پیش پیکربندی شده",
        ar: "مجموعة مكوّنة مسبقًا",
      },
      description: {
        en: "Every app comes pre-configured with best practices. No need to tune settings — we handle the optimizations.",
        fa: "هر برنامه با بهترین روش‌ها از پیش پیکربندی شده است. نیازی به تنظیم تنظیمات نیست — ما بهینه‌سازی‌ها را انجام می‌دهیم.",
        ar: "كل تطبيق يأتي مكوّنًا مسبقًا بأفضل الممارسات. لا حاجة لضبط الإعدادات — نحن نتولى التحسينات.",
      },
    },
    {
      title: {
        en: "Auto Updates",
        fa: "به‌روزرسانی خودکار",
        ar: "تحديثات تلقائية",
      },
      description: {
        en: "Stay up to date with automatic security patches and version updates. We keep your apps current and secure.",
        fa: "با patchهای امنیتی خودکار و به‌روزرسانی نسخه به‌روز بمانید. ما برنامه‌های شما را به‌روز و امن نگه می‌داریم.",
        ar: "ابقَ محدثًا بتصحيحات الأمان التلقائية وتحديثات الإصدار. نحافظ على تطبيقاتك حديثة وآمنة.",
      },
    },
    {
      title: {
        en: "Managed Backups",
        fa: "پشتیبان‌گیری مدیریت‌شده",
        ar: "نسخ احتياطية مُدارة",
      },
      description: {
        en: "Automated backups for every app instance. Restore quickly with one-click recovery and snapshot management.",
        fa: "پشتیبان‌گیری خودکار برای هر نمونه برنامه. با بازیابی یک کلیکی و مدیریت snapshot سریع بازیابی کنید.",
        ar: "نسخ احتياطية تلقائية لكل نسخة تطبيق. استعد بسرعة باسترداد بنقرة واحدة وإدارة اللقطات.",
      },
    },
    {
      title: {
        en: "Custom Domain & SSL",
        fa: "دامنه سفارشی و SSL",
        ar: "نطاق مخصص و SSL",
      },
      description: {
        en: "Connect your own domain with free SSL certificates. Full HTTPS support for every app, automatically.",
        fa: "دامنه خود را با گواهی‌های SSL رایگان متصل کنید. پشتیبانی کامل HTTPS برای هر برنامه، به طور خودکار.",
        ar: "اربط نطاقك الخاص بشهادات SSL مجانية. دعم HTTPS كامل لكل تطبيق، تلقائيًا.",
      },
    },
    {
      title: {
        en: "Resource Monitoring",
        fa: "مانیتورینگ منابع",
        ar: "مراقبة الموارد",
      },
      description: {
        en: "Track CPU, RAM, disk, and network usage in real time. Get insights into your app performance and health.",
        fa: "مصرف CPU، RAM، دیسک و شبکه را در زمان واقعی ردیابی کنید. از عملکرد و سلامت برنامه خود بینش بگیرید.",
        ar: "تتبع استخدام CPU و RAM والقرص والشبكة في الوقت الفعلي. احصل على رؤى حول أداء تطبيقك وصحته.",
      },
    },
    {
      title: {
        en: "Isolated Environment",
        fa: "محیط ایزوله",
        ar: "بيئة معزولة",
      },
      description: {
        en: "Each app runs in an isolated container with dedicated resources. No noisy neighbors, consistent performance.",
        fa: "هر برنامه در یک کانتینر ایزوله با منابع اختصاصی اجرا می‌شود. بدون همسایه‌های پر سر و صدا، عملکرد ثابت.",
        ar: "كل تطبيق يعمل في حاوية معزولة بموارد مخصصة. لا جيران مزعجين، أداء ثابت.",
      },
    },
    {
      title: {
        en: "Pay-as-you-go",
        fa: "پرداخت به‌ازای مصرف",
        ar: "ادفع حسب الاستخدام",
      },
      description: {
        en: "Only pay for the resources your app consumes. Scale up or down at any time with no long-term commitments.",
        fa: "فقط برای منابع مصرفی برنامه خود پرداخت کنید. هر زمان بدون تعهدات بلندمدت افزایش یا کاهش دهید.",
        ar: "ادفع فقط مقابل الموارد التي يستهلكها تطبيقك. وسّع أو قلّص في أي وقت بدون التزامات طويلة الأجل.",
      },
    },
  ],
  benefits: [
    {
      title: {
        en: "One-Click Launch",
        fa: "راه‌اندازی با یک کلیک",
        ar: "إطلاق بنقرة واحدة",
      },
      desc: {
        en: "Deploy any app in seconds. Choose, click, and your app is live.",
        fa: "هر برنامه‌ای را در چند ثانیه دیپلوی کنید. انتخاب کنید، کلیک کنید و برنامه شما زنده است.",
        ar: "انشر أي تطبيق في ثوانٍ. اختر، انقر، وتطبيقك يعمل.",
      },
    },
    {
      title: {
        en: "Auto Updates",
        fa: "به‌روزرسانی خودکار",
        ar: "تحديثات تلقائية",
      },
      desc: {
        en: "Security patches and updates applied automatically. Never worry about maintenance.",
        fa: "patchهای امنیتی و به‌روزرسانی‌ها به طور خودکار اعمال می‌شوند. هرگز نگران نگهداری نباشید.",
        ar: "تصحيحات الأمان والتحديثات تُطبّق تلقائيًا. لا تقلق أبدًا بشأن الصيانة.",
      },
    },
    {
      title: {
        en: "Managed Backups",
        fa: "پشتیبان‌گیری مدیریت‌شده",
        ar: "نسخ احتياطية مُدارة",
      },
      desc: {
        en: "Automated daily backups with one-click restore. Your data is always safe.",
        fa: "پشتیبان‌گیری روزانه خودکار با بازیابی یک کلیکی. داده‌های شما همیشه امن است.",
        ar: "نسخ احتياطية يومية تلقائية مع استعادة بنقرة واحدة. بياناتك آمنة دائمًا.",
      },
    },
    {
      title: {
        en: "Scale on Demand",
        fa: "مقیاس‌دهی بر اساس تقاضا",
        ar: "توسع حسب الطلب",
      },
      desc: {
        en: "Increase resources with zero downtime. Grow your app as you grow your business.",
        fa: "منابع را با صفر downtime افزایش دهید. برنامه خود را با رشد کسب و کار خود بزرگ کنید.",
        ar: "زد الموارد بدون توقف. نمِّ تطبيقك كما تنمو أعمالك.",
      },
    },
  ],
};

/* ───── Product Landing: Consulting ───── */
export const productConsulting = {
  title: {
    en: "Expert guidance. Real results.",
    fa: "راهنمایی متخصص. نتایج واقعی.",
    ar: "إرشاد خبير. نتائج حقيقية.",
  },
  subtitle: { en: "Consulting", fa: "مشاوره", ar: "استشارات" },
  description: {
    en: "Our certified architects help you design, migrate, and optimize your cloud infrastructure. From architecture review to hands-on implementation — we turn your vision into a scalable, cost-efficient reality.",
    fa: "معماران دارای گواهینامه ما به شما در طراحی، مهاجرت و بهینه‌سازی زیرساخت ابری کمک می‌کنند. از بررسی معماری تا پیاده‌سازی عملی — ما vision شما را به واقعیتی مقیاس‌پذیر و مقرون به صرفه تبدیل می‌کنیم.",
    ar: "يساعدك مهندسونا المعتمدون في تصميم وترحيل وتحسين بنيتك التحتية السحابية. من مراجعة الهندسة المعمارية إلى التنفيذ العملي — نحوّل رؤيتك إلى واقع قابل للتوسع وفعال من حيث التكلفة.",
  },
  features: [
    {
      title: {
        en: "Architecture Review",
        fa: "بررسی معماری",
        ar: "مراجعة الهندسة المعمارية",
      },
      description: {
        en: "Our certified architects analyze your current infrastructure and design a scalable, resilient architecture tailored to your specific needs and budget.",
        fa: "معماران دارای گواهینامه ما زیرساخت فعلی شما را تحلیل کرده و معماری مقیاس‌پذیر و مقاوم متناسب با نیازها و بودجه خاص شما طراحی می‌کنند.",
        ar: "يُحلل مهندسونا المعتمدون بنيتك التحتية الحالية ويصمّمون هندسة معمارية قابلة للتوسع ومرنة مصممة خصيصًا لاحتياجاتك وميزانيتك.",
      },
    },
    {
      title: {
        en: "Migration Planning",
        fa: "برنامه‌ریزی مهاجرت",
        ar: "تخطيط الترحيل",
      },
      description: {
        en: "Smooth migration from your current infrastructure to Lexoya. Detailed runbooks, risk assessment, rollback plans, and zero-downtime migration strategies.",
        fa: "مهاجرت روان از زیرساخت فعلی شما به Lexoya. runbookهای دقیق، ارزیابی ریسک، برنامه‌های بازگشت و استراتژی‌های مهاجرت با صفر downtime.",
        ar: "ترحيل سلس من بنيتك التحتية الحالية إلى Lexoya. أدلة تشغيل مفصلة وتقييم مخاطر وخطط تراجع واستراتيجيات ترحيل بدون توقف.",
      },
    },
    {
      title: {
        en: "Cost Optimization",
        fa: "بهینه‌سازی هزینه",
        ar: "تحسين التكلفة",
      },
      description: {
        en: "Analyze your current cloud spending and identify opportunities to reduce costs. Right-size resources, choose optimal pricing models, and eliminate waste.",
        fa: "هزینه‌های ابری فعلی خود را تحلیل کرده و فرصت‌های کاهش هزینه را شناسایی کنید. منابع را درست اندازه‌گیری کنید، مدل‌های قیمت‌گذاری بهینه را انتخاب کنید و هدررفت را حذف کنید.",
        ar: "حلّل إنفاقك السحابي الحالي وحدد فرص خفض التكاليف. حدد حجم الموارد المناسب، اختر نماذج التسعير المثلى، وتخلص من الهدر.",
      },
    },
    {
      title: { en: "Proof of Concept", fa: "اثبات مفهوم", ar: "إثبات المفهوم" },
      description: {
        en: "Validate your architecture decisions with a hands-on proof of concept. We build a working prototype on Lexoya infrastructure and demonstrate real-world performance.",
        fa: "تصمیمات معماری خود را با اثبات مفهوم عملی اعتبارسنجی کنید. ما یک نمونه اولیه کار بر روی زیرساخت Lexoya می‌سازیم و عملکرد واقعی را نشان می‌دهیم.",
        ar: "تحقق من قراراتك المعمارية بإثبات مفهوم عملي. نبني نموذجًا أوليًا عمليًا على بنية Lexoya ونظهر أداء العالم الحقيقي.",
      },
    },
    {
      title: {
        en: "DevOps Setup",
        fa: "راه‌اندازی DevOps",
        ar: "إعداد DevOps",
      },
      description: {
        en: "Full DevOps toolchain implementation — CI/CD pipelines, infrastructure as code, monitoring, and alerting. Your team ships faster with enterprise-grade automation.",
        fa: "پیاده‌سازی کامل زنجیره ابزار DevOps — خطوط لوله CI/CD، زیرساخت به عنوان کد، مانیتورینگ و هشدار. تیم شما با اتوماسیون درجه سازمانی سریع‌تر عرضه می‌کند.",
        ar: "تنفيذ كامل لسلسلة أدوات DevOps — خطوط أنابيب CI/CD والبنية التحتية ككود والمراقبة والتنبيه. فريقك ينشر بشكل أسرع بأتمتة من الدرجة المؤسسية.",
      },
    },
    {
      title: { en: "Security Audit", fa: "حسابرسی امنیتی", ar: "تدقيق أمني" },
      description: {
        en: "Comprehensive security review of your infrastructure. Identify vulnerabilities, harden configurations, and implement best practices for data protection and access control.",
        fa: "بررسی امنیتی جامع از زیرساخت شما. آسیب‌پذیری‌ها را شناسایی کنید، پیکربندی‌ها را تقویت کنید و بهترین روش‌ها را برای حفاظت از داده و کنترل دسترسی پیاده‌سازی کنید.",
        ar: "مراجعة أمنية شاملة لبنيتك التحتية. حدد الثغرات، قوِّ التكوينات، وطبّق أفضل الممارسات لحماية البيانات والتحكم في الوصول.",
      },
    },
    {
      title: {
        en: "Training & Handover",
        fa: "آموزش و تحویل",
        ar: "التدريب والتسليم",
      },
      description: {
        en: "Knowledge transfer sessions for your team. From basic operations to advanced troubleshooting — we make sure your team can manage with confidence.",
        fa: "جلسات انتقال دانش برای تیم شما. از عملیات پایه تا عیب‌یابی پیشرفته — ما مطمئن می‌شویم تیم شما می‌تواند با اطمینان مدیریت کند.",
        ar: "جلسات نقل المعرفة لفريقك. من العمليات الأساسية إلى استكشاف الأخطاء المتقدم — نضمن أن فريقك يمكنه الإدارة بثقة.",
      },
    },
    {
      title: {
        en: "Ongoing Advisory",
        fa: "مشاوره مداوم",
        ar: "استشارات مستمرة",
      },
      description: {
        en: "Continuous cloud advisory services. Regular architecture reviews, cost optimization reports, and direct access to our senior engineers for strategic guidance.",
        fa: "خدمات مشاوره ابری مداوم. بررسی‌های منظم معماری، گزارش‌های بهینه‌سازی هزینه و دسترسی مستقیم به مهندسان ارشد ما برای راهنمایی استراتژیک.",
        ar: "خدمات استشارات سحابية مستمرة. مراجعات معمارية منتظمة وتقارير تحسين التكلفة ووصول مباشر لكبار مهندسينا للإرشاد الاستراتيجي.",
      },
    },
  ],
  benefits: [
    {
      title: {
        en: "Certified Architects",
        fa: "معماران دارای گواهینامه",
        ar: "مهندسون معماريون معتمدون",
      },
      desc: {
        en: "Experienced cloud architects who have designed systems at scale.",
        fa: "معماران ابری با تجربه که سیستم‌ها را در مقیاس طراحی کرده‌اند.",
        ar: "مهندسون معماريون سحابيون ذوو خبرة صمّموا أنظمة على نطاق واسع.",
      },
    },
    {
      title: { en: "Hands-On Support", fa: "پشتیبانی عملی", ar: "دعم عملي" },
      desc: {
        en: "We do the work, not just advise. Real implementation, real results.",
        fa: "ما کار را انجام می‌دهیم، نه فقط مشاوره. پیاده‌سازی واقعی، نتایج واقعی.",
        ar: "نحن نقوم بالعمل، وليس فقط الاستشارة. تنفيذ حقيقي، نتائج حقيقية.",
      },
    },
    {
      title: {
        en: "Tailored Solutions",
        fa: "راهکارهای سفارشی",
        ar: "حلول مخصصة",
      },
      desc: {
        en: "Every recommendation is customized to your specific stack and goals.",
        fa: "هر توصیه برای پشته و اهداف خاص شما سفارشی می‌شود.",
        ar: "كل توصية مخصصة لمجموعتك وأهدافك المحددة.",
      },
    },
    {
      title: { en: "End-to-End Service", fa: "خدمت سرتاسری", ar: "خدمة شاملة" },
      desc: {
        en: "From initial assessment to ongoing optimization — we are with you.",
        fa: "از ارزیابی اولیه تا بهینه‌سازی مداوم — ما با شما هستیم.",
        ar: "من التقييم الأولي إلى التحسين المستمر — نحن معك.",
      },
    },
  ],
};

/* ───── DevOps ───── */
export const productDevOps = {
  heroKicker: {
    en: "DevOps Services",
    fa: "خدمات دوآپس",
    ar: "خدمات ديف أوبس",
  },
  heroTitle1: {
    en: "Automate everything.",
    fa: "همه چیز را خودکار کنید.",
    ar: "أتمت كل شيء.",
  },
  heroTitle2: {
    en: "Ship without fear.",
    fa: "بدون ترس آپدیت جدید بدید.",
    ar: "انشر بلا خوف.",
  },
  heroDesc: {
    en: "Enterprise-grade DevOps toolchain that transforms how your team builds, tests, and deploys software. From code commit to production in minutes — fully automated, fully observable, fully secure.",
    fa: "با پیاده‌سازی دواپس در سازمان خود فرایندهای بعد از کدزدن را بصورت اتوماتیک به کدزنی متصل کنید. با این‌کار ایرادات به سرعت پیدا می‌شوند و نگرانی از بابت مغایرت نسخه‌ها و تغییرات زیاد با نسخه پروداکشن از بین می‌رود.",
    ar: "سلسلة أدوات DevOps من الدرجة المؤسسية تحوّل كيفية بناء فريقك واختباره ونشره للبرامج. من commit الكود إلى الإنتاج في دقائق — آلي بالكامل، مرئي بالكامل، آمن بالكامل.",
  },
  ctaStart: {
    en: "Start Your DevOps Journey",
    fa: "سفر DevOps خود را شروع کنید",
    ar: "ابدأ رحلة DevOps الخاصة بك",
  },
  ctaExplore: {
    en: "Explore Services",
    fa: "مشاهده خدمات",
    ar: "استكشف الخدمات",
  },
  stats: [
    {
      title: { en: "10K+", fa: "۱۰K+", ar: "10K+" },
      desc: {
        en: "Pipelines Run Daily",
        fa: "پایپلاین اجرا شده روزانه",
        ar: "خط أنابيب تُشغّل يوميًا",
      },
    },
    {
      title: { en: "99.99%", fa: "۹۹.۹۹٪", ar: "99.99%" },
      desc: {
        en: "Deployment Success Rate",
        fa: "نرخ موفقیت دیپلوی",
        ar: "معدل نجاح النشر",
      },
    },
    {
      title: { en: "< 3min", fa: "< ۳ دقیقه", ar: "< 3 دقائق" },
      desc: {
        en: "Avg Deploy Time",
        fa: "میانگین زمان دیپلوی",
        ar: "متوسط وقت النشر",
      },
    },
    {
      title: { en: "150+", fa: "۱۵۰+", ar: "150+" },
      desc: { en: "Integrations", fa: "یکپارچه‌سازی", ar: "تكاملات" },
    },
  ],
  featuresHeading: {
    en: "Everything your DevOps team needs in",
    fa: "همه نیازهای دواپسی تیم شما در ",
    ar: "كل ما يحتاجه فريق DevOps الخاص بك في",
  },
  featuresHighlight: { en: "one platform", fa: "یک پلتفرم", ar: "منصة واحدة" },
  featuresDesc: {
    en: "No more stitching together a dozen tools. Get a unified DevOps platform that covers the entire software delivery lifecycle.",
    fa: "دیگر نیازی به کنار هم قرار دادن ده‌ها ابزار نیست. با لکسویا همه‌ی نیازمندی دواپس شما یکجا در اختیار شما قرار می‌گیرد.",
    ar: "لا مزيد من تجميع عشرات الأدوات معًا. احصل على منصة DevOps موحدة تغطي دورة حياة توصيل البرامج بأكملها.",
  },
  services: [
    {
      title: {
        en: "Custom Infrastructure Design",
        fa: "پیاده‌سازی سرویس‌های سفارشی",
        ar: "تصميم البنية التحتية المخصصة",
      },
      desc: {
        en: "We design and implement the most suitable infrastructure—whether web servers, databases, or applications—tailored exactly to your business needs and custom architecture.",
        fa: "ما مناسب‌ترین زیرساخت‌ها اعم از وب‌سرور، دیتابیس یا اپلیکیشن‌ها را دقیقاً بر اساس نیازها و معماری اختصاصی کسب‌وکار شما طراحی و پیاده‌سازی می‌کنیم.",
        ar: "نقوم بتصميم وتنفيذ البنية التحتية الأنسب — سواء كانت خوادم ويب أو قواعد بيانات أو تطبيقات — مصممة خصيصًا لتلبية احتياجات عملك.",
      },
    },
    {
      title: {
        en: "High Availability & Performance",
        fa: "پایداری و کارایی",
        ar: "التوفر العالي والأداء",
      },
      desc: {
        en: "Leveraging High Availability (HA) and Fault Tolerance architectures, we ensure your online services operate with maximum stability and resilience.",
        fa: "با بهره‌گیری از معماری‌های دسترسی بالا (HA) و تحمل خطا (Fault Tolerance)، تضمین می‌کنیم که سرویس‌های شما با بالاترین سطح انعطاف و پایداری در دسترس کاربران قرار گیرند.",
        ar: "بالاعتماد على معماريات التوفر العالي والتسامح مع الأخطاء، نضمن عمل خدماتك عبر الإنترنت بأقصى قدر من الاستقرار والمرونة.",
      },
    },
    {
      title: {
        en: "Process Automation",
        fa: "خودکارسازی فرآیندها",
        ar: "أتمتة العمليات",
      },
      desc: {
        en: "By implementing and redesigning CI/CD pipelines, your infrastructure deployment processes are managed seamlessly and automatically, accelerating complex tasks.",
        fa: "با پیاده‌سازی و بازطراحی خطوط لوله CI/CD، فرآیندهای توسعه و استقرار زیرساخت شما به‌صورت یکپارچه و خودکار مدیریت شده و انجام کارهای پیچیده تسریع می‌یابد.",
        ar: "من خلال تنفيذ وإعادة تصميم خطوط أنابيب CI/CD، تتم إدارة عمليات نشر البنية التحتية الخاصة بك بسلاسة وتلقائية، مما يسرع المهام المعقدة.",
      },
    },
    {
      title: {
        en: "Monitoring & Reporting",
        fa: "نظارت و گزارش‌گیری",
        ar: "المراقبة والإبلاغ",
      },
      desc: {
        en: "By setting up comprehensive monitoring and logging systems, you gain full visibility into your infrastructure and can track the health status of your services in real-time.",
        fa: "با راه‌اندازی سیستم‌های جامع مانیتورینگ و لاگ‌گیری، دید کاملی به بخش‌های مختلف زیرساخت خواهید داشت و می‌توانید وضعیت سلامت سرویس‌ها را در لحظه پایش کنید.",
        ar: "من خلال إعداد أنظمة مراقبة وتسجيل شاملة، تكتسب رؤية كاملة لبنيتك التحتية ويمكنك تتبع الحالة الصحية لخدماتك في الوقت الفعلي.",
      },
    },
    {
      title: {
        en: "Security Hardening & Testing",
        fa: "مقاوم‌سازی و تحمل‌پذیری",
        ar: "تقوية الأمان والاختبار",
      },
      desc: {
        en: "Our team ensures compliance with all security and performance standards by conducting Load Tests, Stress Tests, and comprehensive infrastructure Hardening.",
        fa: "تیم ما با اجرای آزمون‌های بار (Load Test)، تست استرس و مقاوم‌سازی (Hardening) زیرساخت، رعایت تمامی استانداردهای امنیتی و کارایی را برای شما تضمین می‌کند.",
        ar: "يضمن فريقنا الامتثال لجميع معايير الأمان والأداء من خلال إجراء اختبارات التحميل واختبارات الضغط وتقوية البنية التحتية بشكل شامل.",
      },
    },
    {
      title: {
        en: "Disaster Recovery",
        fa: "بازیابی پس از بحران",
        ar: "التعافي من الكوارث",
      },
      desc: {
        en: "Through precise Disaster Recovery scenarios, we help you restore services in the shortest possible time after unforeseen incidents, ensuring your data is always safe.",
        fa: "با اجرای دقیق سناریوهای Disaster Recovery، به شما کمک می‌کنیم در کوتاه‌ترین زمان پس از حوادث غیرمترقبه سرویس‌دهی را بازیابی کرده و از امنیت داده‌ها مطمئن شوید.",
        ar: "من خلال سيناريوهات التعافي من الكوارث الدقيقة، نساعدك على استعادة الخدمات في أقصر وقت ممكن بعد الحوادث غير المتوقعة، مما يضمن أمان بياناتك دائمًا.",
      },
    },
    {
      title: {
        en: "Kubernetes Management",
        fa: "مدیریت Kubernetes",
        ar: "إدارة Kubernetes",
      },
      desc: {
        en: "Managed Kubernetes clusters with automated scaling, self-healing, and rolling updates. Deploy with Helm, monitor with Prometheus, and debug with native kubectl access.",
        fa: "خوشه‌های Kubernetes مدیریت‌شده با مقیاس‌دهی خودکار، خودترمیمی و به‌روزرسانی تدریجی. با Helm دیپلوی کنید، با Prometheus مانیتور کنید و با دسترسی kubectl بومی اشکال‌زدایی کنید.",
        ar: "مجموعات Kubernetes مُدارة بتوسع تلقائي وشفاء ذاتي وتحديثات تدريجية. انشر بـ Helm، راقب بـ Prometheus، وصحّح بوصول kubectl الأصلي.",
      },
    },
    {
      title: {
        en: "Infrastructure as Code",
        fa: "زیرساخت به عنوان کد",
        ar: "البنية التحتية ككود",
      },
      desc: {
        en: "Declarative infrastructure management with Terraform, Pulumi, and CloudFormation. Version-controlled, repeatable, and reviewable infrastructure across all your environments.",
        fa: "مدیریت زیرساخت اعلامی با Terraform، Pulumi و CloudFormation. زیرساخت کنترل نسخه، قابل تکرار و قابل بررسی در تمام محیط‌های شما.",
        ar: "إدارة بنية تحتية تصريحية بـ Terraform و Pulumi و CloudFormation. بنية تحتية مُتحكم في إصدارها وقابلة للتكرار والمراجعة عبر جميع بيئاتك.",
      },
    },
    {
      title: {
        en: "Custom Application Deployment",
        fa: "راه‌اندازی اپلیکیشن‌های سفارشی",
        ar: "نشر التطبيقات المخصصة",
      },
      desc: {
        en: "We can set up any application you want—from GitLab to Elasticsearch and beyond. Fully configured, optimized, and ready to use.",
        fa: "ما می‌توانیم هر اپلیکیشنی که بخواهید را راه‌اندازی کنیم؛ از GitLab تا Elasticsearch و غیره. کاملاً پیکربندی‌شده، بهینه‌شده و آماده استفاده.",
        ar: "يمكننا إعداد أي تطبيق تريده — من GitLab إلى Elasticsearch وما بعدها. مكوّنة بالكامل ومحسّنة وجاهزة للاستخدام.",
      },
    },
  ],
  workflowTitle: {
    en: "You write the code,",
    fa: "شما فقط کد بزن،",
    ar: "أنت فقط تكتب الكود،",
  },
  workflowHighlight: {
    en: "we handle the rest.",
    fa: "بقیش با ما.",
    ar: "الباقي علينا.",
  },
  workflowDesc: {
    en: "Focus on building your product. Just commit your code in step 1, and our fully managed DevOps pipeline takes care of everything else automatically.",
    fa: "روی ساخت محصول خود تمرکز کنید. فقط کد خود را پوش کنید، پلتفرم لکسویابقیه کارها را به صورت خودکار انجام می‌دهند.",
    ar: "ركز على بناء منتجك. فقط ادفع كودك في الخطوة 1، وسيتولى خط أنابيبنا المدار بالكامل الباقي تلقائيًا.",
  },
  toolsHeading: {
    en: "Supported DevOps Tools",
    fa: "ابزارهای دواپس پشتیبانی شده",
    ar: "أدوات DevOps المدعومة",
  },
  toolsDesc: {
    en: "We support and integrate with the industry-leading DevOps tools to streamline your workflows.",
    fa: "ما به ابزارهای رایج DevOps متصل (Integrate) می‌شویم تا کار شما را ساده کنیم.",
    ar: "نحن ندعم ونتكامل مع أدوات DevOps الرائدة في الصناعة لتبسيط سير عملك.",
  },
  categories: {
    category1: { en: "Automation", fa: "خودکارسازی", ar: "الأتمتة" },
    category2: { en: "Monitoring", fa: "مانیتورینگ", ar: "المراقبة" },
    category3: { en: "Databases", fa: "دیتابیس", ar: "قواعد البيانات" },
    category4: { en: "Web Servers", fa: "وب‌سرور", ar: "خوادم الويب" },
    category5: {
      en: "Orchestration & Management",
      fa: "اركستريشن و مدیریت",
      ar: "التنسيق والإدارة",
    },
    category6: {
      en: "Streaming & Messaging",
      fa: "استریمینگ و پیام‌رسانی",
      ar: "البث والمراسلة",
    },
    category7: { en: "Storage", fa: "ذخیره‌سازی", ar: "التخزين" },
    category8: {
      en: "Load & Performance Testing",
      fa: "تست بار و عملکرد",
      ar: "اختبار الحمل والأداء",
    },
    category9: { en: "Conferencing", fa: "کنفرانس", ar: "المؤتمرات" },
  },
  workflowSteps: [
    {
      step: { en: "01", fa: "۰۱", ar: "01" },
      title: {
        en: "You Push Code",
        fa: "شما کد را Push می‌کنید",
        ar: "أنت تدفع الكود",
      },
      desc: {
        en: "Your developers simply push code to any Git provider. That is the only step you need to do.",
        fa: "توسعه‌دهندگان شما فقط کد را به هر Git Server دلخواه‌شان (گیتلب، گیتهاب، گیتی) پوش می‌کنند. این تنها کاری است که شما باید انجام دهید.",
        ar: "يقوم مطوروك ببساطة بدفع الكود إلى أي مزود Git. هذه هي الخطوة الوحيدة التي عليك القيام بها.",
      },
    },
    {
      step: { en: "02", fa: "۰۲", ar: "02" },
      title: {
        en: "We Build & Test",
        fa: "ما می‌سازیم و تست می‌کنیم",
        ar: "نحن نبني ونختبر",
      },
      desc: {
        en: "Our platform automatically detects changes, resolves dependencies, and runs automated tests in isolated containers.",
        fa: "پلتفرم ما به طور خودکار تغییرات را تشخیص داده، وابستگی‌ها را نصب کرده، پروژه را داکرایز می‌کند و تست‌های خودکار را اجرا می‌کند.",
        ar: "تكتشف منصتنا التغييرات تلقائيًا، وتحل التبعيات، وتجري اختبارات آلية في حاويات معزولة.",
      },
    },
    {
      step: { en: "03", fa: "۰۳", ar: "03" },
      title: {
        en: "We Provision Infrastructure",
        fa: "ما زیرساخت را تأمین می‌کنیم",
        ar: "نحن نوفر البنية التحتية",
      },
      desc: {
        en: "We automatically provision the necessary servers, databases, and network configurations tailored to your exact needs.",
        fa: "ما به طور خودکار پروژه را در بستر کوبرنتیز دیپلوی می‌کنیم و اتصالاتش با دیتابیس‌ها و تنظیمات شبکه و دامنه و SSL را انجام می‌دهیم.",
        ar: "نقوم تلقائيًا بتوفير الخوادم وقواعد البيانات وتكوينات الشبكة اللازمة المصممة خصيصًا لاحتياجاتك.",
      },
    },
    {
      step: { en: "04", fa: "۰۴", ar: "04" },
      title: {
        en: "We Deploy & Monitor",
        fa: "ما مستقر و پایش می‌کنیم",
        ar: "نحن ننشر ونراقب",
      },
      desc: {
        en: "Zero-downtime deployment directly to production. We continue monitoring health, security, and performance 24/7.",
        fa: "پس از اینکه دیپلوی بدون قطعی مستقیماً در محیط پروداکشن انجام شد، ما وضعیت سلامت، امنیت و عملکرد را ۲۴ ساعته پایش می‌کنیم.",
        ar: "نشر بدون توقف مباشرة إلى الإنتاج. نواصل مراقبة الصحة والأمان والأداء على مدار الساعة.",
      },
    },
  ],
};

/* ───── Organizations ───── */
export const productOrgs = {
  heroKicker: {
    en: "Organizational Services",
    fa: "خدمات سازمانی",
    ar: "الخدمات المؤسسية",
  },
  heroTitle1: {
    en: "Govern at scale.",
    fa: "در مقیاس جهانی فکر کنید.",
    ar: "حوكم على نطاق واسع.",
  },
  heroTitle2: {
    en: "Control without complexity.",
    fa: "کنترل و مقیاس‌پذیری بدون پیچیدگی.",
    ar: "تحكم بدون تعقيد.",
  },
  heroDesc: {
    en: "Enterprise-grade multi-account management that gives you total visibility and control over your cloud infrastructure — without slowing your teams down.",
    fa: "مدیریت متمرکز و تفکیک شده‌ی حساب‌های تیم‌های سازمان که دید و کنترل کامل به عملکرد و مصرف زیرساخت ابری شما می‌دهد — بدون کند کردن و اختلال در عملکرد تیم‌های شما.",
    ar: "إدارة متعددة الحسابات من الدرجة المؤسسية تمنحك رؤية وتحكم كاملين في بنيتك التحتية السحابية — دون إبطاء فرقك.",
  },
  ctaStart: {
    en: "Set Up Your Organization",
    fa: "سازمان خود را جذاب کنید",
    ar: "أنشئ مؤسستك",
  },
  ctaExplore: {
    en: "Explore Capabilities",
    fa: "مشاهده قابلیت‌ها",
    ar: "استكشف الإمكانيات",
  },
  benefits: [
    {
      title: {
        en: "Single Pane of Glass",
        fa: "یک پنجره واحد",
        ar: "لوحة زجاجية واحدة",
      },
      desc: {
        en: "Every account, every resource, every cost — visible and manageable from one unified dashboard.",
        fa: "هر حساب، هر منبع، هر هزینه — قابل مشاهده و مدیریت از یک داشبورد یکپارچه.",
        ar: "كل حساب، كل مورد، كل تكلفة — مرئية وقابلة للإدارة من لوحة تحكم موحدة.",
      },
    },
    {
      title: { en: "Faster Onboarding", fa: "ورود سریع‌تر", ar: "إعداد أسرع" },
      desc: {
        en: "Provision accounts, set permissions, and configure policies in minutes instead of days.",
        fa: "حساب‌ها را ساخته، مجوزها را تنظیم کرده و سیاست‌ها را در چند دقیقه (به جای چند روز) پیکربندی کنید.",
        ar: "جهّز الحسابات، حدد الأذونات، وكوّن السياسات في دقائق بدلاً من أيام.",
      },
    },
    {
      title: {
        en: "Cost Optimization",
        fa: "بهینه‌سازی هزینه",
        ar: "تحسين التكلفة",
      },
      desc: {
        en: "Eliminate wasted spend with organization-wide visibility, budget alerts, and automated recommendations.",
        fa: "هدررفت هزینه را با دید در سطح سازمان، هشدارهای بودجه و توصیه‌های خودکار لکسویا، مدیریت کنید.",
        ar: "تخلص من الإنفاق المهدر برؤية على مستوى المؤسسة وتنبيهات الميزانية وتوصيات تلقائية.",
      },
    },
    {
      title: {
        en: "Security First",
        fa: "امنیت اولویت اول",
        ar: "الأمان أولاً",
      },
      desc: {
        en: "Built-in encryption, audit logging, and compliance reporting keep your data and workloads secure.",
        fa: "رمزگذاری داخلی، ثبت تمامی لاگ‌های عملکردی سیستم و گزارش انطباق داده‌ها، زیرساخت شما را امن نگه می‌دارد.",
        ar: "التشفير المدمج وتسجيل التدقيق وتقارير الامتثال تحافظ على بياناتك وأعباء عملك آمنة.",
      },
    },
  ],
  featuresHeading: {
    en: "Enterprise capabilities",
    fa: "قابلیت‌های سازمانی",
    ar: "إمكانيات مؤسسية",
  },
  featuresHighlight: {
    en: "built for scale",
    fa: "ساخته شده برای مقیاس",
    ar: "مبنية للتوسع",
  },
  featuresDesc: {
    en: "Everything you need to manage cloud infrastructure across hundreds of accounts and thousands of users — from a single control plane.",
    fa: "همه چیز برای مدیریت زیرساخت ابری در صدها حساب و هزاران کاربر — از یک صفحه کنترل واحد.",
    ar: "كل ما تحتاجه لإدارة البنية التحتية السحابية عبر مئات الحسابات وآلاف المستخدمين — من مستوى تحكم واحد.",
  },
  capabilities: [
    {
      title: {
        en: "Multi-Account Management",
        fa: "مدیریت چند حسابی",
        ar: "إدارة متعددة الحسابات",
      },
      desc: {
        en: "Centrally manage all your cloud accounts from a single pane of glass. Create, organize, and govern accounts with consistent policies across your entire organization.",
        fa: "همه حساب‌های ابری خود را از یک پنجره واحد مدیریت کنید. حساب‌ها را با سیاست‌های سازگار در سراسر سازمان خود ایجاد، سازماندهی و مدیریت کنید.",
        ar: "أدِر جميع حساباتك السحابية مركزياً من لوحة زجاجية واحدة. أنشئ ونظّم وحوكم الحسابات بسياسات متسقة عبر مؤسستك بأكملها.",
      },
    },
    {
      title: {
        en: "Role-Based Access Control",
        fa: "کنترل دسترسی مبتنی بر نقش",
        ar: "التحكم في الوصول القائم على الأدوار",
      },
      desc: {
        en: "Define granular roles and permissions for every team member. Custom roles, scoped access, and automated provisioning with SCIM integration for your identity provider.",
        fa: "نقش‌ها و مجوزهای دقیق برای هر عضو تیم تعریف کنید. نقش‌های سفارشی، دسترسی محدود و تأمین خودکار با یکپارچه‌سازی SCIM برای ارائه‌دهنده هویت شما.",
        ar: "حدد أدوارًا وأذونات دقيقة لكل عضو فريق. أدوار مخصصة ووصول محدود وتجهيز تلقائي بتكامل SCIM لموفر الهوية الخاص بك.",
      },
    },
    {
      title: {
        en: "Consolidated Billing",
        fa: "صورتحساب متمرکز",
        ar: "فوترة موحدة",
      },
      desc: {
        en: "Aggregate usage and costs across all accounts into a single invoice. Set budgets, receive alerts, and allocate costs to departments with custom tagging and chargeback reports.",
        fa: "مصرف و هزینه‌ها را در همه حساب‌ها در یک فاکتور جمع‌آوری کنید. بودجه تعیین کنید، هشدار دریافت کنید و هزینه‌ها را با برچسب‌گذاری سفارشی و گزارش‌های مالی به بخش‌ها تخصیص دهید.",
        ar: "اجمع الاستخدام والتكاليف عبر جميع الحسابات في فاتورة واحدة. حدد ميزانيات، تلَقَ تنبيهات، وخصص التكاليف للأقسام بوسم مخصص وتقارير استرداد التكلفة.",
      },
    },
    {
      title: {
        en: "Audit Logs & Compliance",
        fa: "لاگ‌های حسابرسی و انطباق",
        ar: "سجلات التدقيق والامتثال",
      },
      desc: {
        en: "Immutable audit trails for every action across your organization. Meet SOC 2, ISO 27001, and GDPR requirements with automated compliance reports and retention policies.",
        fa: "مسیرهای حسابرسی تغییرناپذیر برای هر اقدام در سراسر سازمان شما. نیازهای SOC 2، ISO 27001 و GDPR را با گزارش‌های انطباق خودکار و سیاست‌های نگهداری برآورده کنید.",
        ar: "مسارات تدقيق غير قابلة للتغيير لكل إجراء عبر مؤسستك. استوفِ متطلبات SOC 2 و ISO 27001 و GDPR بتقارير امتثال تلقائية وسياسات احتفاظ.",
      },
    },
    {
      title: { en: "Cost Analytics", fa: "تحلیل هزینه", ar: "تحليلات التكلفة" },
      desc: {
        en: "Deep visibility into cloud spending with interactive dashboards. Track trends, identify anomalies, and optimize costs with recommendations powered by usage patterns.",
        fa: "دید عمیق به هزینه‌های ابری با داشبوردهای تعاملی. روندها را ردیابی کنید، ناهنجاری‌ها را شناسایی کنید و هزینه‌ها را با توصیه‌های مبتنی بر الگوهای استفاده بهینه کنید.",
        ar: "رؤية عميقة في الإنفاق السحابي بلوحات تحكم تفاعلية. تتبع الاتجاهات، حدد الحالات الشاذة، وحسّن التكاليف بتوصيات مدعومة بأنماط الاستخدام.",
      },
    },
    {
      title: {
        en: "Policy Management",
        fa: "مدیریت سیاست",
        ar: "إدارة السياسات",
      },
      desc: {
        en: "Define and enforce custom policies across your entire organization. Service control policies, tag policies, and permission boundaries ensure consistent governance at scale.",
        fa: "سیاست‌های سفارشی را در سراسر سازمان خود تعریف و اجرا کنید. سیاست‌های کنترل سرویس، سیاست‌های برچسب و مرزهای مجوز حکمرانی سازگار در مقیاس را تضمین می‌کنند.",
        ar: "حدد ونفّذ سياسات مخصصة عبر مؤسستك بأكملها. سياسات التحكم في الخدمة وسياسات الوسم وحدود الأذونات تضمن حوكمة متسقة على نطاق واسع.",
      },
    },
    {
      title: {
        en: "Dedicated Account Manager",
        fa: "پشتیبان اختصاصی",
        ar: "مدير حساب مخصص",
      },
      desc: {
        en: "Get a dedicated technical account manager (TAM) and 24/7 VIP support to ensure your infrastructure runs smoothly and efficiently.",
        fa: "یک پشتیبان فنی اختصاصی (TAM) و پشتیبانی اختصاصی ۲۴/۷ بصورت VIP دریافت کنید تا در کمترین زمان نیازهای زیرساخت خود را برطرف کنید.",
        ar: "احصل على مدير حساب فني مخصص (TAM) ودعم VIP على مدار الساعة لضمان عمل بنيتك التحتية بسلاسة وكفاءة.",
      },
    },
    {
      title: {
        en: "Private Cloud & Custom SLA",
        fa: "ابر خصوصی و SLA سفارشی",
        ar: "سحابة خاصة واتفاقية مستوى خدمة مخصصة",
      },
      desc: {
        en: "Dedicated hardware, isolated networks (VPC), and tailored SLAs up to 99.99% for mission-critical workloads that demand absolute reliability.",
        fa: "سخت‌افزار اختصاصی، شبکه‌های ایزوله (VPC) و SLAهای سفارشی تا ۹۹.۹۹٪ برای سایت‌هایی که نیاز به پایداری بالا دارند.",
        ar: "أجهزة مخصصة وشبكات معزولة (VPC) واتفاقيات مستوى خدمة مخصصة تصل إلى 99.99٪ لأعباء العمل الحرجة التي تتطلب موثوقية مطلقة.",
      },
    },
  ],
};

/* ───── Status Page ───── */
export const productStatus = {
  heroKicker: { en: "Status", fa: "وضعیت", ar: "الحالة" },
  heroTitle: { en: "Server", fa: "وضعیت", ar: "حالة" },
  heroHighlight: { en: "Status", fa: "سرویس‌ها", ar: "الخادم" },
  heroDesc: {
    en: "Real-time status of Lexoya services and infrastructure.",
    fa: "وضعیت خدمات و زیرساخت لکسویا.",
    ar: "حالة فورية لخدمات Lexoya والبنية التحتية.",
  },
  lastChecked: { en: "Last checked:", fa: "آخرین بررسی:", ar: "آخر فحص:" },
  overallOperational: {
    en: "All Systems Operational",
    fa: "همه سیستم‌ها عملیاتی",
    ar: "جميع الأنظمة تعمل",
  },
  overallDegraded: {
    en: "Degraded performance",
    fa: "عملکرد کاهش یافته",
    ar: "أداء متدهور",
  },
  overallDown: {
    en: "Some systems down",
    fa: "برخی سیستم‌ها غیرفعال",
    ar: "بعض الأنظمة معطلة",
  },
  operational: { en: "Operational", fa: "عملیاتی", ar: "يعمل" },
  degraded: { en: "Degraded", fa: "کاهش یافته", ar: "متدهور" },
  down: { en: "Down", fa: "غیرفعال", ar: "معطل" },
  recentIncidents: {
    en: "Recent Incidents",
    fa: "حوادث اخیر",
    ar: "الحوادث الأخيرة",
  },
  affected: { en: "Affected:", fa: "تحت تأثیر:", ar: "المتأثرة:" },
  investigating: { en: "Investigating", fa: "در حال بررسی", ar: "قيد التحقيق" },
  identified: { en: "Identified", fa: "شناسایی شده", ar: "تم تحديده" },
  fixed: { en: "Fixed", fa: "رفع شده", ar: "تم الحل" },
  resolved: { en: "Resolved", fa: "حل شده", ar: "تم الحل" },
  plannedMaintenance: {
    en: "Planned Maintenance",
    fa: "تعمیرات و بروز رسانی‌های برنامه‌ریزی شده",
    ar: "صيانة مجدولة",
  },
  scheduled: { en: "Scheduled", fa: "برنامه‌ریزی شده", ar: "مجدول" },
  from: { en: "From:", fa: "از:", ar: "من:" },
  to: { en: "To:", fa: "تا:", ar: "إلى:" },
  incidentHistory: {
    en: "Incident History",
    fa: "تاریخچه حوادث",
    ar: "سجل الحوادث",
  },
  noIncidents: {
    en: "No incidents reported",
    fa: "هیچ حادثه‌ای گزارش نشده",
    ar: "لم يتم الإبلاغ عن حوادث",
  },
  noMaintenance: {
    en: "No planned maintenance at this time",
    fa: "در حال حاضر هیچ تعمیراتی برنامه‌ریزی نشده است",
    ar: "لا توجد صيانة مجدولة في هذا الوقت",
  },
};

/* ───── Product Landing: Streaming ───── */
export const productStreaming = {
  title: {
    en: "Deliver content seamlessly with dedicated streaming servers.",
    fa: "با سرورهای استریم اختصاصی محتوا را روان تحویل دهید.",
    ar: "قدم المحتوى بسلاسة مع خوادم بث مخصصة.",
  },
  subtitle: { en: "Streaming Servers", fa: "سرورهای استریم", ar: "خوادم بث" },
  description: {
    en: "High-performance streaming servers for media delivery, optimized for video and audio content. Provide a flawless experience for your audience worldwide.",
    fa: "سرورهای استریم با عملکرد بالا برای تحویل رسانه، بهینه شده برای محتوای ویدیویی و صوتی. تجربه‌ای بی‌نقص برای مخاطبان خود در سراسر جهان فراهم کنید.",
    ar: "خوادم بث عالية الأداء لتوصيل الوسائط، محسنة لمحتوى الفيديو والصوت. قدم تجربة خالية من العيوب لجمهورك في جميع أنحاء العالم.",
  },
  featuresHeading: {
    en: "Everything you need for",
    fa: "همه چیز مورد نیاز برای",
    ar: "كل ما تحتاجه لـ",
  },
  featuresDesc: {
    en: "Professional streaming infrastructure with adaptive bitrate, global CDN, and dedicated bandwidth. Built for content creators, broadcasters, and media platforms.",
    fa: "زیرساخت استریم حرفه‌ای با پخش تطبیقی، CDN جهانی و پهنای باند اختصاصی. ساخته شده برای تولیدکنندگان محتوا، صدا و سیما و پلتفرم‌های رسانه‌ای.",
    ar: "بنية تحتية بث احترافية مع معدل بت تكيفي وCDN عالمي ونطاق ترددي مخصص. مصممة لمنشئي المحتوى والمذيعين ومنصات الوسائط.",
  },
  features: [
    {
      title: {
        en: "High Bandwidth",
        fa: "پهنای باند بالا",
        ar: "نطاق ترددي عالٍ",
      },
      description: {
        en: "Dedicated bandwidth for buffer-free streaming. Our high-capacity network handles thousands of concurrent viewers without degradation, ensuring your live streams and on-demand content deliver flawlessly even during peak traffic hours.",
        fa: "پهنای باند اختصاصی برای استریم بدون بافر. شبکه پرظرفیت ما هزاران بازدیدکننده همزمان را بدون افت کیفیت مدیریت می‌کند.",
        ar: "نطاق ترددي مخصص لبث خالٍ من التخزين المؤقت. شبكتنا عالية السعة تتعامل مع آلاف المشاهدين المتزامنين دون تدهور.",
      },
    },
    {
      title: {
        en: "Global CDN Delivery",
        fa: "تحویل با CDN جهانی",
        ar: "توصيل عبر CDN عالمي",
      },
      description: {
        en: "Reach audiences worldwide with ultra-low latency through our global content delivery network. Edge servers distributed across strategic locations cache and serve your video content closer to viewers, reducing buffering and start-up time regardless of their geographic location.",
        fa: "با CDN جهانی به مخاطبان در سراسر جهان با تأخیر بسیار کم دسترسی پیدا کنید. سرورهای لبه در مکان‌های استراتژیک محتوای ویدیویی شما را نزدیک‌تر به کاربران ذخیره و ارائه می‌دهند.",
        ar: "صل إلى الجماهير في جميع أنحاء العالم بزمن وصول فائق الانخفاض عبر شبكة توصيل المحتوى العالمية لدينا. خوادم الحافة الموزعة عبر مواقع استراتيجية تخزّن وتقدم محتوى الفيديو الخاص بك أقرب إلى المشاهدين.",
      },
    },
    {
      title: {
        en: "Adaptive Bitrate (ABR)",
        fa: "پخش تطبیقی (ABR)",
        ar: "معدل البت التكيفي (ABR)",
      },
      description: {
        en: "Deliver the best viewing experience for every audience member with automatic quality adjustment. Our ABR technology detects each viewer network speed and device capability in real time, seamlessly switching between quality tiers from SD to 4K without interrupting playback.",
        fa: "با تنظیم خودکار کیفیت، بهترین تجربه تماشا را برای هر کاربر فراهم کنید. فناوری ABR ما سرعت اینترنت و توانایی دستگاه هر کاربر را در لحظه تشخیص داده و بین کیفیت‌ها بدون قطعی جابه‌جا می‌شود.",
        ar: "قدم أفضل تجربة مشاهدة لكل فرد من الجمهور بضبط الجودة التلقائي. تقنية ABR لدينا تكتشف سرعة شبكة كل مشاهد وقدرات جهازه في الوقت الفعلي.",
      },
    },
    {
      title: {
        en: "Live & On-Demand",
        fa: "پخش زنده و درخواستی",
        ar: "بث مباشر وعند الطلب",
      },
      description: {
        en: "Support for both live streaming and video-on-demand (VOD) in a single platform. Broadcast live events with sub-second latency, then automatically archive and make them available for catch-up viewing. Perfect for webinars, sports, news, and entertainment.",
        fa: "پشتیبانی از پخش زنده و ویدیوی درخواستی در یک پلتفرم. رویدادهای زنده با تأخیر زیر ثانیه پخش کنید و به طور خودکار آرشیو کرده و برای تماشای مجدد در دسترس قرار دهید.",
        ar: "دعم لكل من البث المباشر والفيديو عند الطلب في منصة واحدة. ابث الأحداث المباشرة بزمن وصول أقل من ثانية، ثم أرشفها تلقائيًا واجعلها متاحة للمشاهدة اللاحقة.",
      },
    },
    {
      title: {
        en: "DVR & Catch-Up TV",
        fa: "DVR و ماشین زمان",
        ar: "DVR وآلة الزمن",
      },
      description: {
        en: "Give your viewers full control over live content with DVR functionality. Pause, rewind, and resume live streams at any point — just like traditional TV but better. Combined with catch-up TV, your audience never misses a moment of your live broadcasts.",
        fa: "با DVR کنترل کامل پخش زنده را به کاربران خود بدهید. پخش زنده را در هر نقطه متوقف، به عقب برگردانید و ادامه دهید. همراه با ماشین زمان، مخاطبان شما هیچ لحظه‌ای از پخش زنده را از دست نمی‌دهند.",
        ar: "امنح مشاهديك تحكمًا كاملاً في المحتوى المباشر بوظيفة DVR. أوقف وأعد وأكمل البث المباشر في أي نقطة. مع آلة الزمن، جمهورك لا يفوت أبدًا أي لحظة من بثك المباشر.",
      },
    },
    {
      title: {
        en: "Multi-Protocol Support",
        fa: "پشتیبانی از چندین پروتکل",
        ar: "دعم متعدد البروتوكولات",
      },
      description: {
        en: "Ingest and deliver streams using any major streaming protocol — RTMP, HLS, SRT, WebRTC, and MPEG-DASH. Whether you are using OBS, Wirecast, a hardware encoder, or a custom application, our servers integrate seamlessly with your existing workflow.",
        fa: "با هر پروتکل اصلی استریم — RTMP، HLS، SRT، WebRTC و MPEG-DASH — استریم دریافت و تحویل دهید. سرورهای ما با نرم‌افزارهایی مثل OBS، Wirecast و انکودرهای سخت‌افزاری یکپارچه می‌شوند.",
        ar: "استقبل وقدم البث باستخدام أي بروتوكول بث رئيسي — RTMP و HLS و SRT و WebRTC و MPEG-DASH. تتكامل خوادمنا بسلاسة مع سير عملك الحالي.",
      },
    },
    {
      title: { en: "Security & DRM", fa: "امنیت و DRM", ar: "الأمان وDRM" },
      description: {
        en: "Protect your premium content from unauthorized access and piracy with enterprise-grade DRM solutions including Widevine, PlayReady, and FairPlay. Token-based authentication, IP whitelisting, and geo-blocking give you full control over who can access your streams.",
        fa: "از محتوای ارزشمند خود در برابر دسترسی غیرمجاز و کپی‌برداری با DRM سطح سازمانی شامل Widevine، PlayReady و FairPlay محافظت کنید. احراز هویت توکن، IP whitelist و محدودیت جغرافیایی کنترل کامل دسترسی را به شما می‌دهد.",
        ar: "احمِ محتواك المتميز من الوصول غير المصرح به والقرصنة بحلول DRM من الدرجة المؤسسية بما في ذلك Widevine و PlayReady و FairPlay.",
      },
    },
    {
      title: {
        en: "Detailed Analytics",
        fa: "تحلیل‌های دقیق",
        ar: "تحليلات مفصلة",
      },
      description: {
        en: "Make data-driven decisions with real-time analytics on viewer engagement, stream quality, bandwidth consumption, and geographic distribution. Track key metrics like buffering ratio, average watch time, peak concurrency, and device breakdown to optimize your content strategy.",
        fa: "با تحلیل‌های لحظه‌ای از تعامل کاربران، کیفیت استریم، مصرف پهنای باند و توزیع جغرافیایی تصمیمات مبتنی بر داده بگیرید. معیارهایی مثل نرخ بافر، زمان تماشا و هم‌روندی اوج را ردیابی کنید.",
        ar: "اتخذ قرارات مبنية على البيانات بتحليلات في الوقت الفعلي لتفاعل المشاهدين وجودة البث واستهلاك النطاق الترددي والتوزيع الجغرافي.",
      },
    },
    {
      title: {
        en: "Recording & Archiving",
        fa: "ضبط و آرشیو خودکار",
        ar: "تسجيل وأرشفة تلقائي",
      },
      description: {
        en: "Automatically record every live stream and store it for future use. Set custom retention policies, export recordings to S3-compatible storage, and repurpose your live content for VOD, social media clips, or archival. Never lose a broadcast again.",
        fa: "هر پخش زنده را به طور خودکار ضبط و برای استفاده آینده ذخیره کنید. سیاست‌های نگهداری سفارشی تنظیم کنید و محتوای زنده را برای VOD، کلیپ‌های شبکه‌های اجتماعی یا بایگانی استفاده مجدد کنید.",
        ar: "سجّل كل بث مباشر تلقائيًا وخزّنه للاستخدام المستقبلي. حدد سياسات احتفاظ مخصصة وصدر التسجيلات لتخزين متوافق مع S3.",
      },
    },
    {
      title: {
        en: "Optimized Hardware",
        fa: "سخت‌افزار بهینه",
        ar: "أجهزة محسنة",
      },
      description: {
        en: "Servers purpose-built for media workloads with dedicated GPU-accelerated transcoding, NVMe SSD storage for fast read/write, and Intel Xeon processors optimized for video encoding. Every streaming server is configured, tuned, and monitored for maximum throughput.",
        fa: "سرورهایی که به طور خاص برای بارهای کاری رسانه ساخته شده‌اند با رمزگذاری شتاب‌دهی شده با GPU، حافظه NVMe SSD و پردازنده‌های Xeon بهینه برای ویدیو.",
        ar: "خوادم مبنية خصيصًا لأعباء عمل الوسائط مع ترميز مسرع بالGPU وتخزين NVMe SSD ومعالجات Xeon محسنة للفيديو.",
      },
    },
    {
      title: {
        en: "Instant Scalability",
        fa: "مقیاس‌پذیری فوری",
        ar: "قابلية توسع فورية",
      },
      description: {
        en: "Handle unexpected traffic spikes with automatic horizontal scaling. From 100 to 100,000 concurrent viewers — our infrastructure auto-deploys additional streaming nodes in seconds. No capacity planning, no manual intervention, just seamless growth when you need it most.",
        fa: "با مقیاس‌دهی خودکار افقی، موج‌های ترافیک غیرمنتظره را مدیریت کنید. از ۱۰۰ تا ۱۰۰،۰۰۰ بازدیدکننده همزمان — زیرساخت ما به طور خودکار گره‌های استریم جدید را در ثانیه دیپلوی می‌کند.",
        ar: "تعامل مع ارتفاعات حركة المرور غير المتوقعة بتوسع أفقي تلقائي. من ١٠٠ إلى ١٠٠,٠٠٠ مشاهد متزامن — بنيتنا التحتية تنشر عقد بث إضافية تلقائيًا في ثوانٍ.",
      },
    },
    {
      title: {
        en: "24/7 Expert Support",
        fa: "پشتیبانی ۲۴/۷",
        ar: "دعم خبير على مدار الساعة",
      },
      description: {
        en: "Round-the-clock technical support from streaming engineers who understand video infrastructure inside out. From transcoding optimization to CDN configuration, our team is on standby to resolve issues within minutes, not hours. Enterprise SLA available.",
        fa: "پشتیبانی فنی ۲۴ ساعته از مهندسان استریم که زیرساخت ویدیو را مو به مو می‌شناسند. از بهینه‌سازی رمزگذاری تا پیکربندی CDN، تیم ما آماده رفع مشکلات در چند دقیقه است.",
        ar: "دعم فني على مدار الساعة من مهندسي البث الذين يفهمون البنية التحتية للفيديو من الداخل والخارج. من تحسين الترميز إلى تكوين CDN، فريقنا على أهبة الاستعداد لحل المشكلات في دقائق.",
      },
    },
  ],
  benefits: [
    {
      title: { en: "Zero Buffering", fa: "بدون بافر", ar: "بدون تخزين مؤقت" },
      desc: {
        en: "Deliver a smooth streaming experience.",
        fa: "تجربه استریم روان ارائه دهید.",
        ar: "قدم تجربة بث سلسة.",
      },
    },
    {
      title: { en: "Scalable", fa: "مقیاس‌پذیر", ar: "قابل للتوسع" },
      desc: {
        en: "Scale easily as your audience grows.",
        fa: "با رشد مخاطب به راحتی مقیاس دهید.",
        ar: "توسع بسهولة مع نمو جمهورك.",
      },
    },
    {
      title: { en: "High Quality", fa: "کیفیت بالا", ar: "جودة عالية" },
      desc: {
        en: "Support up to 4K resolution.",
        fa: "پشتیبانی تا کیفیت 4K.",
        ar: "دعم دقة تصل إلى 4K.",
      },
    },
    {
      title: { en: "Fast Delivery", fa: "تحویل سریع", ar: "توصيل سريع" },
      desc: {
        en: "Global CDN for low latency.",
        fa: "شبکه توزیع محتوای جهانی برای تأخیر کم.",
        ar: "CDN عالمي لزمن وصول منخفض.",
      },
    },
  ],
};

/* ───── Product Landing: Sentry ───── */
export const productSentry = {
  title: {
    en: "Track errors. Monitor performance.",
    fa: "خطاها را ردیابی کنید. عملکرد را پایش کنید.",
    ar: "تتبع الأخطاء. راقب الأداء.",
  },
  subtitle: { en: "Sentry Service", fa: "سرویس سنتری", ar: "خدمة سنتري" },
  description: {
    en: "Error tracking and performance monitoring for your applications. Identify, diagnose, and fix issues in real time with our managed Sentry service. Available as shared or dedicated (self-hosted) plans.",
    fa: "ردیابی خطا و مانیتورینگ عملکرد برای برنامه‌های شما. مشکلات را در زمان واقعی شناسایی، تشخیص و رفع کنید. به صورت پلن‌های اشتراکی یا اختصاصی موجود است.",
    ar: "تتبع الأخطاء ومراقبة الأداء لتطبيقاتك. حدد وتشخص وأصلح المشكلات في الوقت الفعلي من خلال خدمة سنتري المُدارة. متوفرة كخطط مشتركة أو مخصصة.",
  },
  features: [
    {
      title: {
        en: "Real-time Alerts",
        fa: "هشدارهای لحظه‌ای",
        ar: "تنبيهات في الوقت الفعلي",
      },
      description: {
        en: "Get notified of errors as they happen.",
        fa: "از خطاها به محض وقوع مطلع شوید.",
        ar: "احصل على إشعارات بالأخطاء فور حدوثها.",
      },
    },
    {
      title: {
        en: "Performance Monitoring",
        fa: "مانیتورینگ عملکرد",
        ar: "مراقبة الأداء",
      },
      description: {
        en: "Track bottlenecks and slow database queries.",
        fa: "گلوگاه‌ها و کوئری‌های کند دیتابیس را ردیابی کنید.",
        ar: "تتبع الاختناقات واستعلامات قاعدة البيانات البطيئة.",
      },
    },
    {
      title: {
        en: "Shared or Dedicated",
        fa: "اشتراکی یا اختصاصی",
        ar: "مشترك أو مخصص",
      },
      description: {
        en: "Choose between managed shared service or a dedicated self-hosted setup.",
        fa: "بین سرویس اشتراکی مدیریت‌شده یا راه‌اندازی اختصاصی انتخاب کنید.",
        ar: "اختر بين الخدمة المشتركة المُدارة أو الإعداد المخصص.",
      },
    },
    {
      title: {
        en: "Seamless Integration",
        fa: "یکپارچگی آسان",
        ar: "تكامل سلس",
      },
      description: {
        en: "Integrate with any platform or framework.",
        fa: "با هر پلتفرم یا فریم‌ورکی یکپارچه شوید.",
        ar: "تكامل مع أي منصة أو إطار عمل.",
      },
    },
    {
      title: { en: "Error Grouping", fa: "گروه‌بندی خطا", ar: "تجميع الأخطاء" },
      description: {
        en: "Smart grouping of identical errors to reduce noise.",
        fa: "گروه‌بندی هوشمند خطاهای مشابه برای کاهش نویز.",
        ar: "تجميع ذكي للأخطاء المتطابقة لتقليل الضوضاء.",
      },
    },
    {
      title: {
        en: "Release Tracking",
        fa: "ردیابی انتشار",
        ar: "تتبع الإصدارات",
      },
      description: {
        en: "See exactly which release introduced each issue.",
        fa: "دقیقاً ببینید کدام انتشار هر مشکل را ایجاد کرده است.",
        ar: "اعرف بالضبط أي إصدار تسبب في كل مشكلة.",
      },
    },
    {
      title: {
        en: "Team Collaboration",
        fa: "همکاری تیمی",
        ar: "التعاون الجماعي",
      },
      description: {
        en: "Assign issues, add comments, and resolve together.",
        fa: "اختصاص مشکلات، افزودن نظرات و رفع آنها به صورت گروهی.",
        ar: "تعيين المشكلات وإضافة التعليقات والحل معًا.",
      },
    },
    {
      title: { en: "Source Maps", fa: "نقشه منبع", ar: "خرائط المصدر" },
      description: {
        en: "View readable stack traces with original source code.",
        fa: "ردیابی پشته قابل خواندن با کد منبع اصلی را مشاهده کنید.",
        ar: "عرض تتبعات المكدس القابلة للقراءة مع كود المصدر الأصلي.",
      },
    },
    {
      title: {
        en: "Session Replay",
        fa: "بازپخش جلسه",
        ar: "إعادة تشغيل الجلسة",
      },
      description: {
        en: "See exactly what users did before an error occurred.",
        fa: "دقیقاً ببینید کاربران قبل از وقوع خطا چه کردند.",
        ar: "شاهد بالضبط ما فعله المستخدمون قبل حدوث الخطأ.",
      },
    },
    {
      title: {
        en: "Issue Integrations",
        fa: "یکپارچه‌سازی با Issues",
        ar: "تكامل مع أنظمة التذاكر",
      },
      description: {
        en: "Sync errors with GitHub, GitLab, Jira, and more.",
        fa: "همگام‌سازی خطاها با گیت‌هاب، گیت‌لب، جیرا و موارد دیگر.",
        ar: "مزامنة الأخطاء مع جيت‌هاب وجيت‌لاب وجيرا والمزيد.",
      },
    },
    {
      title: {
        en: "Custom Alerts",
        fa: "هشدارهای سفارشی",
        ar: "تنبيهات مخصصة",
      },
      description: {
        en: "Set custom alert rules based on conditions and frequency.",
        fa: "قوانین هشدار سفارشی بر اساس شرایط و فرکانس تنظیم کنید.",
        ar: "تعيين قواعد تنبيه مخصصة بناءً على الظروف والتكرار.",
      },
    },
  ],
  benefits: [
    {
      title: { en: "Fix Faster", fa: "سریع‌تر رفع کنید", ar: "أصلح أسرع" },
      desc: {
        en: "Spend less time debugging and more time building.",
        fa: "زمان کمتری برای دیباگ و زمان بیشتری برای ساخت صرف کنید.",
        ar: "اقض وقتًا أقل في التصحيح ووقتًا أكثر في البناء.",
      },
    },
    {
      title: {
        en: "Better UX",
        fa: "تجربه کاربری بهتر",
        ar: "تجربة مستخدم أفضل",
      },
      desc: {
        en: "Improve user experience by identifying issues early.",
        fa: "با شناسایی زودهنگام مشکلات، تجربه کاربری را بهبود بخشید.",
        ar: "حسّن تجربة المستخدم بتحديد المشكلات مبكرًا.",
      },
    },
    {
      title: {
        en: "Seamless Integration",
        fa: "یکپارچگی آسان",
        ar: "تكامل سلس",
      },
      desc: {
        en: "Connect with any language or framework.",
        fa: "اتصال به هر زبان یا فریم‌ورک.",
        ar: "اتصل بأي لغة أو إطار عمل.",
      },
    },
    {
      title: {
        en: "Data Privacy",
        fa: "حریم خصوصی داده",
        ar: "خصوصية البيانات",
      },
      desc: {
        en: "Keep your data secure with dedicated instances.",
        fa: "امنیت داده‌های شما در نمونه‌های اختصاصی تضمین شده است.",
        ar: "حافظ على أمان بياناتك من خلال المثيلات المخصصة.",
      },
    },
  ],
};

/* ───── Product Landing: MCP ───── */
export const productMCP = {
  title: {
    en: "Deploy from a sentence.",
    fa: "با یک جمله پروژه‌ات رو دیپلوی کن.",
    ar: "انشر بجملة واحدة.",
  },
  subtitle: {
    en: "MCP Server",
    fa: "سرور MCP",
    ar: "خادم MCP",
  },
  description: {
    en: "Connect Claude, Cursor, opencode, VS Code, or any assistant that speaks MCP to your Lexoya account. From the chat window you already use, it lists your servers, deploys repositories, reads build logs, and manages databases.",
    fa: "دستیارتون رو وصل کنید: Claude، Cursor، opencode، VS\u00A0Code یا هر ابزار دیگه‌ای که MCP می‌فهمه. بعدش همه‌چیز از همون پنجره‌ی چت انجام می‌شه؛ سرورها رو می‌بینید، ریپو رو دیپلوی می‌کنید، لاگ بیلد رو می‌خونید و دیتابیس رو مدیریت می‌کنید.",
    ar: "اربط Claude أو Cursor أو opencode أو VS\u00A0Code أو أي مساعد يدعم MCP بحسابك في لكسویا. بعد الربط يتم كل شيء من نافذة الدردشة نفسها: عرض الخوادم، ونشر المستودعات، وقراءة سجلات البناء، وإدارة قواعد البيانات.",
  },
  heroHint: {
    en: "One real exchange: your prompt, the tool calls it triggers, and the result that comes back.",
    fa: "یه گفتگوی واقعی: پرامپت شما، ابزارهایی که صدا زده می‌شن و نتیجه‌ای که برمی‌گرده.",
    ar: "حوار حقيقي: طلبك، واستدعاءات الأدوات، والنتيجة التي تعود.",
  },
  workflowsHeading: {
    en: "From one message to a running app",
    fa: "از یه پیام تا اپی که بالا میاد",
    ar: "من رسالة واحدة إلى تطبيق يعمل",
  },
  workflowsDesc: {
    en: "Each scenario below is a few MCP tool calls your assistant makes for you. They run across the whole platform, not inside one service.",
    fa: "هر سناریوی زیر چند تا کال MCP هست که دستیارتون به‌جای شما می‌زنه. این کال‌ها روی کل پلتفرم لکسویا کار می‌کنن، نه فقط روی یه سرویس.",
    ar: "كل سيناريو أدناه بضعة استدعاءات أدوات MCP ينفذها مساعدك بدلًا عنك، وتعمل على كامل منصة لكسویا لا على خدمة واحدة.",
  },
  workflows: [
    {
      title: { en: "Ship an app and its database", fa: "اپ رو با دیتابیسش بالا بیارید", ar: "انشر تطبيقًا مع قاعدة بياناته" },
      ask: {
        en: "Deploy this repo and give it a Postgres.",
        fa: "این ریپو رو دیپلوی کن و یه Postgres هم براش بساز.",
        ar: "انشر هذا المستودع وجهّز له Postgres.",
      },
      steps: ["create_project", "deploy_project", "create_quick_app", "get_quick_app_settings"],
      result: {
        en: "The app is live on an HTTPS subdomain, the database is connected, and you get the username and password in the same reply.",
        fa: "اپ روی یه ساب‌دامین HTTPS بالا میاد، دیتابیس وصل می‌شه و یوزرنیم و پسورد همون‌جا توی جواب میاد.",
        ar: "يعمل التطبيق على نطاق فرعي HTTPS، وتُربط قاعدة البيانات، وتصلك بيانات الدخول في الرد نفسه.",
      },
    },
    {
      title: { en: "Debug a failed deploy", fa: "دیپلوی فیل‌شده رو دیباگ کنید", ar: "شخّص النشر الفاشل" },
      ask: {
        en: "What went wrong with my last deploy?",
        fa: "دیپلوی آخرم چرا خراب شد؟",
        ar: "ما الذي خرب في آخر نشر لي؟",
      },
      steps: ["get_pipeline_logs"],
      result: {
        en: "The assistant reads the log of the stage that failed, explains the cause in plain language, and retries once it fixes it.",
        fa: "دستیار لاگ مرحله‌ی فیل‌شده رو می‌خونه، دلیلش رو ساده توضیح می‌ده و اگه بتونه درستش کنه، دوباره اجرا می‌کنه.",
        ar: "يقرأ المساعد سجل المرحلة الفاشلة، ويشرح السبب بلغة بسيطة، ويعيد المحاولة بعد إصلاحه.",
      },
    },
    {
      title: { en: "Run your servers without the panel", fa: "سرورها رو بدون پنل مدیریت کنید", ar: "شغّل خوادمك دون اللوحة" },
      ask: {
        en: "Restart nginx and check the disk on node-01.",
        fa: "nginx رو ریستارت کن و دیسک node-01 رو چک کن.",
        ar: "أعد تشغيل nginx وافحص القرص على node-01.",
      },
      steps: ["list_servers", "server_power", "server_stats"],
      result: {
        en: "The service restarts, disk usage comes back in the answer, and you know it is healthy again.",
        fa: "سرویس ریستارت می‌شه، فضای دیسک رو همون‌جا می‌بینید و مطمئن می‌شید که همه‌چیز سالم برگشته.",
        ar: "تُعاد الخدمة إلى العمل، ويظهر استخدام القرص في الرد، وتتأكد أن كل شيء عاد سليمًا.",
      },
    },
    {
      title: { en: "Watch your spend from chat", fa: "هزینه‌های ماه رو از چت ببینید", ar: "راقب إنفاقك من الدردشة" },
      ask: {
        en: "How much did I spend this month, and what is still running?",
        fa: "این ماه چقدر خرج کردم و کدوم سرویس‌ها هنوز روشنن؟",
        ar: "كم أنفقت هذا الشهر وما الذي ما زال يعمل؟",
      },
      steps: ["get_account_overview", "list_servers", "list_databases"],
      result: {
        en: "Your balance, hourly cost, days of credit left, and everything currently running, in one answer.",
        fa: "موجودی، هزینه‌ی ساعتی، چند روز شارژ مونده و هر چیزی که الان روشنه، توی یه جواب.",
        ar: "الرصيد، والتكلفة بالساعة، والأيام المتبقية، وكل ما يعمل الآن، في إجابة واحدة.",
      },
    },
  ],
  connectHeading: {
    en: "Connect in under a minute",
    fa: "کمتر از یه دقیقه وصل می‌شید",
    ar: "اتصل في أقل من دقيقة",
  },
  connectDesc: {
    en: "Create an API key in the panel, put the endpoint in your client, and you are done.",
    fa: "توی پنل یه کلید API بسازید، اندپوینت رو بذارید توی کلاینتتون و تمام.",
    ar: "أنشئ مفتاح API في اللوحة، وضع العنوان في عميلك، وانتهى الأمر.",
  },
  keySteps: [
    {
      en: "Panel → API Keys → create a key. Give it a name, and an expiry date if you want one.",
      fa: "پنل ← کلیدهای API ← ساخت کلید. یه اسم براش بذارید و اگه خواستید تاریخ انقضا بدید.",
      ar: "اللوحة ← مفاتيح API ← أنشئ مفتاحًا. أعطه اسمًا، واختياريًا تاريخ انتهاء.",
    },
    {
      en: "Copy the key right then. The full key is shown once, at creation, and the panel only stores its hash.",
      fa: "کلید رو همون لحظه کپی کنید؛ کاملش فقط یه‌بار و موقع ساخت نشون داده می‌شه. پنل فقط هش کلید رو نگه می‌داره.",
      ar: "انسخ المفتاح في الحال؛ يظهر كاملًا مرة واحدة عند الإنشاء، ولا تحفظ اللوحة إلا تجزئته.",
    },
    {
      en: "Add the MCP server to your assistant: pick your client below and paste the ready-made config.",
      fa: "سرور MCP رو به دستیارتون اضافه کنید: تب کلاینت خودتون رو انتخاب کنید و کانفیگ آماده رو کپی کنید.",
      ar: "أضف خادم MCP إلى مساعدك: اختر عميلك من التبويبات والصق الإعداد الجاهز.",
    },
  ],
  clients: {
    claude: { en: "Claude Desktop", fa: "Claude Desktop", ar: "Claude Desktop" },
    cursor: { en: "Cursor", fa: "Cursor", ar: "Cursor" },
    opencode: { en: "opencode", fa: "opencode", ar: "opencode" },
    others: { en: "VS Code / curl", fa: "VS Code / curl", ar: "VS Code / curl" },
  },
  endpointLabel: {
    en: "MCP endpoint",
    fa: "اندپوینت MCP",
    ar: "عنوان MCP",
  },
  copy: { en: "Copy", fa: "کپی", ar: "نسخ" },
  copied: { en: "Copied", fa: "کپی شد", ar: "تم النسخ" },
  promptsHeading: {
    en: "What you can ask",
    fa: "چه چیزهایی می‌شه پرسید",
    ar: "ماذا يمكنك أن تسأل",
  },
  promptsDesc: {
    en: "A few things people actually ask. The assistant can do a lot more; these are just examples.",
    fa: "چند تا از چیزهایی که کاربرهای واقعی می‌پرسن. دستیار خیلی بیشتر از این‌ها کار می‌کنه؛ این‌ها فقط برای شروع‌ه.",
    ar: "بعض ما يسأله الناس فعلًا. يستطيع المساعد أكثر بكثير، وهذه مجرد أمثلة.",
  },
  promptsMore: {
    en: "Plus pipelines, git repos, S3 storage, and the rest of your account.",
    fa: "به‌علاوه‌ی پایپ‌لاین‌ها، ریپوهای git، فضای S3 و بقیه‌ی حسابتون.",
    ar: "إضافةً إلى خطوط الأنابيب ومستودعات git وتخزين S3 وبقية حسابك.",
  },
  promptToolsLabel: {
    en: "Tools it calls",
    fa: "ابزارهایی که صدا می‌زنه",
    ar: "الأدوات التي يستدعيها",
  },
  prompts: [
    {
      en: "Ship my project and ping me when it's live.",
      fa: "پروژه‌م رو بذار بالا، آنلاین شد بهم خبر بده.",
      ar: "انشر مشروعي وأخبرني عندما يصبح مباشرًا.",
      tools: "create_project · deploy_project",
    },
    {
      en: "Spin up a Postgres for my project and hook it up.",
      fa: "برای پروژه‌م یه Postgres بساز و وصلش کن.",
      ar: "جهّز Postgres لمشروعي واربطه به.",
      tools: "create_quick_app · get_quick_app_settings",
    },
    {
      en: "What went wrong with my last deploy?",
      fa: "دیپلوی آخرم چرا خراب شد؟",
      ar: "ما الذي خرب في آخر نشر لي؟",
      tools: "get_pipeline_logs",
    },
    {
      en: "How much credit do I have left, and how long will it last?",
      fa: "چقدر موجودی دارم و تا کی دوام می‌آره؟",
      ar: "كم رصيدي المتبقي وكم سيكفي؟",
      tools: "get_account_overview",
    },
    {
      en: "Which of my servers are running and which are stopped?",
      fa: "کدوم سرورهام روشنه و کدوم‌ها خاموشه؟",
      ar: "أي من خوادمي يعمل وأيها متوقف؟",
      tools: "list_servers",
    },
    {
      en: "Upload this file to my bucket and send me a download link.",
      fa: "این فایل رو بریز تو باکتم و لینک دانلودش رو بفرست.",
      ar: "ارفع هذا الملف إلى مخزني وأرسل لي رابط التنزيل.",
      tools: "list_buckets · get_file",
    },
    {
      en: "Show my projects and the status of their last deploy.",
      fa: "پروژه‌هام و وضعیت آخرین دیپلوی‌شون رو نشون بده.",
      ar: "اعرض مشاريعي وحالة آخر نشر لها.",
      tools: "list_projects · get_project_pipelines",
    },
    {
      en: "Put my Postgres on a public port so I can connect from my laptop.",
      fa: "Postgres رو روی یه پورت عمومی بذار تا از لپ‌تاپم وصل شم.",
      ar: "اعرض Postgres على منفذ عام لأتصل من حاسوبي.",
      tools: "expose_quick_app",
    },
  ],
  ctaHeading: {
    en: "Hand the deploy headache to MCP and your agent.",
    fa: "دردسر دیپلوی رو بده به MCP و\u00A0ایجنت.",
    ar: "سلّم همّ النشر إلى MCP ووكيلك.",
  },
  ctaDesc: {
    en: "Create an API key, connect your assistant, and get on with the work from chat.",
    fa: "یه کلید API بسازید، دستیارتون رو وصل کنید و کار رو از همون چت جلو ببرید.",
    ar: "أنشئ مفتاح API، واربط مساعدك، وتابع عملك من الدردشة.",
  },
  heroPrimary: {
    en: "Connect your assistant",
    fa: "دستیارتون رو وصل کنید",
    ar: "اربط مساعدك",
  },
  heroSecondary: {
    en: "Create an API key",
    fa: "کلید API بسازید",
    ar: "أنشئ مفتاح API",
  },
  ctaPrimary: {
    en: "Create your API key",
    fa: "کلید API بسازید",
    ar: "أنشئ مفتاح API",
  },
  ctaSecondary: {
    en: "See how to connect",
    fa: "ببینید چطور وصل می‌شید",
    ar: "كيف تتصل",
  },
  ctaPoints: [
    { en: "Only a URL and a key", fa: "فقط یه آدرس و یه کلید API", ar: "عنوان واحد ومفتاح API فقط" },
    { en: "Works with Claude, Cursor, opencode and VS Code", fa: "با Claude، Cursor، opencode، VS\u00A0Code کار می‌کنه", ar: "يعمل مع Claude و Cursor و opencode و VS\u00A0Code" },
    { en: "Revoke the key whenever you want", fa: "هر وقت خواستید کلید رو باطل کنید", ar: "ألغِ المفتاح متى شئت" },
  ],
  contactHeading: {
    en: "Stuck?",
    fa: "گیر کردید؟",
    ar: "واجهت مشكلة؟",
  },
  contactDesc: {
    en: "Open a ticket whenever something goes wrong. A real engineer from the Lexoya team answers, not a bot.",
    fa: "هر مشکلی داشتید تیکت بزنید؛ یه مهندس از تیم لکسویا جواب می‌ده، نه ربات.",
    ar: "افتح تذكرة عند أي مشكلة، وسيجيبك مهندس من فريق لكسويا، لا روبوت.",
  },
  contactTicket: {
    en: "Open a ticket",
    fa: "ثبت تیکت",
    ar: "افتح تذكرة",
  },
  contactChannelsLabel: {
    en: "Or reach us directly",
    fa: "یا مستقیم پیام بدید",
    ar: "أو راسلنا مباشرة",
  },
  contactNote: {
    en: "Include the error text and the tool name so we can answer faster.",
    fa: "متن ارور و اسم ابزار رو هم بفرستید تا سریع‌تر جواب بگیرید.",
    ar: "أرفق نص الخطأ واسم الأداة لتحصل على إجابة أسرع.",
  },
  contactPhone: { en: "Phone", fa: "تلفن", ar: "الهاتف" },
  contactTelegram: { en: "Telegram", fa: "تلگرام", ar: "تيليجرام" },
  contactEmail: { en: "Email", fa: "ایمیل", ar: "البريد الإلكتروني" },
  faqHeading: {
    en: "Questions teams ask before connecting",
    fa: "سوالاتی که تیم‌ها قبل از اتصال می‌پرسن",
    ar: "أسئلة تطرحها الفرق قبل الربط",
  },
  faqDesc: {
    en: "Short answers about how the official Lexoya MCP server works and what it can touch in your account.",
    fa: "جواب‌های کوتاه به چند سوال رایج: سرور رسمی MCP لکسویا چطور کار می‌کنه و به چه چیزهایی توی حسابتون دسترسی داره.",
    ar: "إجابات مختصرة عن كيفية عمل خادم MCP الرسمي من لكسویا وما يمكنه الوصول إليه في حسابك.",
  },
  faq: [
    {
      q: {
        en: "Why run my cloud through Lexoya's MCP server?",
        fa: "چرا ابرم رو با سرور MCP لکسویا مدیریت کنم؟",
        ar: "لماذا أدير سحابتي عبر خادم MCP الخاص بلكسویا؟",
      },
      a: {
        en: "Because it is the same server the Lexoya panel talks to, so what you ask for in chat is what the panel would do. It creates servers, deploys apps, manages databases, reads build logs and checks your balance through one endpoint, and there is no separate integration that can fall behind the product.",
        fa: "چون همون سرور MCP هست که پنل لکسویا هم با همون کار می‌کنه؛ یعنی کاری که توی چت می‌خواید همون کاریه که از پنل هم برمیاد. ساخت سرور، دیپلوی اپ، مدیریت دیتابیس، خوندن لاگ بیلد و چک کردن موجودی، همه از یه اندپوینت. اینتگریشن جدایی هم در کار نیست که از محصول عقب بمونه.",
        ar: "لأنه الخادم نفسه الذي تتحدث معه لوحة لكسویا، فما تطلبه في الدردشة هو ما تفعله اللوحة تمامًا. ينشئ الخوادم، وينشر التطبيقات، ويدير قواعد البيانات، ويقرأ سجلات البناء، ويفحص الرصيد عبر عنوان واحد، ولا يوجد تكامل منفصل قد يتخلف عن المنتج.",
      },
    },
    {
      q: {
        en: "Can the assistant really deploy my app from a chat message?",
        fa: "دستیار واقعاً می‌تونه اپلیکیشنم رو از یه پیام چت دیپلوی کنه؟",
        ar: "هل يمكن للمساعد حقًا نشر تطبيقي من رسالة محادثة؟",
      },
      a: {
        en: "Yes. Say \u201Cdeploy my repo\u201D and it creates the project, adds a Dockerfile if the repo needs one, runs the pipeline and hands you the live URL. You can ask for the log of any stage while it runs.",
        fa: "آره. بگید «ریپوم رو دیپلوی کن»؛ پروژه ساخته می‌شه، اگه ریپو Dockerfile نداشته باشه دستیار یکی می‌نویسه، پایپ‌لاین اجرا می‌شه و لینک زنده رو تحویل می‌گیرید. لاگ هر مرحله رو هم می‌تونید وسط کار بخواید.",
        ar: "نعم. قل «انشر مستودعي» فينشئ المشروع، ويضيف Dockerfile إذا احتاجه المستودع، ويشغّل خط الأنابيب ويعطيك الرابط المباشر. ويمكنك طلب سجل أي مرحلة أثناء التنفيذ.",
      },
    },
    {
      q: {
        en: "Which AI clients work with Lexoya's MCP server?",
        fa: "کدوم کلاینت‌های هوش مصنوعی با سرور MCP لکسویا کار می‌کنن؟",
        ar: "ما عملاء الذكاء الاصطناعي الذين يعملون مع خادم MCP الخاص بلكسویا؟",
      },
      a: {
        en: "Anything that speaks streamable HTTP MCP: Claude Desktop, Cursor, opencode, VS Code and most agent frameworks. You add one URL and your API key, and the tools show up.",
        fa: "هر چیزی که streamable HTTP MCP رو پشتیبانی کنه: Claude Desktop، Cursor، opencode، VS\u00A0Code و بیشتر فریم‌ورک‌های ایجنت. یه آدرس و کلید API اضافه می‌کنید و ابزارها خودشون ظاهر می‌شن.",
        ar: "أي شيء يدعم streamable HTTP MCP: Claude Desktop و Cursor و opencode و VS\u00A0Code ومعظم أطر عمل الوكلاء. تضيف عنوانًا واحدًا ومفتاح API، وتظهر الأدوات.",
      },
    },
    {
      q: {
        en: "Is it safe to give an assistant access to my account?",
        fa: "دسترسی دادن به دستیار برای حسابم امنه؟",
        ar: "هل من الآمن منح المساعد وصولًا إلى حسابي؟",
      },
      a: {
        en: "Each key is limited to your account, stored as a hash, and can be revoked or given an expiry date. The endpoint is rate-limited at 600 requests per minute, and the server never sees your kubeconfig or S3 credentials. Treat the key like a password, because that is what it is.",
        fa: "ابزارها به حساب و کلید API شما محدودن. کلیدها به‌صورت هش نگه داشته می‌شن، قابل لغو هستن و می‌شه براشون تاریخ انقضا گذاشت. اندپوینت محدودیت نرخ داره (۶۰۰ درخواست در دقیقه) و سرور هیچ‌وقت kubeconfig یا کلیدهای S3 شما رو نمی‌بینه. با کلید مثل رمز عبور رفتار کنید، چون واقعاً همونه.",
        ar: "كل مفتاح مقيّد بحسابك، ويُخزن كتجزئة، ويمكن إلغاؤه أو ضبط تاريخ انتهاء له. العنوان محدود المعدل (600 طلب في الدقيقة)، ولا يرى الخادم أبدًا kubeconfig أو بيانات اعتماد S3 الخاصة بك. تعامل مع المفتاح ككلمة مرور، لأنه كذلك.",
      },
    },
    {
      q: {
        en: "What can the assistant do with my databases?",
        fa: "دستیار با دیتابیس‌های من چه کار می‌کنه؟",
        ar: "ماذا يمكن للمساعد أن يفعل بقواعد بياناتي؟",
      },
      a: {
        en: "Create managed Postgres, MySQL, MongoDB or Redis; get the password and internal URL; connect them to your apps; and expose one on a public port for an outside client. Scaling and backups work the same way.",
        fa: "Postgres، MySQL، MongoDB یا Redis مدیریت‌شده می‌سازه، پسورد و آدرس داخلی رو می‌گیره، به اپتون وصلشون می‌کنه و اگه لازم شد روی پورت عمومی expose می‌کنه. اسکیل و بکاپ هم همین‌طور.",
        ar: "ينشئ Postgres أو MySQL أو MongoDB أو Redis مُدارًا، ويحصل على كلمة المرور والعنوان الداخلي، ويربطها بتطبيقاتك، ويعرض أحدها على منفذ عام لعميل خارجي. والتوسع والنسخ الاحتياطي بالطريقة نفسها.",
      },
    },
    {
      q: {
        en: "What does \u201Cofficial MCP\u201D actually mean?",
        fa: "«MCP رسمی» واقعاً یعنی چی؟",
        ar: "ماذا يعني «MCP رسمي» فعلًا؟",
      },
      a: {
        en: "It means your assistant talks to the MCP server Lexoya builds and maintains, the same tools the panel uses. What you get in chat matches the product and moves when the product moves, with no third-party wrapper in between.",
        fa: "یعنی دستیارتون با همون سرور MCP حرف می‌زنه که تیم لکسویا می‌سازه و نگه‌داری می‌کنه؛ همون ابزارهایی که پنل استفاده می‌کنه. پس هر چیزی که توی چت می‌گیرید با خود محصول یکیه و با آپدیت‌های محصول جلو می‌ره. هیچ wrapper غیررسمی‌ای هم وسط نیست.",
        ar: "يعني أن مساعدك يتحدث مع خادم MCP الذي تبنيه لكسویا وتصونه، وهو نفسه الأدوات التي تستخدمها اللوحة. فما تحصل عليه في الدردشة يطابق المنتج ويتحرك معه، دون أي ربط غير رسمي في المنتصف.",
      },
    },
    {
      q: {
        en: "How do I get started with Lexoya MCP?",
        fa: "چطور کار با MCP لکسویا رو شروع کنم؟",
        ar: "كيف أبدأ مع MCP لكسویا؟",
      },
      a: {
        en: "Create an API key in the panel (Panel → API Keys), add the endpoint to your client, and start asking. The first deploy usually takes a few minutes.",
        fa: "توی پنل یه کلید API بسازید (پنل ← API Keys)، اندپوینت رو به کلاینتتون اضافه کنید و شروع کنید به پرسیدن. اولین دیپلوی معمولاً چند دقیقه‌ای طول می‌کشه.",
        ar: "أنشئ مفتاح API في اللوحة (اللوحة ← API Keys)، وأضف العنوان إلى عميلك، وابدأ بالسؤال. أول نشر يستغرق عادةً بضع دقائق.",
      },
    },
  ],
};
